var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import customEventHandler from '../helper/customEvents';
import InputBox from './InputBox';
import { saveState } from '../helper/stateManagerHelper';
import { calculateTokenPosition } from '../helper/tokenDropHelper';
import { listFreeSnapSlots } from '../helper/linearPieceSnapHelper';
import { snapToBackgroundGrid, snapToTriangleGrid } from '../helper/snapHelper';
import { GRID_SIZE } from '../constants/gridConstants';
import { computePxPerUnit } from '../helper/numberLineHelper';
import { MOVE_STEP, getToolRulesForId, hasSubLockMenu, hasLengthLock, getDisabledButtonsByLock, disableEmptySubmenuParents, } from '../helper/contextMenuHelper';
import { announce, announceAssertive } from '../helper/announcer';
import { computeGroupSnapDelta, computePatternShapeSnap } from '../widgets/patternShapesSnapHelper';
import { playAudio } from '../helper/audioHelper';
import { ColorEnum } from './ColorPicker';
import { unlinkSelectedCubes, CSS_CUBE_SIZE } from '../widgets/LinkingCubes';
import '../styles/contextMenu.scss';
// mathlive-static.css provides .ML__latex-scoped rules (fractions, superscripts,
// vlist tables) that convertLatexToMarkup output needs outside a shadow DOM.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – webpack handles CSS imports; TS lacks a declaration for this file
import 'mathlive/static.css';
import { convertLatexToMarkup } from 'mathlive';
import { sumAndReduceFractions, parseFraction, getFractionDimensions, getFractionAriaText, findSnapTargetManualTile, isProperRectangle, } from '../helper/helper';
import { createLinearPieceForSelectedTile, resolveColoredTilesTarget, } from '../helper/linearPieceHelper';
import { buildColoredTilesExpressionAria, buildColoredTilesProductAria, buildColoredTilesDimensionLabelsAria, } from '../helper/coloredTilesAria';
import { getDenominatorFromFractionType, getMaxPartsForDenominator, } from './SplitFractionTileModal';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var ContextMenu = function () {
    var _a, _b, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43;
    var t = useTranslation().t;
    var _44 = useContext(DataContext), contextMenu = _44.contextMenu, setContextMenu = _44.setContextMenu, selectedTool = _44.selectedTool, setSelectedTool = _44.setSelectedTool, manipulative = _44.manipulative, setManipulative = _44.setManipulative, getPositionForNewItem = _44.getPositionForNewItem, text = _44.text, setText = _44.setText, table = _44.table, setTable = _44.setTable, annotations = _44.annotations, setAnnotations = _44.setAnnotations, shades = _44.shades, setShades = _44.setShades, color = _44.color, setColor = _44.setColor;
    var viewEditMode = globalStore.useStoreState(function (state) { return state.playerStore.viewEditMode; });
    var isViewMode = viewEditMode === 'view';
    var playerBgPattern = globalStore.useStoreState(function (s) { return s.playerStore; }).playerBgPattern;
    var _45 = useState(0), activeIndex = _45[0], setActiveIndex = _45[1];
    var _46 = useState(false), submenuOpen = _46[0], setSubmenuOpen = _46[1];
    var _47 = useState(0), submenuIndex = _47[0], setSubmenuIndex = _47[1];
    var _48 = useState(''), amAnnouncement = _48[0], setAmAnnouncement = _48[1];
    var menuRef = useRef(null);
    var submenuRef = useRef(null);
    var triggerRef = useRef(null);
    var activeMenuItemRef = useRef(null);
    var _49 = useState(new Set()), checkedDiceIds = _49[0], setCheckedDiceIds = _49[1];
    var _50 = useState(new Set()), checkedPatternIds = _50[0], setCheckedPatternIds = _50[1];
    var highlightedDiceRef = useRef(null);
    var choralActiveRef = useRef(false);
    // Remembers the shade position at the moment the context menu opened, so
    // Move Up/Down/Left/Right can announce the total delta accumulated over
    // repeated menu activations instead of the per-step MOVE_STEP value.
    var shadeMoveOriginRef = useRef(null);
    var textMoveOriginRef = useRef(null);
    var tableMoveOriginRef = useRef(null);
    var _51 = useState(null), choralSpeedLocal = _51[0], setChoralSpeedLocal = _51[1];
    var _52 = useState(null), choralDirLocal = _52[0], setChoralDirLocal = _52[1];
    var _53 = useState(false), choralIsPlaying = _53[0], setChoralIsPlaying = _53[1];
    // Reset choral playing state whenever the menu opens (contextMenu becomes non-null)
    // or the target manipulative changes. This ensures Play is always enabled on fresh open.
    useEffect(function () {
        var _a;
        if (contextMenu) {
            setChoralIsPlaying(false);
            // Pre-check the context tile itself so it appears selected at the top of
            // the "Combine Tiles" list. The user can uncheck it if needed.
            var isFractionTile = (_a = contextMenu.manipulativeId) === null || _a === void 0 ? void 0 : _a.startsWith('fractionTiles_');
            setCheckedDiceIds(isFractionTile ? new Set([contextMenu.manipulativeId]) : new Set());
            setAmAnnouncement('');
        }
        // Reset the shade move-origin whenever the context menu opens or
        // targets a different shade. The move handler fills it in lazily on the
        // first Move* click so the announced delta accumulates across clicks.
        shadeMoveOriginRef.current = null;
        textMoveOriginRef.current = null;
        tableMoveOriginRef.current = null;
    }, [contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId, contextMenu]);
    // Listen for choral completion to reset playing state
    useEffect(function () {
        var onChoralEvent = function (data) {
            if (data.id === 'choralComplete') {
                setChoralIsPlaying(false);
            }
        };
        customEventHandler.addEventListener('choralDrawer', onChoralEvent);
        return function () { return customEventHandler.removeEventListener('choralDrawer', onChoralEvent); };
    }, []);
    var _54 = useState({
        interval: 1,
        integer: 0,
        numerator: 0,
        denominator: 1,
    }), stampEveryInput = _54[0], setStampEveryInput = _54[1];
    var stampEveryInputRef = useRef(stampEveryInput);
    stampEveryInputRef.current = stampEveryInput;
    // Always-current manipulative ref so snap computations in action handlers never see stale state
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    // Highlight/unhighlight a dice on the canvas by toggling a data attribute
    var highlightDice = function (diceId) {
        // Remove previous highlight
        if (highlightedDiceRef.current) {
            var prevEl = document.querySelector("[data-manipulative-id=\"".concat(highlightedDiceRef.current, "\"]"));
            if (prevEl)
                prevEl.dataset.highlighted = '';
        }
        highlightedDiceRef.current = diceId;
        // Add new highlight
        if (diceId) {
            var el = document.querySelector("[data-manipulative-id=\"".concat(diceId, "\"]"));
            if (el)
                el.dataset.highlighted = 'true';
        }
    };
    var isCanvasPanMenu = (contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId) === 'canvas_pan';
    // Detect whether the context menu target is a shade, card, or deck
    var isShadeMenu = (_a = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('shades_')) !== null && _a !== void 0 ? _a : false;
    var isCardMenu = (_b = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('card_')) !== null && _b !== void 0 ? _b : false;
    var isDeckMenu = (_d = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('deck_')) !== null && _d !== void 0 ? _d : false;
    var isAnnotationMenu = (_e = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('annotations_')) !== null && _e !== void 0 ? _e : false;
    var annotationIndex = isAnnotationMenu ? Number(contextMenu.manipulativeId.split('_')[1]) : -1;
    var selectedAnnotation = isAnnotationMenu ? annotations[annotationIndex] : null;
    var isTextMenu = (_f = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('text_')) !== null && _f !== void 0 ? _f : false;
    var textIndex = isTextMenu ? Number(contextMenu.manipulativeId.split('_')[1]) : -1;
    var selectedTextItem = isTextMenu ? ((_g = text[textIndex]) !== null && _g !== void 0 ? _g : null) : null;
    // Table-level menu: tableText_0, tableText_1, etc. (excludes per-cell IDs)
    var isTableTextMenu = (_h = ((contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('tableText_')) &&
        !contextMenu.manipulativeId.includes('_cell_'))) !== null && _h !== void 0 ? _h : false;
    var tableTextIndex = isTableTextMenu ? Number(contextMenu.manipulativeId.split('_')[1]) : -1;
    var selectedTableItem = isTableTextMenu ? ((_j = table[tableTextIndex]) !== null && _j !== void 0 ? _j : null) : null;
    // Per-cell menu: tableText_0_cell_2_1
    var TABLE_CELL_MENU_RE = /^tableText_(\d+)_cell_(\d+)_(\d+)$/;
    var isTableCellMenu = TABLE_CELL_MENU_RE.test((_k = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId) !== null && _k !== void 0 ? _k : '');
    var tableCellMenuMatch = isTableCellMenu
        ? TABLE_CELL_MENU_RE.exec(contextMenu.manipulativeId)
        : null;
    var cellMenuTableIndex = tableCellMenuMatch ? Number(tableCellMenuMatch[1]) : -1;
    var cellMenuRow = tableCellMenuMatch ? Number(tableCellMenuMatch[2]) : -1;
    var cellMenuCol = tableCellMenuMatch ? Number(tableCellMenuMatch[3]) : -1;
    var cellMenuTableItem = isTableCellMenu ? ((_l = table[cellMenuTableIndex]) !== null && _l !== void 0 ? _l : null) : null;
    var cellMenuNumCols = (_p = (_o = (_m = cellMenuTableItem === null || cellMenuTableItem === void 0 ? void 0 : cellMenuTableItem.data) === null || _m === void 0 ? void 0 : _m.colWidth) === null || _o === void 0 ? void 0 : _o.length) !== null && _p !== void 0 ? _p : 1;
    var cellIsLocked = (_v = (_u = (_t = (_s = (_r = (_q = cellMenuTableItem === null || cellMenuTableItem === void 0 ? void 0 : cellMenuTableItem.data) === null || _q === void 0 ? void 0 : _q.cellData) === null || _r === void 0 ? void 0 : _r[cellMenuRow * cellMenuNumCols + cellMenuCol]) === null || _s === void 0 ? void 0 : _s.data) === null || _t === void 0 ? void 0 : _t.lock) === null || _u === void 0 ? void 0 : _u.editing) !== null && _v !== void 0 ? _v : false;
    // Bead menu: manipulativeId shape is `numberRacks_<uuid>_bead_<rodIdx>_<beadIdx>`
    var isBeadMenu = (_w = ((contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId.startsWith('numberRacks_')) &&
        contextMenu.manipulativeId.includes('_bead_'))) !== null && _w !== void 0 ? _w : false;
    var beadRackId = isBeadMenu ? contextMenu.manipulativeId.split('_bead_')[0] : '';
    var beadRodIdx = isBeadMenu
        ? Number((_x = contextMenu.manipulativeId.split('_bead_')[1]) === null || _x === void 0 ? void 0 : _x.split('_')[0])
        : -1;
    var beadBeadIdx = isBeadMenu
        ? Number((_y = contextMenu.manipulativeId.split('_bead_')[1]) === null || _y === void 0 ? void 0 : _y.split('_')[1])
        : -1;
    // Context menu heading based on manipulative type (with fallback)
    var getContextMenuHeading = function () {
        var _a;
        if (isCanvasPanMenu)
            return t('text.panningActions');
        if (isAnnotationMenu) {
            return (selectedAnnotation === null || selectedAnnotation === void 0 ? void 0 : selectedAnnotation.type) === 'line'
                ? t('text.lineActions')
                : t('text.shapeActions');
        }
        if (isTextMenu)
            return t('text.textActions');
        if (isBeadMenu)
            return t('text.beadActions');
        var manipId = (_a = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId) !== null && _a !== void 0 ? _a : '';
        // PS permanent group: show "Pattern Block Group Actions" instead of generic "Group Actions"
        if (manipId.startsWith('manipulativeGroup_')) {
            var isPSGroup = manipulative.some(function (m) { var _a; return m.data.groupId === manipId && ((_a = m.data.id) === null || _a === void 0 ? void 0 : _a.startsWith('patternShapes_')); });
            if (isPSGroup)
                return t('text.patternBlockGroupActions');
        }
        // Branch numberPiece/moneyPiece by checking the actual manipulative data
        if (manipId.startsWith('numberPiece') || manipId.startsWith('moneyPiece')) {
            var item = manipulative.find(function (m) { return m.data.id === manipId; });
            if ((item === null || item === void 0 ? void 0 : item.data.type) === 'moneyPiece')
                return t('text.moneyPieceActions');
            if ((item === null || item === void 0 ? void 0 : item.data.type) === 'numberPiece')
                return t('text.numberPieceActions');
        }
        // PatternShapes outlines (library `isOutline` or user-converted `isStaticOutline`)
        // get their own heading so the menu reads as "Pattern Block Outline Actions".
        if (manipId.startsWith('patternShapes')) {
            var item = manipulative.find(function (m) { return m.data.id === manipId; });
            var d = item === null || item === void 0 ? void 0 : item.data;
            if (d && (d.isOutline || d.isStaticOutline || d.shapeType === 'outline')) {
                return t('text.patternBlockOutlineActions');
            }
        }
        var headingMap = {
            spinner: 'text.spinnerActions',
            dice: 'text.diceActions',
            deck: 'text.deckActions',
            card: 'text.cardActions',
            analog_clock: 'text.clockActions',
            timer_clock: 'text.timerActions',
            numberChart: 'text.numberChartActions',
            numberLine: 'text.numberLineActions',
            // Jump Bar + Custom Tick are children of a Number Line but each
            // get their own heading so screen-reader users can distinguish
            // which widget's context menu just opened. Previously both
            // collapsed to "Number Line Actions", which was indistinguishable.
            jumpBar: 'text.jumpBarActions',
            customTick: 'text.customTickActions',
            counter: 'text.counterActions',
            numberFrameCounter: 'text.counterActions',
            numberFrame: 'text.numberFrameActions',
            fractionTile: 'text.fractionTileActions',
            coloredTiles: 'text.coloredTileActions',
            linearPiece: 'text.linearPieceActions',
            patternShapes: 'text.patternBlockActions',
            coinsAndCounters: 'text.coinsAndCountersActions',
            protractor: 'text.protractorActions',
            manipulativeGroup: 'text.groupActions',
            shades: 'text.shadeActions',
            linkingCube: 'text.linkingCubesActions',
            numberRacks: 'text.numberRackActions',
            arrayModel: 'text.arrayModelActions',
            table: 'text.tableActions',
            tableText: 'text.cellActions',
        };
        for (var _b = 0, _d = Object.keys(headingMap); _b < _d.length; _b++) {
            var prefix = _d[_b];
            if (manipId.startsWith(prefix))
                return t(headingMap[prefix]);
        }
        // Fallback for any unrecognized manipulative
        return t('text.manipulativeActions');
    };
    // Friendly manipulative name (bare noun, no "Actions" suffix) used in
    // screen-reader announcements. For annotations, reuses the existing
    // `getAnnotationTypeName` so line/arrow/rect/ellipse variants are read
    // naturally ("Arrow line position is in locked state" etc.).
    var getManipulativeFriendlyName = function () {
        var _a;
        if (isAnnotationMenu)
            return getAnnotationTypeName();
        if (isTextMenu)
            return t('text.textItem', { defaultValue: 'Text' });
        if (isShadeMenu)
            return t('text.shade');
        if (isBeadMenu)
            return t('text.numberRack');
        var manipId = (_a = contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId) !== null && _a !== void 0 ? _a : '';
        var nameMap = {
            spinner: 'text.spinner',
            dice: 'text.dice',
            deck: 'text.manipulativeCardDeck',
            card: 'text.manipulativeCardDeck',
            analog: 'text.manipulativeClock',
            timer: 'text.manipulativeTimer',
            numberChart: 'text.manipulativeNumberChart',
            numberLine: 'text.manipulativeNumberLine',
            counter: 'text.manipulativeCounter',
            numberFrameCounter: 'text.manipulativeCounter',
            numberFrame: 'text.manipulativeNumberFrame',
            fractionTiles: 'text.manipulativeFractionTile',
            coloredTiles: 'text.manipulativeColoredTile',
            patternShapes: 'text.manipulativePatternBlock',
            coinsAndCounters: 'text.coinsAndCounters',
            protractor: 'text.manipulativeProtractor',
            linkingCubes: 'text.manipulativeLinkingCubes',
            arrayModel: 'text.manipulativeArrayModel',
            numberRacks: 'text.numberRack',
            jumpBar: 'text.jumpBarLabel',
            manipulativeGroup: 'text.group',
        };
        for (var _b = 0, _d = Object.keys(nameMap); _b < _d.length; _b++) {
            var prefix = _d[_b];
            if (manipId.startsWith(prefix))
                return t(nameMap[prefix]);
        }
        return t('text.manipulative', { defaultValue: 'Manipulative' });
    };
    // Get friendly type name for announcements
    var getAnnotationTypeName = function () {
        if (!selectedAnnotation)
            return t('text.annotation');
        if (selectedAnnotation.type === 'line') {
            var arrows = selectedAnnotation.data.arrowheadStyle;
            if ((arrows === null || arrows === void 0 ? void 0 : arrows.start) === 'arrow' && (arrows === null || arrows === void 0 ? void 0 : arrows.end) === 'arrow')
                return t('text.doubleArrowLine');
            if ((arrows === null || arrows === void 0 ? void 0 : arrows.end) === 'arrow' || (arrows === null || arrows === void 0 ? void 0 : arrows.start) === 'arrow')
                return t('text.arrowLine');
            return t('text.line');
        }
        if (selectedAnnotation.type === 'rectangle') {
            return selectedAnnotation.data.style === 'fill'
                ? t('text.filledRectangle')
                : t('text.outlineRectangle');
        }
        if (selectedAnnotation.type === 'ellipse') {
            return selectedAnnotation.data.style === 'fill'
                ? t('text.filledEllipse')
                : t('text.outlineEllipse');
        }
        return t('text.annotation');
    };
    // Extract shade index from ID (e.g. "shades_id_abc123_2" → 2)
    var shadeIndex = isShadeMenu
        ? (function () {
            var parts = contextMenu.manipulativeId.split('_');
            return +parts[parts.length - 1];
        })()
        : -1;
    var selectedShade = isShadeMenu ? shades[shadeIndex] : null;
    // Get the selected manipulative data
    // For groups (manipulativeGroup_xxx), find the first member of the group
    // For cards (card_xxx), look inside parent deck's cards[] array
    var selectedItem = contextMenu && !isShadeMenu
        ? isCardMenu
            ? (function () {
                for (var _a = 0, manipulative_1 = manipulative; _a < manipulative_1.length; _a++) {
                    var m = manipulative_1[_a];
                    if (m.data.type === 'deck') {
                        var card = m.data.cards.find(function (c) { return c.id === contextMenu.manipulativeId; });
                        if (card)
                            return { type: 'card', data: card };
                    }
                }
                return null;
            })()
            : contextMenu.manipulativeId.startsWith('manipulativeGroup_')
                ? manipulative.find(function (m) { return m.data.groupId === contextMenu.manipulativeId; })
                : manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; })
        : null;
    var positionLock = isShadeMenu
        ? ((_1 = (_0 = (_z = selectedShade === null || selectedShade === void 0 ? void 0 : selectedShade.data) === null || _z === void 0 ? void 0 : _z.lock) === null || _0 === void 0 ? void 0 : _0.positioning) !== null && _1 !== void 0 ? _1 : false)
        : isAnnotationMenu
            ? ((_4 = (_3 = (_2 = selectedAnnotation === null || selectedAnnotation === void 0 ? void 0 : selectedAnnotation.data) === null || _2 === void 0 ? void 0 : _2.lock) === null || _3 === void 0 ? void 0 : _3.positioning) !== null && _4 !== void 0 ? _4 : false)
            : isTextMenu
                ? ((_7 = (_6 = (_5 = selectedTextItem === null || selectedTextItem === void 0 ? void 0 : selectedTextItem.data) === null || _5 === void 0 ? void 0 : _5.lock) === null || _6 === void 0 ? void 0 : _6.positioning) !== null && _7 !== void 0 ? _7 : false)
                : isTableTextMenu
                    ? ((_10 = (_9 = (_8 = selectedTableItem === null || selectedTableItem === void 0 ? void 0 : selectedTableItem.data) === null || _8 === void 0 ? void 0 : _8.lock) === null || _9 === void 0 ? void 0 : _9.positioning) !== null && _10 !== void 0 ? _10 : false)
                    : isCardMenu
                        ? ((_13 = (_12 = (_11 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _11 === void 0 ? void 0 : _11.lock) === null || _12 === void 0 ? void 0 : _12.positioning) !== null && _13 !== void 0 ? _13 : false)
                        : ((_16 = (_15 = (_14 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _14 === void 0 ? void 0 : _14.lock) === null || _15 === void 0 ? void 0 : _15.positioning) !== null && _16 !== void 0 ? _16 : false);
    var editingLock = isShadeMenu
        ? ((_19 = (_18 = (_17 = selectedShade === null || selectedShade === void 0 ? void 0 : selectedShade.data) === null || _17 === void 0 ? void 0 : _17.lock) === null || _18 === void 0 ? void 0 : _18.editing) !== null && _19 !== void 0 ? _19 : false)
        : isAnnotationMenu
            ? ((_22 = (_21 = (_20 = selectedAnnotation === null || selectedAnnotation === void 0 ? void 0 : selectedAnnotation.data) === null || _20 === void 0 ? void 0 : _20.lock) === null || _21 === void 0 ? void 0 : _21.editing) !== null && _22 !== void 0 ? _22 : false)
            : isTextMenu
                ? ((_25 = (_24 = (_23 = selectedTextItem === null || selectedTextItem === void 0 ? void 0 : selectedTextItem.data) === null || _23 === void 0 ? void 0 : _23.lock) === null || _24 === void 0 ? void 0 : _24.editing) !== null && _25 !== void 0 ? _25 : false)
                : isTableTextMenu
                    ? ((_28 = (_27 = (_26 = selectedTableItem === null || selectedTableItem === void 0 ? void 0 : selectedTableItem.data) === null || _26 === void 0 ? void 0 : _26.lock) === null || _27 === void 0 ? void 0 : _27.editing) !== null && _28 !== void 0 ? _28 : false)
                    : isCardMenu
                        ? ((_31 = (_30 = (_29 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _29 === void 0 ? void 0 : _29.lock) === null || _30 === void 0 ? void 0 : _30.editing) !== null && _31 !== void 0 ? _31 : false)
                        : ((_34 = (_33 = (_32 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _32 === void 0 ? void 0 : _32.lock) === null || _33 === void 0 ? void 0 : _33.editing) !== null && _34 !== void 0 ? _34 : false);
    var isPresetItem = isCardMenu
        ? ((_36 = (_35 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _35 === void 0 ? void 0 : _35.isPreset) !== null && _36 !== void 0 ? _36 : false)
        : ((_38 = (_37 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _37 === void 0 ? void 0 : _37.isPreset) !== null && _38 !== void 0 ? _38 : false);
    // Disabled buttons based on lock state — same logic as sidebar
    var lockDisabledButtons = useMemo(function () {
        return getDisabledButtonsByLock({
            selectedTool: selectedTool,
            text: text,
            table: table,
            annotations: annotations,
            shades: shades,
            manipulative: manipulative,
        });
    }, [selectedTool, text, table, annotations, shades, manipulative]);
    // Build menu items dynamically based on manipulative type
    var buildMenuItems = useCallback(function () {
        var _a, _b, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79, _80, _81, _82, _83;
        if (!contextMenu)
            return [];
        // Canvas pan menu — opened when panning mode is active and the user presses
        // Enter/Space on the whiteboard. Provides keyboard/VO access to pan the canvas.
        if (contextMenu.manipulativeId === 'canvas_pan') {
            return [
                { id: 'panUp', label: t('text.panUp') },
                { id: 'panDown', label: t('text.panDown') },
                { id: 'panLeft', label: t('text.panLeft') },
                { id: 'panRight', label: t('text.panRight') },
                { id: 'exit', label: t('text.exitMenu'), isBack: true },
            ];
        }
        // ColoredTiles cell-level context menu (synthetic _cell_R_C suffix on the
        // manipulativeId). Only entry point is via keyboard cell navigation; the
        // only action is Unsnap, which extracts the cell as a standalone 1×1 tile.
        if (/^coloredTiles_[^_]+_cell_\d+_\d+$/.test(contextMenu.manipulativeId)) {
            return [
                { id: 'unsnapCell', label: t('text.coloredTilesUnsnap') },
                { id: 'exit', label: t('text.exitMenu'), isBack: true },
            ];
        }
        // Bead-specific menu (NumberRack beads)
        if (isBeadMenu) {
            // Compute the bead's current position and its clamp bounds so we can
            // disable Shift Left / Shift Right when the bead cannot move further.
            var rack = manipulative.find(function (m) { return m.data.id === beadRackId; });
            var rods = (_a = rack === null || rack === void 0 ? void 0 : rack.data) === null || _a === void 0 ? void 0 : _a.rods;
            var rod = rods === null || rods === void 0 ? void 0 : rods[beadRodIdx];
            var positions = (_b = rod === null || rod === void 0 ? void 0 : rod.data.beadsRawPosition) !== null && _b !== void 0 ? _b : [];
            var totalBeads = positions.length;
            var currentPos = (_d = positions[beadBeadIdx]) !== null && _d !== void 0 ? _d : 0;
            // Same constants as NumberRackManipulative
            var BEAD_SPACING_C = 39.4;
            var BEAD_DIAMETER_C = 39.4;
            var ROD_LEFT_PADDING_C = 3.4;
            var ROD_WIDTH_C = 873.6;
            var minForBead = ROD_LEFT_PADDING_C + beadBeadIdx * BEAD_SPACING_C;
            var maxForBead = ROD_WIDTH_C -
                BEAD_DIAMETER_C -
                ROD_LEFT_PADDING_C -
                (totalBeads - beadBeadIdx - 1) * BEAD_SPACING_C;
            var canShiftLeft = totalBeads > 0 && currentPos - minForBead > 0.5;
            var canShiftRight = totalBeads > 0 && maxForBead - currentPos > 0.5;
            return [
                { id: 'beadShiftLeft', label: t('text.beadShiftLeft'), disabled: !canShiftLeft },
                { id: 'beadShiftRight', label: t('text.beadShiftRight'), disabled: !canShiftRight },
                { id: 'exit', label: t('text.exitMenu'), isBack: true },
            ];
        }
        // Shade-specific menu
        if (isShadeMenu && selectedShade) {
            var items = [];
            var isRevealed = selectedShade.data.revealed;
            var isLocked = positionLock;
            // 1. Show / Hide
            items.push({
                id: 'shadeVisibility',
                label: isRevealed ? t('text.hideShadeContent') : t('text.showShadeContent'),
            });
            // 2. Change Size — unified submenu matching annotation shapes
            items.push({
                id: 'changeSize',
                label: t('text.changeSizeAction'),
                disabled: positionLock,
                submenu: [
                    {
                        id: 'shadeIncreaseWidth',
                        label: t('text.increaseWidth'),
                        disabled: positionLock,
                    },
                    {
                        id: 'shadeDecreaseWidth',
                        label: t('text.decreaseWidth'),
                        disabled: positionLock,
                    },
                    {
                        id: 'shadeIncreaseHeight',
                        label: t('text.increaseHeight'),
                        disabled: positionLock,
                    },
                    {
                        id: 'shadeDecreaseHeight',
                        label: t('text.decreaseHeight'),
                        disabled: positionLock,
                    },
                ],
            });
            // 4. Move
            items.push({
                id: 'move',
                label: t('text.moveAction'),
                disabled: positionLock,
                submenu: [
                    { id: 'moveUp', label: t('text.moveUp'), disabled: positionLock },
                    { id: 'moveDown', label: t('text.moveDown'), disabled: positionLock },
                    { id: 'moveLeft', label: t('text.moveLeft'), disabled: positionLock },
                    { id: 'moveRight', label: t('text.moveRight'), disabled: positionLock },
                ],
            });
            // 5. Lock
            items.push({
                id: 'shadeLock',
                label: isLocked ? t('text.unlock') : t('text.lock'),
            });
            // 6. Copy
            items.push({
                id: 'shadeCopy',
                label: t('text.copy'),
            });
            // 7. Delete
            items.push({
                id: 'shadeDelete',
                label: t('text.deleteAction'),
                disabled: positionLock,
            });
            items.push({ id: 'exit', label: t('text.exitMenu'), isBack: true });
            return items;
        }
        // Annotation-specific menu (lines, rectangles, ellipses)
        if (isAnnotationMenu && selectedAnnotation) {
            var items = [];
            // Thickness values: highlighterCollapsible uses index * 4 + 3 = [3, 7, 11, 15]
            // Default line creation uses thickness: 2, so normalize it to the nearest option
            var rawThickness = (_e = selectedAnnotation.data.thickness) !== null && _e !== void 0 ? _e : 2;
            var normalizedThickness_1 = rawThickness <= 3 ? 3 : rawThickness <= 7 ? 7 : rawThickness <= 11 ? 11 : 15;
            var THICKNESS_OPTS = [
                {
                    id: 'thicknessExtraFine',
                    label: t('text.thicknessExtraFine'),
                    value: 3,
                },
                { id: 'thicknessFine', label: t('text.thicknessFine'), value: 7 },
                { id: 'thicknessMedium', label: t('text.thicknessMedium'), value: 11 },
                { id: 'thicknessBroad', label: t('text.thicknessBroad'), value: 15 },
            ];
            items.push({
                id: 'move',
                label: t('text.moveAction'),
                disabled: positionLock,
                submenu: [
                    { id: 'moveUp', label: t('text.moveUp'), disabled: positionLock },
                    { id: 'moveDown', label: t('text.moveDown'), disabled: positionLock },
                    { id: 'moveLeft', label: t('text.moveLeft'), disabled: positionLock },
                    {
                        id: 'moveRight',
                        label: t('text.moveRight'),
                        disabled: positionLock,
                    },
                ],
            });
            {
                var rotateDisabled = selectedAnnotation.type === 'line' ? false : editingLock;
                items.push({
                    id: 'rotate',
                    label: t('text.rotateAction'),
                    disabled: rotateDisabled,
                    submenu: [
                        {
                            id: 'rotateClockwise',
                            label: t('text.rotateClockwise'),
                            disabled: rotateDisabled,
                        },
                        {
                            id: 'rotateAnticlockwise',
                            label: t('text.rotateAnticlockwise'),
                            disabled: rotateDisabled,
                        },
                    ],
                });
            }
            if (selectedAnnotation.type === 'line') {
                items.push({
                    id: 'thickness',
                    label: t('text.thicknessAction'),
                    disabled: editingLock,
                    submenu: THICKNESS_OPTS.map(function (th) { return ({
                        id: th.id,
                        label: th.label,
                        disabled: editingLock,
                        selected: normalizedThickness_1 === th.value,
                    }); }),
                });
            }
            {
                var sizeSubmenu = [
                    {
                        id: 'increaseWidth',
                        label: t('text.increaseWidth'),
                        disabled: editingLock,
                    },
                    {
                        id: 'decreaseWidth',
                        label: t('text.decreaseWidth'),
                        disabled: editingLock,
                    },
                ];
                if (selectedAnnotation.type !== 'line') {
                    sizeSubmenu.push({
                        id: 'increaseHeight',
                        label: t('text.increaseHeight'),
                        disabled: editingLock,
                    }, {
                        id: 'decreaseHeight',
                        label: t('text.decreaseHeight'),
                        disabled: editingLock,
                    });
                }
                items.push({
                    id: 'changeSize',
                    label: t('text.changeSizeAction'),
                    disabled: editingLock,
                    submenu: sizeSubmenu,
                });
            }
            {
                var COLOR_OPTS = [
                    { id: 'colorRed', label: t('text.red'), hex: ColorEnum.RED },
                    { id: 'colorYellow', label: t('text.yellow'), hex: ColorEnum.YELLOW },
                    { id: 'colorBlue', label: t('text.blue'), hex: ColorEnum.BLUE },
                    { id: 'colorBrown', label: t('text.brown'), hex: ColorEnum.BROWN },
                    { id: 'colorOrange', label: t('text.orange'), hex: ColorEnum.ORANGE },
                    { id: 'colorGreen', label: t('text.green'), hex: ColorEnum.GREEN },
                    { id: 'colorPurple', label: t('text.purple'), hex: ColorEnum.PURPLE },
                    { id: 'colorBlack', label: t('text.black'), hex: ColorEnum.BLACK },
                    { id: 'colorWhite', label: t('text.white'), hex: ColorEnum.WHITE },
                    { id: 'colorGray', label: t('text.gray'), hex: ColorEnum.GRAY },
                ];
                var currentColor_1 = (_f = selectedAnnotation.data.color) === null || _f === void 0 ? void 0 : _f.toLowerCase();
                items.push({
                    id: 'changeColor',
                    label: t('text.changeColorAction'),
                    disabled: editingLock,
                    submenu: COLOR_OPTS.map(function (c) { return ({
                        id: c.id,
                        label: c.label,
                        disabled: editingLock,
                        selected: currentColor_1 === c.hex.toLowerCase(),
                    }); }),
                });
            }
            {
                var isLine = selectedAnnotation.type === 'line';
                var editingLabel = editingLock
                    ? isLine
                        ? t('text.unlockLength')
                        : t('text.unlockSizingAction')
                    : isLine
                        ? t('text.lockLength')
                        : t('text.lockSizingAction');
                items.push({
                    id: 'lock',
                    label: t('text.lockAction'),
                    submenu: [
                        {
                            id: 'lockPosition',
                            label: positionLock ? t('text.unlockPositionAction') : t('text.lockPositionAction'),
                        },
                        {
                            id: 'lockEditing',
                            label: editingLabel,
                        },
                    ],
                });
            }
            items.push({
                id: 'annotationDuplicate',
                label: t('text.duplicateAction'),
            });
            items.push({
                id: 'annotationDelete',
                label: t('text.deleteAction'),
                disabled: positionLock || editingLock,
            });
            items.push({ id: 'exit', label: t('text.exitMenu'), isBack: true });
            return items;
        }
        // Text item menu
        console.log('[ContextMenu menuItems] isTableTextMenu:', isTableTextMenu, 'selectedTableItem:', !!selectedTableItem, 'tableTextIndex:', tableTextIndex, 'isTableCellMenu:', isTableCellMenu);
        if (isTableTextMenu && selectedTableItem) {
            var tableData = selectedTableItem.data;
            var tNumRows = (_h = (_g = tableData.rowsHeight) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 1;
            var tNumCols = (_k = (_j = tableData.colWidth) === null || _j === void 0 ? void 0 : _j.length) !== null && _k !== void 0 ? _k : 1;
            var tPosLocked = (_m = (_l = tableData.lock) === null || _l === void 0 ? void 0 : _l.positioning) !== null && _m !== void 0 ? _m : false;
            var tEditLocked = (_p = (_o = tableData.lock) === null || _o === void 0 ? void 0 : _o.editing) !== null && _p !== void 0 ? _p : false;
            var tCellSelectMode = (_q = tableData.cellSelectMode) !== null && _q !== void 0 ? _q : false;
            var items = [];
            items.push({
                id: 'tableRows',
                label: t('text.tableRows'),
                disabled: tPosLocked,
                submenu: [
                    { id: 'tableAddRowAbove', label: t('text.tableAddRowAbove'), disabled: tPosLocked || tNumRows >= 20 },
                    { id: 'tableAddRowBelow', label: t('text.tableAddRowBelow'), disabled: tPosLocked || tNumRows >= 20 },
                    { id: 'tableDeleteRow', label: t('text.tableDeleteRow'), disabled: tPosLocked || tNumRows <= 1 },
                ],
            });
            items.push({
                id: 'tableColumns',
                label: t('text.tableColumns'),
                disabled: tPosLocked,
                submenu: [
                    { id: 'tableAddColLeft', label: t('text.tableAddColumnLeft'), disabled: tPosLocked || tNumCols >= 20 },
                    { id: 'tableAddColRight', label: t('text.tableAddColumnRight'), disabled: tPosLocked || tNumCols >= 20 },
                    { id: 'tableDeleteCol', label: t('text.tableDeleteColumn'), disabled: tPosLocked || tNumCols <= 1 },
                ],
            });
            items.push({
                id: 'tableToolbar',
                label: t('text.tableToolbar'),
                disabled: tEditLocked,
            });
            var TABLE_TEXT_COLOR_OPTS = [
                { id: 'tableLevelTextColor_RED', label: t('text.red'), hex: ColorEnum.RED },
                { id: 'tableLevelTextColor_YELLOW', label: t('text.yellow'), hex: ColorEnum.YELLOW },
                { id: 'tableLevelTextColor_BLUE', label: t('text.blue'), hex: ColorEnum.BLUE },
                { id: 'tableLevelTextColor_BROWN', label: t('text.brown'), hex: ColorEnum.BROWN },
                { id: 'tableLevelTextColor_ORANGE', label: t('text.orange'), hex: ColorEnum.ORANGE },
                { id: 'tableLevelTextColor_GREEN', label: t('text.green'), hex: ColorEnum.GREEN },
                { id: 'tableLevelTextColor_PURPLE', label: t('text.purple'), hex: ColorEnum.PURPLE },
                { id: 'tableLevelTextColor_BLACK', label: t('text.black'), hex: ColorEnum.BLACK },
                { id: 'tableLevelTextColor_WHITE', label: t('text.white'), hex: ColorEnum.WHITE },
                { id: 'tableLevelTextColor_GRAY', label: t('text.gray'), hex: ColorEnum.GRAY },
            ];
            items.push({
                id: 'tableChangeColor',
                label: t('text.changeColorAction'),
                disabled: tEditLocked,
                submenu: TABLE_TEXT_COLOR_OPTS.map(function (c) { return ({ id: c.id, label: c.label }); }),
            });
            var TABLE_BG_COLOR_OPTS = [
                { id: 'tableLevelBgColor_RED', label: t('text.red'), hex: ColorEnum.RED },
                { id: 'tableLevelBgColor_YELLOW', label: t('text.yellow'), hex: ColorEnum.YELLOW },
                { id: 'tableLevelBgColor_BLUE', label: t('text.blue'), hex: ColorEnum.BLUE },
                { id: 'tableLevelBgColor_BROWN', label: t('text.brown'), hex: ColorEnum.BROWN },
                { id: 'tableLevelBgColor_ORANGE', label: t('text.orange'), hex: ColorEnum.ORANGE },
                { id: 'tableLevelBgColor_GREEN', label: t('text.green'), hex: ColorEnum.GREEN },
                { id: 'tableLevelBgColor_PURPLE', label: t('text.purple'), hex: ColorEnum.PURPLE },
                { id: 'tableLevelBgColor_BLACK', label: t('text.black'), hex: ColorEnum.BLACK },
                { id: 'tableLevelBgColor_WHITE', label: t('text.white'), hex: ColorEnum.WHITE },
                { id: 'tableLevelBgColor_GRAY', label: t('text.gray'), hex: ColorEnum.GRAY },
                { id: 'tableLevelBgColor_NONE', label: t('text.none'), hex: '' },
            ];
            items.push({
                id: 'tableChangeBgColor',
                label: t('text.changeBackgroundColorAction'),
                disabled: tEditLocked,
                submenu: TABLE_BG_COLOR_OPTS.map(function (c) { return ({ id: c.id, label: c.label }); }),
            });
            // Shared items: Move, Scale, Lock, Duplicate, Delete
            items.push({
                id: 'move',
                label: t('text.moveAction'),
                disabled: tPosLocked,
                submenu: [
                    { id: 'moveUp', label: t('text.moveUp'), disabled: tPosLocked },
                    { id: 'moveDown', label: t('text.moveDown'), disabled: tPosLocked },
                    { id: 'moveLeft', label: t('text.moveLeft'), disabled: tPosLocked },
                    { id: 'moveRight', label: t('text.moveRight'), disabled: tPosLocked },
                ],
            });
            items.push({
                id: 'lock',
                label: t('text.lockAction'),
                submenu: [
                    {
                        id: 'lockPosition',
                        label: tPosLocked ? t('text.unlockPositionAction') : t('text.lockPositionAction'),
                    },
                    {
                        id: 'lockEditing',
                        label: tEditLocked ? t('text.unlockEditingAction') : t('text.lockEditingAction'),
                    },
                ],
            });
            items.push({ id: 'duplicate', label: t('text.duplicateAction') });
            items.push({
                id: 'delete',
                label: t('text.deleteAction'),
                disabled: tPosLocked,
            });
            items.push({ id: 'exit', label: t('text.exitMenu'), isBack: true });
            return items;
        }
        if (isTableCellMenu && cellMenuTableItem) {
            var tPosLocked = (_t = (_s = (_r = cellMenuTableItem.data) === null || _r === void 0 ? void 0 : _r.lock) === null || _s === void 0 ? void 0 : _s.positioning) !== null && _t !== void 0 ? _t : false;
            var items = [];
            items.push({ id: 'tableCellEdit', label: t('text.tableCellEdit'), disabled: cellIsLocked });
            items.push({
                id: 'tableCellLock',
                label: cellIsLocked ? t('text.tableCellUnlock') : t('text.tableCellLock'),
            });
            items.push({ id: 'tableToolbar', label: t('text.tableToolbar'), disabled: cellIsLocked });
            var CELL_TEXT_COLOR_OPTS = [
                { id: 'tableCellTextColor_RED', label: t('text.red'), hex: ColorEnum.RED },
                { id: 'tableCellTextColor_YELLOW', label: t('text.yellow'), hex: ColorEnum.YELLOW },
                { id: 'tableCellTextColor_BLUE', label: t('text.blue'), hex: ColorEnum.BLUE },
                { id: 'tableCellTextColor_BROWN', label: t('text.brown'), hex: ColorEnum.BROWN },
                { id: 'tableCellTextColor_ORANGE', label: t('text.orange'), hex: ColorEnum.ORANGE },
                { id: 'tableCellTextColor_GREEN', label: t('text.green'), hex: ColorEnum.GREEN },
                { id: 'tableCellTextColor_PURPLE', label: t('text.purple'), hex: ColorEnum.PURPLE },
                { id: 'tableCellTextColor_BLACK', label: t('text.black'), hex: ColorEnum.BLACK },
                { id: 'tableCellTextColor_WHITE', label: t('text.white'), hex: ColorEnum.WHITE },
                { id: 'tableCellTextColor_GRAY', label: t('text.gray'), hex: ColorEnum.GRAY },
            ];
            var cellCi = cellMenuRow * cellMenuNumCols + cellMenuCol;
            var currentTextHex_1 = (_z = (_y = (_x = (_w = (_v = (_u = cellMenuTableItem.data) === null || _u === void 0 ? void 0 : _u.cellData) === null || _v === void 0 ? void 0 : _v[cellCi]) === null || _w === void 0 ? void 0 : _w.data) === null || _x === void 0 ? void 0 : _x.color) === null || _y === void 0 ? void 0 : _y.toLowerCase()) !== null && _z !== void 0 ? _z : '';
            items.push({
                id: 'tableCellChangeColor',
                label: t('text.changeColorAction'),
                disabled: cellIsLocked,
                submenu: CELL_TEXT_COLOR_OPTS.map(function (c) { return ({
                    id: c.id,
                    label: c.label,
                    selected: currentTextHex_1 === c.hex.toLowerCase(),
                }); }),
            });
            var CELL_BG_COLOR_OPTS = [
                { id: 'tableCellBgColor_RED', label: t('text.red'), hex: ColorEnum.RED },
                { id: 'tableCellBgColor_YELLOW', label: t('text.yellow'), hex: ColorEnum.YELLOW },
                { id: 'tableCellBgColor_BLUE', label: t('text.blue'), hex: ColorEnum.BLUE },
                { id: 'tableCellBgColor_BROWN', label: t('text.brown'), hex: ColorEnum.BROWN },
                { id: 'tableCellBgColor_ORANGE', label: t('text.orange'), hex: ColorEnum.ORANGE },
                { id: 'tableCellBgColor_GREEN', label: t('text.green'), hex: ColorEnum.GREEN },
                { id: 'tableCellBgColor_PURPLE', label: t('text.purple'), hex: ColorEnum.PURPLE },
                { id: 'tableCellBgColor_BLACK', label: t('text.black'), hex: ColorEnum.BLACK },
                { id: 'tableCellBgColor_WHITE', label: t('text.white'), hex: ColorEnum.WHITE },
                { id: 'tableCellBgColor_GRAY', label: t('text.gray'), hex: ColorEnum.GRAY },
                { id: 'tableCellBgColor_NONE', label: t('text.none'), hex: '' },
            ];
            var currentBgHex_1 = (_5 = (_4 = (_3 = (_2 = (_1 = (_0 = cellMenuTableItem.data) === null || _0 === void 0 ? void 0 : _0.cellData) === null || _1 === void 0 ? void 0 : _1[cellCi]) === null || _2 === void 0 ? void 0 : _2.data) === null || _3 === void 0 ? void 0 : _3.backgroundColor) === null || _4 === void 0 ? void 0 : _4.toLowerCase()) !== null && _5 !== void 0 ? _5 : '';
            items.push({
                id: 'tableCellChangeBgColor',
                label: t('text.changeBackgroundColorAction'),
                disabled: cellIsLocked,
                submenu: CELL_BG_COLOR_OPTS.map(function (c) { return ({
                    id: c.id,
                    label: c.label,
                    selected: c.hex ? currentBgHex_1 === c.hex.toLowerCase() : currentBgHex_1 === '',
                }); }),
            });
            items.push({ id: 'exit', label: t('text.exitMenu'), isBack: true });
            return items;
        }
        if (isTextMenu && selectedTextItem) {
            var items = [];
            var posLock = (_8 = (_7 = (_6 = selectedTextItem.data) === null || _6 === void 0 ? void 0 : _6.lock) === null || _7 === void 0 ? void 0 : _7.positioning) !== null && _8 !== void 0 ? _8 : false;
            var editLock_1 = (_11 = (_10 = (_9 = selectedTextItem.data) === null || _9 === void 0 ? void 0 : _9.lock) === null || _10 === void 0 ? void 0 : _10.editing) !== null && _11 !== void 0 ? _11 : false;
            items.push({
                id: 'move',
                label: t('text.moveAction'),
                disabled: positionLock,
                submenu: [
                    { id: 'moveUp', label: t('text.moveUp'), disabled: positionLock },
                    { id: 'moveDown', label: t('text.moveDown'), disabled: positionLock },
                    { id: 'moveLeft', label: t('text.moveLeft'), disabled: positionLock },
                    { id: 'moveRight', label: t('text.moveRight'), disabled: positionLock },
                ],
            });
            items.push({
                id: 'rotate',
                label: t('text.rotateAction'),
                disabled: editLock_1,
                submenu: [
                    { id: 'rotateClockwise', label: t('text.rotateClockwise'), disabled: editLock_1 },
                    { id: 'rotateAnticlockwise', label: t('text.rotateAnticlockwise'), disabled: editLock_1 },
                ],
            });
            items.push({
                id: 'changeSize',
                label: t('text.changeSizeAction'),
                disabled: editLock_1,
                submenu: [
                    { id: 'increaseWidth', label: t('text.increaseWidth'), disabled: editLock_1 },
                    { id: 'decreaseWidth', label: t('text.decreaseWidth'), disabled: editLock_1 },
                    { id: 'increaseHeight', label: t('text.increaseHeight'), disabled: editLock_1 },
                    { id: 'decreaseHeight', label: t('text.decreaseHeight'), disabled: editLock_1 },
                ],
            });
            {
                var COLOR_OPTS = [
                    { id: 'colorRed', label: t('text.red'), hex: ColorEnum.RED },
                    { id: 'colorYellow', label: t('text.yellow'), hex: ColorEnum.YELLOW },
                    { id: 'colorBlue', label: t('text.blue'), hex: ColorEnum.BLUE },
                    { id: 'colorBrown', label: t('text.brown'), hex: ColorEnum.BROWN },
                    { id: 'colorOrange', label: t('text.orange'), hex: ColorEnum.ORANGE },
                    { id: 'colorGreen', label: t('text.green'), hex: ColorEnum.GREEN },
                    { id: 'colorPurple', label: t('text.purple'), hex: ColorEnum.PURPLE },
                    { id: 'colorBlack', label: t('text.black'), hex: ColorEnum.BLACK },
                    { id: 'colorWhite', label: t('text.white'), hex: ColorEnum.WHITE },
                    { id: 'colorGray', label: t('text.gray'), hex: ColorEnum.GRAY },
                ];
                var currentColor_2 = (_13 = (_12 = selectedTextItem.data) === null || _12 === void 0 ? void 0 : _12.color) === null || _13 === void 0 ? void 0 : _13.toLowerCase();
                items.push({
                    id: 'changeColor',
                    label: t('text.changeColorAction'),
                    disabled: editLock_1,
                    submenu: COLOR_OPTS.map(function (c) { return ({
                        id: c.id,
                        label: c.label,
                        disabled: editLock_1,
                        selected: currentColor_2 === c.hex.toLowerCase(),
                    }); }),
                });
            }
            // Formatting submenu — same IDs the bottom-panel formatting buttons
            // dispatch, so existing TextManipulativeComp / MathEditor handlers run
            // unchanged. Bold/Italic/Underline have no data-level state to read for
            // a checkmark (they're inline Quill formats); alignment does live on the
            // text data so we surface its current value as a `selected` indicator.
            items.push({
                id: 'textFormatting',
                label: t('text.formattingAction'),
                disabled: editLock_1,
                submenu: [
                    { id: 'bold', label: t('text.bold'), disabled: editLock_1 },
                    { id: 'italic', label: t('text.italic'), disabled: editLock_1 },
                    { id: 'underline', label: t('text.underline'), disabled: editLock_1 },
                    { id: 'larger', label: t('text.increaseFontSize'), disabled: editLock_1 },
                    { id: 'smaller', label: t('text.decreaseFontSize'), disabled: editLock_1 },
                    {
                        id: 'leftAlign',
                        label: t('text.alignLeft'),
                        disabled: editLock_1,
                        // Undefined alignment renders as left (see TextManipulativeComp's
                        // textAlign fallback), so treat it the same here for the checkmark.
                        selected: ((_15 = (_14 = selectedTextItem.data) === null || _14 === void 0 ? void 0 : _14.alignment) !== null && _15 !== void 0 ? _15 : 'left') === 'left',
                    },
                    {
                        id: 'centerAlign',
                        label: t('text.alignCenter'),
                        disabled: editLock_1,
                        selected: ((_16 = selectedTextItem.data) === null || _16 === void 0 ? void 0 : _16.alignment) === 'center',
                    },
                    {
                        id: 'rightAlign',
                        label: t('text.alignRight'),
                        disabled: editLock_1,
                        selected: ((_17 = selectedTextItem.data) === null || _17 === void 0 ? void 0 : _17.alignment) === 'right',
                    },
                    // Add Link — opens BottomPanel's inline URL input via openLinkPopup.
                    { id: 'addLink', label: t('text.addLinkAction'), disabled: editLock_1 },
                ],
            });
            items.push({
                id: 'lock',
                label: t('text.lockAction'),
                submenu: [
                    {
                        id: 'lockPosition',
                        label: posLock ? t('text.unlockPositionAction') : t('text.lockPositionAction'),
                    },
                    {
                        id: 'lockEditing',
                        label: editLock_1 ? t('text.unlockEditingAction') : t('text.lockEditingAction'),
                    },
                ],
            });
            items.push({ id: 'textDuplicate', label: t('text.duplicateAction') });
            items.push({
                id: 'textDelete',
                label: t('text.deleteAction'),
                disabled: posLock || editLock_1,
            });
            items.push({ id: 'exit', label: t('text.exitMenu'), isBack: true });
            return items;
        }
        if (!selectedItem)
            return [];
        var toolRules = getToolRulesForId(contextMenu.manipulativeId);
        var hiddenTools = new Set(toolRules.hiddenTools);
        var disabledTools = new Set(toolRules.disabledTools);
        // In view mode, globally hide state-changing items from every
        // manipulative's context menu: edit and the lock submenu. Delete stays
        // available so users can still remove items in view mode.
        if (isViewMode) {
            hiddenTools.add('edit');
            hiddenTools.add('lock');
        }
        var isHidden = function (toolId) { return hiddenTools.has(toolId); };
        var isDisabled = function (toolId) {
            if (disabledTools.has(toolId))
                return true;
            if (lockDisabledButtons.includes(toolId))
                return true;
            return false;
        };
        // Type-specific primary actions (inserted at the top)
        var typeActions = [];
        var isGroupMenu = contextMenu.manipulativeId.startsWith('manipulativeGroup_');
        var type = selectedItem.type;
        var dataType = selectedItem.data.type;
        if (type === 'spinner') {
            typeActions.push({ id: 'spinWheel', label: t('text.spinWheel') });
        }
        else if (dataType === 'dice') {
            typeActions.push({ id: 'rollDice', label: t('text.rollDice') });
            // Change color submenu — dice listens for `colourChange` event already,
            // so the existing colorRed/Blue/etc. action handler can dispatch to it
            // identically to how annotations work.
            var diceCurrentColor_1 = selectedItem.data.color;
            var DICE_COLOR_OPTS = [
                { id: 'colorRed', label: t('text.red'), key: 'RED' },
                { id: 'colorYellow', label: t('text.yellow'), key: 'YELLOW' },
                { id: 'colorBlue', label: t('text.blue'), key: 'BLUE' },
                { id: 'colorBrown', label: t('text.brown'), key: 'BROWN' },
                { id: 'colorOrange', label: t('text.orange'), key: 'ORANGE' },
                { id: 'colorGreen', label: t('text.green'), key: 'GREEN' },
                { id: 'colorPurple', label: t('text.purple'), key: 'PURPLE' },
                { id: 'colorBlack', label: t('text.black'), key: 'BLACK' },
                { id: 'colorWhite', label: t('text.white'), key: 'WHITE' },
                { id: 'colorGray', label: t('text.gray'), key: 'GRAY' },
            ];
            typeActions.push({
                id: 'changeColor',
                label: t('text.changeColorAction'),
                submenu: DICE_COLOR_OPTS.map(function (c) { return ({
                    id: c.id,
                    label: c.label,
                    selected: diceCurrentColor_1 === c.key,
                }); }),
            });
        }
        else if (isDeckMenu) {
            var deckCards = (_19 = (_18 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _18 === void 0 ? void 0 : _18.cards) !== null && _19 !== void 0 ? _19 : [];
            var unplacedCount = deckCards.filter(function (c) { return !c.placedOnDeck; }).length;
            typeActions.push({ id: 'dealCard', label: t('text.dealCard') });
            typeActions.push({
                id: 'shuffleDeck',
                label: t('text.shuffleDeck'),
                disabled: unplacedCount <= 1,
            });
        }
        else if (isCardMenu) {
            typeActions.push({ id: 'flipCard', label: t('text.flipCard') });
            typeActions.push({ id: 'stackCard', label: t('text.restackCards') });
        }
        else if (type === 'numberRacks' || dataType === 'numberRacks') {
            var rackRods = (_21 = (_20 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _20 === void 0 ? void 0 : _20.rods) !== null && _21 !== void 0 ? _21 : [];
            typeActions.push({
                id: 'addRow',
                label: t('text.addRow'),
                disabled: rackRods.length >= 10,
            });
            typeActions.push({
                id: 'removeRow',
                label: t('text.removeRow'),
                disabled: rackRods.length <= 1,
            });
            typeActions.push({
                id: 'reverseBeads',
                label: t('text.reverseBeads'),
                disabled: rackRods.length < 6,
            });
            typeActions.push({
                id: 'selectRow',
                label: t('text.selectRow'),
                disabled: rackRods.length === 0,
                submenu: rackRods.map(function (_, rowIdx) { return ({
                    id: "selectRow_".concat(rowIdx),
                    label: t('text.rowLabel', { index: rowIdx + 1 }),
                }); }),
            });
        }
        else if (dataType === 'numberFrames') {
            // NumberFrame: "Token Settings" moves focus to the top token-setting-btn
            // in the right panel (see the openTokenSettings case in the action handler).
            typeActions.push({
                id: 'openTokenSettings',
                label: t('text.tokenSettings'),
            });
            // Show / hide 5×5 grid overlay — mirrors the left-toolbox toggle button.
            // Disabled when rows ≤ 5 AND cols ≤ 5 (frame is already small enough that
            // the 5×5 overlay adds no value). Same rule as GameController.tsx:1380.
            var nfData = (_22 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _22 !== void 0 ? _22 : {};
            var nfGridVisible = (_23 = nfData.show5x5Grid) !== null && _23 !== void 0 ? _23 : false;
            var nfRows = (_24 = nfData.rows) !== null && _24 !== void 0 ? _24 : 0;
            var nfCols = (_25 = nfData.cols) !== null && _25 !== void 0 ? _25 : 0;
            typeActions.push({
                id: 'show5x5Grid',
                label: t(nfGridVisible ? 'text.hide5x5Grid' : 'text.show5x5Grid'),
                disabled: nfRows <= 5 && nfCols <= 5,
            });
        }
        else if (dataType === 'linkingCubes') {
            // No type-specific actions here. The "Link Cubes" entry (formerly
            // "Link to Cube") lives under commonItems below and serves both
            // standalone cubes and chain members; the duplicate "Link with
            // another cube" entry was removed since it activated the same
            // target-picker as Link Cubes. Unlink also lives under commonItems.
        }
        else if (type === 'numberLine') {
            var nlData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            var editLocked = (_26 = nlData === null || nlData === void 0 ? void 0 : nlData.lock) === null || _26 === void 0 ? void 0 : _26.editing;
            typeActions.push({
                id: 'nlExtendLeft',
                label: t('text.increaseLeft'),
                disabled: editLocked,
            });
            typeActions.push({
                id: 'nlShrinkLeft',
                label: t('text.decreaseLeft'),
                disabled: editLocked,
            });
            typeActions.push({
                id: 'nlExtendRight',
                label: t('text.increaseRight'),
                disabled: editLocked,
            });
            typeActions.push({
                id: 'nlShrinkRight',
                label: t('text.decreaseRight'),
                disabled: editLocked,
            });
        }
        // Common tools — filtered by hiddenTools
        var commonItems = [];
        // ── Type-specific items at top level ──
        // NumberChart-specific items
        var isNumberChart = contextMenu.manipulativeId.startsWith('numberChart_');
        if (isNumberChart) {
            var ncData = (_27 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _27 !== void 0 ? _27 : {};
            var gridVisible = (_28 = ncData.showGridLines) !== null && _28 !== void 0 ? _28 : true;
            var isInGameMode = (_29 = ncData.isGameMode) !== null && _29 !== void 0 ? _29 : false;
            var ncActionType = (_30 = ncData.actionType) !== null && _30 !== void 0 ? _30 : '';
            var ncStampIndex_1 = (_31 = ncData.stampIndex) !== null && _31 !== void 0 ? _31 : 0;
            var isStampActive_1 = ncActionType === 'stamp';
            // Stamp shape labels (indices 0-10 matching btnData stamp elements)
            var stampShapeLabels = [
                t('text.solidSquare'),
                t('text.transparentSquare'),
                t('text.stampShape2'),
                t('text.stampShape3'),
                t('text.stampShape4'),
                t('text.stampShape5'),
                t('text.stampShape6'),
                t('text.stampShape7'),
                t('text.stampShape8'),
                t('text.stampShape9'),
                t('text.stampShape10'),
            ];
            commonItems.push({
                id: 'ncGridToggle',
                label: gridVisible ? t('text.hideGridLines') : t('text.showGridLines'),
            }); // Choral Counting - flattened controls in submenu
            var choralDir = (_32 = choralDirLocal !== null && choralDirLocal !== void 0 ? choralDirLocal : ncData.choralDirectionIndex) !== null && _32 !== void 0 ? _32 : 0;
            var choralSpeed = (_33 = choralSpeedLocal !== null && choralSpeedLocal !== void 0 ? choralSpeedLocal : ncData.choralSpeed) !== null && _33 !== void 0 ? _33 : 1;
            commonItems.push({
                id: 'ncChoral',
                label: t('text.choralCounting'),
                submenu: [
                    { id: 'ncChoralPlay', label: t('text.choralPlay'), disabled: choralIsPlaying },
                    { id: 'ncChoralPause', label: t('text.choralPause'), disabled: !choralIsPlaying },
                    { id: 'ncChoralNext', label: t('text.choralNext'), disabled: choralIsPlaying },
                    {
                        id: 'ncChoralSpeed_0',
                        label: t('text.speedSlow'),
                        selected: choralSpeed === 0,
                        divider: true,
                    },
                    {
                        id: 'ncChoralSpeed_1',
                        label: t('text.speedNormal'),
                        selected: choralSpeed === 1,
                    },
                    {
                        id: 'ncChoralSpeed_2',
                        label: t('text.speedFast'),
                        selected: choralSpeed === 2,
                    },
                    {
                        id: 'ncChoralDir_0',
                        label: t('text.dirTopRight'),
                        selected: choralDir === 0,
                        divider: true,
                    },
                    {
                        id: 'ncChoralDir_1',
                        label: t('text.dirLeftDown'),
                        selected: choralDir === 1,
                    },
                    {
                        id: 'ncChoralDir_2',
                        label: t('text.dirBottomRight'),
                        selected: choralDir === 2,
                    },
                    {
                        id: 'ncChoralDir_3',
                        label: t('text.dirBottomUp'),
                        selected: choralDir === 3,
                    },
                    {
                        id: 'ncChoralReset',
                        label: t('text.choralReset'),
                        divider: true,
                    },
                ],
            });
            // Stamp color submenu (before stamp shape selection)
            var stampColors = [
                { id: 'ncColor_RED', hex: '#f73530', label: t('text.colorRed') },
                { id: 'ncColor_YELLOW', hex: '#f4c500', label: t('text.colorYellow') },
                { id: 'ncColor_BLUE', hex: '#2a90f7', label: t('text.colorBlue') },
                { id: 'ncColor_BROWN', hex: '#b5674d', label: t('text.colorBrown') },
                { id: 'ncColor_ORANGE', hex: '#ff8901', label: t('text.colorOrange') },
                { id: 'ncColor_GREEN', hex: '#1dac78', label: t('text.colorGreen') },
                { id: 'ncColor_PURPLE', hex: '#c038f7', label: t('text.colorPurple') },
                { id: 'ncColor_BLACK', hex: '#232323', label: t('text.colorBlack') },
                { id: 'ncColor_WHITE', hex: '#EDEDED', label: t('text.colorWhite') },
                { id: 'ncColor_GRAY', hex: '#95918C', label: t('text.colorGray') },
            ];
            commonItems.push({
                id: 'ncStampColor',
                label: t('text.stampColor'),
                submenu: stampColors.map(function (c) { return ({
                    id: c.id,
                    label: c.label,
                    selected: color === c.id.replace('ncColor_', ''),
                }); }),
            });
            commonItems.push({
                id: 'ncStamps',
                label: t('text.stampsAction'),
                submenu: stampShapeLabels.map(function (label, idx) { return ({
                    id: "ncStamp_".concat(idx),
                    label: label,
                    selected: isStampActive_1 && ncStampIndex_1 === idx,
                }); }),
            });
            commonItems.push({
                id: 'ncStampEvery',
                label: t('text.stampEveryMenu'),
                customContent: 'stampEvery',
            });
            // Show / Hide submenu — clubs the per-stamp and global toggles under
            // one parent so the top-level menu is shorter. Child IDs unchanged so
            // the existing action handlers fire identically.
            commonItems.push({
                id: 'ncShowHideMenu',
                label: t('text.showHide'),
                submenu: [
                    { id: 'ncShowHideNumbers', label: t('text.showHideNum') },
                    { id: 'ncShowAllNumbers', label: t('text.showHideAllNum') },
                ],
            });
            // Same hasStamps check as BottomPanel.tsx line 1624
            var hasAnyStamps = (_34 = ncData.cells) === null || _34 === void 0 ? void 0 : _34.some(function (row) {
                return row.some(function (cell) { return cell.iconIndex && cell.iconIndex.length > 0; });
            });
            // Erase submenu — clubs single + all-stamps erase. Parent disabled when
            // there's nothing to erase; children mirror the same flag for SR.
            commonItems.push({
                id: 'ncEraseMenu',
                label: t('text.eraseAction'),
                disabled: !hasAnyStamps,
                submenu: [
                    { id: 'ncEraseStamp', label: t('text.eraseStamp'), disabled: !hasAnyStamps },
                    { id: 'ncEraseAllStamps', label: t('text.eraseAllStamps'), disabled: !hasAnyStamps },
                ],
            });
            commonItems.push({
                id: 'ncGame',
                label: t('text.gameAction'),
                submenu: __spreadArray([
                    { id: 'ncGameStart', label: t('text.startGame') }
                ], (isInGameMode
                    ? [
                        { id: 'ncReplay', label: t('text.replayGame') },
                        { id: 'ncExitGame', label: t('text.exitGame') },
                    ]
                    : []), true),
            });
        }
        // NumberFrame-specific rotate submenu (mirrors the annotation shape menu).
        // Dispatched action IDs 'rotateClockwise' / 'rotateAnticlockwise' are
        // handled in NumberFrames.tsx's bottomPanel listener. Rotate is never
        // disabled for number frames — lock position governs movement, not rotation.
        if (contextMenu.manipulativeId.startsWith('numberFrame_')) {
            commonItems.push({
                id: 'rotate',
                label: t('text.rotateAction'),
                submenu: [
                    {
                        id: 'rotateClockwise',
                        label: t('text.rotateClockwise'),
                    },
                    {
                        id: 'rotateAnticlockwise',
                        label: t('text.rotateAnticlockwise'),
                    },
                ],
            });
        }
        // PatternShapes-specific items at top level (individual shapes and groups)
        if (contextMenu.manipulativeId.startsWith('patternShapes_') ||
            (isGroupMenu && type === 'patternShapes')) {
            var patternData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            var editLock = (_36 = (_35 = patternData === null || patternData === void 0 ? void 0 : patternData.lock) === null || _35 === void 0 ? void 0 : _35.editing) !== null && _36 !== void 0 ? _36 : false;
            commonItems.push({
                id: 'rotate',
                label: t('text.rotateAction'),
                submenu: [
                    { id: 'rotateClockwise', label: t('text.rotateClockwise'), disabled: editLock },
                    { id: 'rotateAnticlockwise', label: t('text.rotateAnticlockwise'), disabled: editLock },
                ],
            });
        }
        // Coins & Counters: per-spec "Flip" action (dice-style random flip).
        // Dispatches a 'flip' bottomPanel event — CoinsAndCountersManipulative
        // handles single counters, CoinsAndCountersGroupOverlay handles groups.
        // Disabled when no flip-enabled counters are in the selection.
        if (contextMenu.manipulativeId.startsWith('coinsAndCounters_') ||
            (isGroupMenu && type === 'coinsAndCounters')) {
            var canFlip = false;
            if (isGroupMenu) {
                canFlip = manipulative.some(function (m) {
                    return m.data.groupId === contextMenu.manipulativeId &&
                        m.data.type === 'coinsAndCounters' &&
                        m.data.flipEnabled;
                });
            }
            else {
                canFlip = !!selectedItem.data.flipEnabled;
            }
            commonItems.push({
                id: 'flip',
                label: t('text.coinsAndCountersFlip'),
                disabled: !canFlip,
            });
        }
        // Tray counter rotate submenu — same dispatch pattern as numberFrames /
        // protractor; TrayCounter listens for 'rotateClockwise' / 'rotateAnticlockwise'
        // on the bottomPanel event. Only applies to standalone tray counters
        // (`type: 'counter'` with `customImage`), not to in-frame `numberFrameCounter`.
        if (contextMenu.manipulativeId.startsWith('counter_') &&
            ((_37 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _37 === void 0 ? void 0 : _37.type) === 'counter' &&
            !!selectedItem.data.customImage) {
            var counterEditLock = (_39 = (_38 = selectedItem.data.lock) === null || _38 === void 0 ? void 0 : _38.editing) !== null && _39 !== void 0 ? _39 : false;
            commonItems.push({
                id: 'rotate',
                label: t('text.rotateAction'),
                submenu: [
                    {
                        id: 'rotateClockwise',
                        label: t('text.rotateClockwise'),
                        disabled: counterEditLock,
                    },
                    {
                        id: 'rotateAnticlockwise',
                        label: t('text.rotateAnticlockwise'),
                        disabled: counterEditLock,
                    },
                ],
            });
        }
        // Protractor-specific items at top level
        if (contextMenu.manipulativeId.startsWith('protractor_')) {
            commonItems.push({
                id: 'rotate',
                label: t('text.rotateAction'),
                submenu: [
                    { id: 'rotateClockwise', label: t('text.rotateClockwise') },
                    { id: 'rotateAnticlockwise', label: t('text.rotateAnticlockwise') },
                ],
            }, {
                id: 'angle',
                label: t('text.angleAction'),
                submenu: [
                    { id: 'angleClockwise', label: t('text.angleClockwise') },
                    { id: 'angleAnticlockwise', label: t('text.angleAnticlockwise') },
                ],
            });
        }
        // Fraction tile specific actions (split and glue)
        var isFractionTile = contextMenu.manipulativeId.startsWith('fractionTiles_');
        if (isFractionTile) {
            var singleSelectedTile = selectedTool.length === 1
                ? manipulative.find(function (m) {
                    return m.data.type === 'fractionTiles' &&
                        (m.data.id === selectedTool[0] ||
                            selectedTool[0].startsWith(m.data.id + '_'));
                })
                : null;
            var singleTileData_1 = singleSelectedTile === null || singleSelectedTile === void 0 ? void 0 : singleSelectedTile.data;
            var singleFractionType = singleTileData_1
                ? ((_40 = singleTileData_1.gluedFrom) === null || _40 === void 0 ? void 0 : _40.length) > 0
                    ? sumAndReduceFractions(singleTileData_1.gluedFrom.map(function (f) { return f.fractionSize; }))
                    : singleTileData_1.fractionSize
                : null;
            var splitDenominator = singleFractionType
                ? getDenominatorFromFractionType(singleFractionType)
                : 0;
            var maxSplitParts = splitDenominator > 0 ? getMaxPartsForDenominator(splitDenominator) : 0;
            var isSmallTileForSplit = (function () {
                var _a, _b, _d, _e;
                if (!singleTileData_1)
                    return false;
                var gluedFrom = singleTileData_1.gluedFrom, fractionSize = singleTileData_1.fractionSize, shapeType = singleTileData_1.shapeType;
                var finalSize = (gluedFrom === null || gluedFrom === void 0 ? void 0 : gluedFrom.length) > 0
                    ? sumAndReduceFractions(gluedFrom.map(function (f) { return f.fractionSize; }))
                    : fractionSize;
                var config = getFractionDimensions(finalSize);
                if (!config)
                    return false;
                return shapeType === 'rectangle'
                    ? ((_b = (_a = config.rectangle) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0) < 32
                    : ((_e = (_d = config.circle) === null || _d === void 0 ? void 0 : _d.sweep) !== null && _e !== void 0 ? _e : 0) < 20;
            })();
            var canSplit = maxSplitParts >= 2 && !isSmallTileForSplit;
            commonItems.push({
                id: 'splitFractionTile',
                label: t('text.scissors'),
                disabled: !canSplit || editingLock,
            });
            // Only show "Add Custom Label" for tiles already in Custom Label mode
            var isCustomLabelTile = (singleTileData_1 === null || singleTileData_1 === void 0 ? void 0 : singleTileData_1.lablesType) === 'custom';
            if (isCustomLabelTile) {
                var canAddCustomLabel = !!singleSelectedTile && !isSmallTileForSplit && !editingLock;
                commonItems.push({
                    id: 'addCustomLabel',
                    label: t('text.addCustomLabel', { defaultValue: 'Add Custom Label' }),
                    disabled: !canAddCustomLabel,
                });
            }
            var FRACTION_TILE_TYPES = [
                'fraction-1',
                'fraction-1-2',
                'fraction-1-3',
                'fraction-1-4',
                'fraction-1-5',
                'fraction-1-6',
                'fraction-1-8',
                'fraction-1-10',
                'fraction-1-12',
            ].map(function (id) {
                var parts = id.split('-');
                var d = parts.length === 2 ? 1 : Number(parts[parts.length - 1]);
                return { id: id, n: 1, d: d };
            });
            commonItems.push({
                id: 'addFractionTile',
                label: t('text.addTile', { defaultValue: 'Add Tile' }),
                submenu: FRACTION_TILE_TYPES.map(function (ft) { return ({
                    id: "addTile_".concat(ft.id),
                    label: ft.d === 1 ? '1' : "1/".concat(ft.d),
                    ariaLabel: getFractionAriaText(ft.n, ft.d),
                }); }),
            });
            // "Combine Tiles" submenu — lists all other fraction tiles of the same
            // shape type on the canvas so the user can check them and combine.
            var contextTile = manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; });
            var currentShape_1 = (_42 = (_41 = contextTile === null || contextTile === void 0 ? void 0 : contextTile.data) === null || _41 === void 0 ? void 0 : _41.shapeType) !== null && _42 !== void 0 ? _42 : 'rectangle';
            var otherFractionTiles = manipulative.filter(function (m) {
                return m.data.type === 'fractionTiles' &&
                    m.data.id !== contextMenu.manipulativeId &&
                    m.data.shapeType === currentShape_1;
            });
            if (otherFractionTiles.length > 0) {
                var getTileWholeCheck_1 = function (td) {
                    var _a, _b, _d, _e;
                    var effectiveSize = ((_a = td.gluedFrom) === null || _a === void 0 ? void 0 : _a.length) > 0
                        ? sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }))
                        : td.fractionSize;
                    var parsed = parseFraction(effectiveSize);
                    var fn = (_d = (_b = td.customNumerator) !== null && _b !== void 0 ? _b : parsed === null || parsed === void 0 ? void 0 : parsed.numerator) !== null && _d !== void 0 ? _d : 1;
                    var fd = (_e = td.customDenominator) !== null && _e !== void 0 ? _e : getDenominatorFromFractionType(effectiveSize);
                    return fd !== 0 && fn === fd;
                };
                var contextTileIsWhole = contextTile ? getTileWholeCheck_1(contextTile.data) : false;
                // Extract plain text from a custom label (for ariaLabel / isEmpty check).
                var getCustomLabelText_1 = function (label) {
                    if (typeof label === 'string')
                        return label.trim();
                    if (Array.isArray(label)) {
                        return label
                            .map(function (item) { return (item.type === 'math' ? item.latex || '' : item.value || ''); })
                            .join(' ')
                            .trim();
                    }
                    return '';
                };
                // Helper: convert LaTeX to an inline HTML span using MathLive's
                // synchronous convertLatexToMarkup.  Used for both fraction labels and
                // custom math items so both go through the same proven rendering path.
                var latexToSpan_1 = function (latex, key) { return (_jsx("span", { dangerouslySetInnerHTML: {
                        __html: (function () {
                            try {
                                return convertLatexToMarkup(latex, { defaultMode: 'inline-math' });
                            }
                            catch (_a) {
                                return latex;
                            }
                        })(),
                    }, style: { display: 'inline-block', verticalAlign: 'middle', fontSize: '1rem' } }, key)); };
                // Render custom label items as React nodes so math displays correctly
                // instead of showing raw LaTeX code.
                var renderCustomLabelVisual_1 = function (label) {
                    if (typeof label === 'string')
                        return _jsx("span", { children: label.trim() });
                    if (!Array.isArray(label))
                        return null;
                    return (_jsx("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }, children: label.map(function (item, i) {
                            return item.type === 'math' ? latexToSpan_1(item.latex || '', i) : (_jsx("span", { children: item.value }, i));
                        }) }));
                };
                // Helper: build the visual label + ariaLabel for a single tile data object.
                var unlabeledCounter_1 = 0;
                var buildTileLabel_1 = function (td) {
                    var _a, _b, _d, _e;
                    var effectiveFractionType = ((_a = td.gluedFrom) === null || _a === void 0 ? void 0 : _a.length) > 0
                        ? sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }))
                        : td.fractionSize;
                    var parsed = parseFraction(effectiveFractionType);
                    var fn = (_d = (_b = td.customNumerator) !== null && _b !== void 0 ? _b : parsed === null || parsed === void 0 ? void 0 : parsed.numerator) !== null && _d !== void 0 ? _d : 1;
                    var fd = (_e = td.customDenominator) !== null && _e !== void 0 ? _e : getDenominatorFromFractionType(effectiveFractionType);
                    var fractionAria = getFractionAriaText(fn, fd, currentShape_1);
                    var visualLabel;
                    var ariaLabel;
                    if (td.lablesType === 'custom') {
                        var customText = getCustomLabelText_1(td.customLabel);
                        if (customText) {
                            visualLabel = renderCustomLabelVisual_1(td.customLabel);
                            ariaLabel = customText;
                        }
                        else {
                            unlabeledCounter_1 += 1;
                            visualLabel = t('text.unlabeledTile', { count: unlabeledCounter_1, defaultValue: 'Unlabeled {{count}}' });
                            ariaLabel = t('text.unlabeledTileAria', { count: unlabeledCounter_1, fraction: fractionAria, defaultValue: 'Unlabeled {{count}}, {{fraction}}' });
                        }
                    }
                    else {
                        ariaLabel = fractionAria;
                        // Use MathLive's convertLatexToMarkup for both whole numbers and
                        // fractions so they render identically to the tile itself.
                        var latex = fd === 1 ? String(fn) : "\\frac{".concat(fn, "}{").concat(fd, "}");
                        visualLabel = latexToSpan_1(latex);
                    }
                    return { visualLabel: visualLabel, ariaLabel: ariaLabel };
                };
                // Context tile is first in the list, pre-checked by default.
                var contextTileData = contextTile === null || contextTile === void 0 ? void 0 : contextTile.data;
                var ctxLabelResult = contextTileData ? buildTileLabel_1(contextTileData) : null;
                var contextTileItem = ctxLabelResult
                    ? {
                        id: "fractionCheck_".concat(contextTileData.id),
                        label: ctxLabelResult.visualLabel,
                        ariaLabel: ctxLabelResult.ariaLabel,
                        isCheckbox: true,
                        diceId: contextTileData.id,
                    }
                    : null;
                var tileCheckboxItems_1 = contextTileItem ? [contextTileItem] : [];
                otherFractionTiles.forEach(function (m) {
                    var td = m.data;
                    var _a = buildTileLabel_1(td), visualLabel = _a.visualLabel, ariaLabel = _a.ariaLabel;
                    tileCheckboxItems_1.push({
                        id: "fractionCheck_".concat(td.id),
                        label: visualLabel,
                        ariaLabel: ariaLabel,
                        isCheckbox: true,
                        diceId: td.id,
                    });
                });
                var allCheckedAreWhole = checkedDiceIds.size > 0 &&
                    Array.from(checkedDiceIds).every(function (tid) {
                        var tile = manipulative.find(function (m) { return m.data.id === tid; });
                        return tile ? getTileWholeCheck_1(tile.data) : false;
                    });
                // For circles: block combining if the context tile OR any checked tile is
                // whole (a full-circle tile cannot combine with anything).
                // For rectangles: keep existing rule — only block when ALL tiles are whole.
                var anyCheckedIsWhole = checkedDiceIds.size > 0 &&
                    Array.from(checkedDiceIds).some(function (tid) {
                        var tile = manipulative.find(function (m) { return m.data.id === tid; });
                        return tile ? getTileWholeCheck_1(tile.data) : false;
                    });
                // Disable "Combine" when whole-tile rules are violated.
                var combineAllWholeDisabled = currentShape_1 === 'circle' ? anyCheckedIsWhole : allCheckedAreWhole;
                // For circle tiles: also disable when the sum of checked fractions > 1.
                // You can't combine sectors whose total sweep exceeds a full circle.
                var gcd_1 = function (a, b) { return (b === 0 ? a : gcd_1(b, a % b)); };
                var combineSumExceedsWhole = currentShape_1 === 'circle' &&
                    (function () {
                        var _a, _b, _d, _e;
                        var num = 0;
                        var den = 1;
                        var _loop_1 = function (tid) {
                            var tile = manipulative.find(function (m) { return m.data.id === tid; });
                            if (!tile)
                                return "continue";
                            var td = tile.data;
                            var effectiveSize = ((_a = td.gluedFrom) === null || _a === void 0 ? void 0 : _a.length) > 0
                                ? sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }))
                                : td.fractionSize;
                            var parsed = parseFraction(effectiveSize);
                            var fn = (_d = (_b = td.customNumerator) !== null && _b !== void 0 ? _b : parsed === null || parsed === void 0 ? void 0 : parsed.numerator) !== null && _d !== void 0 ? _d : 1;
                            var fd = (_e = td.customDenominator) !== null && _e !== void 0 ? _e : getDenominatorFromFractionType(effectiveSize);
                            if (!fd)
                                return "continue";
                            num = num * fd + fn * den;
                            den = den * fd;
                            var g = gcd_1(Math.abs(num), den);
                            num /= g;
                            den /= g;
                        };
                        for (var _f = 0, _g = Array.from(checkedDiceIds); _f < _g.length; _f++) {
                            var tid = _g[_f];
                            _loop_1(tid);
                        }
                        return num > den; // sum > 1
                    })();
                var combineWholeCount = checkedDiceIds.size;
                var combineDisabled = checkedDiceIds.size < 2 || combineAllWholeDisabled || combineSumExceedsWhole;
                tileCheckboxItems_1.push({
                    id: 'combineFractionTilesAction',
                    label: t('text.combine', { defaultValue: 'Combine' }),
                    ariaLabel: combineAllWholeDisabled
                        ? t('text.wholeTilesCannotCombine', {
                            count: combineWholeCount,
                            defaultValue: '{{count}} whole tiles selected, cannot be combined',
                        })
                        : combineSumExceedsWhole
                            ? t('text.sumExceedsWhole', {
                                defaultValue: 'Selected tiles exceed one whole, cannot be combined',
                            })
                            : t('text.combine', { defaultValue: 'Combine' }),
                    isAction: true,
                    disabled: combineDisabled,
                });
                commonItems.push({
                    id: 'combineFractionTiles',
                    label: t('text.combineTiles', { defaultValue: 'Combine Tiles' }),
                    submenu: tileCheckboxItems_1,
                    disabled: editingLock,
                });
            }
        }
        // Clock-specific items (Analog clock)
        var isAnalogClock = contextMenu.manipulativeId.startsWith('analog_');
        if (isAnalogClock) {
            var clockItemData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            var isGeared = (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.movement) === 'geared';
            if (isGeared) {
                commonItems.push({
                    id: 'clockDigitalDisplay',
                    label: (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.digitalClockVisible)
                        ? t('text.hideDigitalDisplay')
                        : t('text.showDigitalDisplay'),
                });
                commonItems.push({
                    id: 'clockRunAndJumpMenu',
                    label: t('text.runAndJump'),
                    submenu: [
                        {
                            id: 'clockRunAndJumpToggle',
                            label: (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.runJumpVisible)
                                ? t('text.hideRunAndJump')
                                : t('text.showRunAndJump'),
                        },
                        {
                            id: 'clockRunPlay',
                            label: (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.clockRunIsRunning) ? t('text.pauseTimer') : t('text.playTimer'),
                            disabled: !(clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.runJumpVisible),
                        },
                        {
                            id: 'clockRunStop',
                            // Distinct key from text.stopAndResetTimer (used by the Timer
                            // Actions menu) so the Run/Jump submenu can read "Stop and
                            // Reset Run" / "Detener y restablecer Iniciar".
                            label: t('text.stopAndResetRun'),
                            disabled: !(clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.runJumpVisible),
                        },
                    ],
                });
                commonItems.push({
                    id: 'clockElapsedTime',
                    // The toggle's persisted state lives at elapsedTimeDisplay.shown
                    // on the clock manipulative data — not elapsedState (which is
                    // local React state inside AnalogClock and undefined here).
                    // Reading the wrong path made this label perma-stuck on "Show".
                    label: ((_43 = clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.elapsedTimeDisplay) === null || _43 === void 0 ? void 0 : _43.shown)
                        ? t('text.hideElapsedTime')
                        : t('text.showElapsedTime'),
                });
            }
            // Update Time submenu — all analog clocks (geared and not-geared)
            if (isAnalogClock) {
                var currentFormat = (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.timeFormat) || '12h';
                var currentPeriod = (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.period) || 'AM';
                var updateTimeItems = [
                    { id: 'clockIncreaseHour', label: t('text.increaseHours') },
                    { id: 'clockDecreaseHour', label: t('text.decreaseHours') },
                    { id: 'clockIncreaseMinute', label: t('text.increaseMinutes') },
                    { id: 'clockDecreaseMinute', label: t('text.decreaseMinutes') },
                ];
                // AM/PM and format toggles — only meaningful when the digital clock
                // face is visible on the workspace (AM/PM + 24h format only show on
                // the digital readout, not on the analog dial).
                var digitalVisible = (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.digitalClockVisible) === true;
                if (isGeared && digitalVisible) {
                    updateTimeItems.push({
                        id: 'clockTogglePeriod',
                        label: currentPeriod === 'AM' ? t('text.switchToPM') : t('text.switchToAM'),
                        disabled: currentFormat !== '12h',
                    });
                    updateTimeItems.push({
                        id: 'clockToggleFormat',
                        label: currentFormat === '12h' ? t('text.switchTo24h') : t('text.switchTo12h'),
                    });
                }
                commonItems.push({
                    id: 'updateTime',
                    label: t('text.updateTime'),
                    submenu: updateTimeItems,
                });
            }
            // Fraction Overlay submenu — all clock types
            var fractionParts = [2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60];
            var currentParts_1 = (_44 = clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.fractionOverlayParts) !== null && _44 !== void 0 ? _44 : null;
            commonItems.push({
                id: 'clockFractionOverlay',
                label: t('text.fractionOverlay'),
                submenu: __spreadArray([
                    {
                        id: 'clockFraction_none',
                        label: t('text.none'),
                        selected: currentParts_1 === null,
                    }
                ], fractionParts.map(function (p, idx) { return ({
                    id: "clockFraction_".concat(idx),
                    label: "".concat(p, " ").concat(t('text.parts')),
                    selected: currentParts_1 === p,
                }); }), true),
            });
            // Fraction Shading toggle — disabled when no fraction overlay is active (matches right panel)
            commonItems.push({
                id: 'clockFractionShading',
                label: ((_45 = clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.fractionOverlayShadingEnabled) !== null && _45 !== void 0 ? _45 : false)
                    ? t('text.shadingOff')
                    : t('text.shadingOn'),
                selected: (_46 = clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.fractionOverlayShadingEnabled) !== null && _46 !== void 0 ? _46 : false,
                disabled: (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.fractionOverlayParts) == null,
            });
            // Color submenu — for fraction overlay shading color
            var clockColors = [
                { id: 'clockColor_RED', label: t('text.colorRed') },
                { id: 'clockColor_YELLOW', label: t('text.colorYellow') },
                { id: 'clockColor_BLUE', label: t('text.colorBlue') },
                { id: 'clockColor_BROWN', label: t('text.colorBrown') },
                { id: 'clockColor_ORANGE', label: t('text.colorOrange') },
                { id: 'clockColor_GREEN', label: t('text.colorGreen') },
                { id: 'clockColor_PURPLE', label: t('text.colorPurple') },
                { id: 'clockColor_BLACK', label: t('text.colorBlack') },
                { id: 'clockColor_WHITE', label: t('text.colorWhite') },
                { id: 'clockColor_GRAY', label: t('text.colorGray') },
            ];
            commonItems.push({
                id: 'clockColor',
                label: t('text.colors'),
                disabled: (clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.fractionOverlayParts) == null ||
                    !((_47 = clockItemData === null || clockItemData === void 0 ? void 0 : clockItemData.fractionOverlayShadingEnabled) !== null && _47 !== void 0 ? _47 : false),
                submenu: clockColors.map(function (c) { return ({
                    id: c.id,
                    label: c.label,
                    selected: color === c.id.replace('clockColor_', ''),
                }); }),
            });
        }
        // Timer-specific items
        var isTimer = contextMenu.manipulativeId.startsWith('timer_');
        if (isTimer) {
            var timerData = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            var isRunning = (timerData === null || timerData === void 0 ? void 0 : timerData.timer_is_running) && !(timerData === null || timerData === void 0 ? void 0 : timerData.timer_is_paused);
            var isStopped = !(timerData === null || timerData === void 0 ? void 0 : timerData.timer_is_running) && !(timerData === null || timerData === void 0 ? void 0 : timerData.timer_is_paused);
            // Timer auto-completed: reached 0:0 while stopped (not manually reset)
            var originalMinutes = (_48 = timerData === null || timerData === void 0 ? void 0 : timerData.timer_minutes) !== null && _48 !== void 0 ? _48 : 10;
            var originalSeconds = (_49 = timerData === null || timerData === void 0 ? void 0 : timerData.timer_seconds) !== null && _49 !== void 0 ? _49 : 0;
            var isAtZero = (timerData === null || timerData === void 0 ? void 0 : timerData.timer_current_minutes) === 0 && (timerData === null || timerData === void 0 ? void 0 : timerData.timer_current_seconds) === 0;
            var isAtOriginal = (timerData === null || timerData === void 0 ? void 0 : timerData.timer_current_minutes) === originalMinutes &&
                (timerData === null || timerData === void 0 ? void 0 : timerData.timer_current_seconds) === originalSeconds;
            // Auto-completed: stopped at 0:0 but original time is non-zero (not manually reset)
            var isComplete = isStopped && isAtZero && (originalMinutes > 0 || originalSeconds > 0) && !isAtOriginal;
            commonItems.push({
                id: 'timerStart',
                label: t('text.startTimer'),
                // Disabled when running, auto-completed, or nothing to count down
                disabled: isRunning || isComplete || (isStopped && isAtZero && !isComplete),
            });
            commonItems.push({
                id: 'timerPause',
                label: t('text.pauseTimerBtn'),
                disabled: !isRunning,
            });
            commonItems.push({
                id: 'timerStop',
                label: t('text.stopAndResetTimer'),
                // Disabled only when already at original stopped position (nothing to reset)
                disabled: isStopped && isAtOriginal,
            });
        }
        // ── Toolbox submenu (flattened — Edit, Scale, Move, Lock, Duplicate) ──
        var toolboxItems = [];
        var anyLock = positionLock || editingLock;
        var isCounterLike = dataType === 'counter' || dataType === 'numberFrameCounter';
        if (!isHidden('edit') && !isCounterLike) {
            toolboxItems.push({
                id: 'edit',
                label: t('text.editAction'),
                disabled: isDisabled('edit') || isPresetItem,
            });
        }
        if (!isHidden('changeSize') && !isHidden('scale')) {
            var currentScale = Math.round(((_50 = selectedItem.data.scale) !== null && _50 !== void 0 ? _50 : 1) * 10) / 10;
            toolboxItems.push({
                id: 'scalePlus',
                label: t('text.scaleUpAction'),
                disabled: currentScale >= 5,
            }, {
                id: 'scaleMinus',
                label: t('text.scaleDownAction'),
                disabled: currentScale <= 0.5,
            });
        }
        // Number frames only use positionLock to gate movement — editing lock
        // governs shape/token edits, not position. Other manipulatives keep the
        // existing anyLock (position OR editing) behavior.
        var moveDisabled = dataType === 'numberFrames' ? positionLock : anyLock;
        // 'moveVertical' is a per-widget rule (see contextMenuHelper.ts) that
        // hides Up/Down for widgets that only meaningfully move along a single
        // axis — currently NumberLineCustomTick, which lives on a 1-D line.
        var hideVerticalMove = isHidden('moveVertical');
        if (!hideVerticalMove) {
            toolboxItems.push({ id: 'moveUp', label: t('text.moveUp'), disabled: moveDisabled }, { id: 'moveDown', label: t('text.moveDown'), disabled: moveDisabled });
        }
        toolboxItems.push({ id: 'moveLeft', label: t('text.moveLeft'), disabled: moveDisabled }, { id: 'moveRight', label: t('text.moveRight'), disabled: moveDisabled });
        if (!isHidden('lock')) {
            var manipId_1 = contextMenu.manipulativeId;
            if (hasSubLockMenu(manipId_1)) {
                toolboxItems.push({
                    id: 'lockPosition',
                    label: positionLock ? t('text.unlockPositionAction') : t('text.lockPositionAction'),
                }, {
                    id: 'lockEditing',
                    // ArrayModel and ColoredTiles both use "Parts" terminology for the
                    // second lock in the left-side toolbar (CollapsibleDiv.tsx:1374 —
                    // labels it `text.parts`). Match that here so context-menu and
                    // sidebar agree on the wording.
                    label: manipId_1.startsWith('arrayModel_') || manipId_1.startsWith('coloredTiles_')
                        ? editingLock
                            ? t('text.unlockPartsAction')
                            : t('text.lockPartsAction')
                        : editingLock
                            ? t('text.unlockEditingAction')
                            : t('text.lockEditingAction'),
                });
                if (hasLengthLock(manipId_1)) {
                    var jumpBar = manipulative.find(function (m) { return m.data.id === manipId_1; });
                    var lengthLock = (_53 = (_52 = (_51 = jumpBar === null || jumpBar === void 0 ? void 0 : jumpBar.data) === null || _51 === void 0 ? void 0 : _51.lock) === null || _52 === void 0 ? void 0 : _52.length) !== null && _53 !== void 0 ? _53 : false;
                    toolboxItems.push({
                        id: 'lockLength',
                        label: lengthLock ? t('text.unlockLength') : t('text.lockLength'),
                    });
                    // JumpBar-only: Snap to / Unsnap from number line. The JB carries
                    // `snappedPosition` (null = unsnapped). Toggle label is computed
                    // from current state; placement on snap follows the chain rule
                    // (1st JB on this NL → parent.startNum; subsequent → end of the
                    // previous snapped JB on the same NL).
                    var isSnapped = ((_55 = (_54 = jumpBar === null || jumpBar === void 0 ? void 0 : jumpBar.data) === null || _54 === void 0 ? void 0 : _54.snappedPosition) !== null && _55 !== void 0 ? _55 : null) !== null;
                    toolboxItems.push({
                        id: 'toggleSnapToLine',
                        label: isSnapped ? t('text.unsnapFromLine') : t('text.snapToLine'),
                        disabled: anyLock,
                    });
                }
            }
            else {
                var isLocked = positionLock || editingLock;
                toolboxItems.push({
                    id: 'lockToggle',
                    label: isLocked ? t('text.unLock') : t('text.lock'),
                });
            }
        }
        if (!isHidden('duplicate')) {
            toolboxItems.push({
                id: 'duplicate',
                label: t('text.duplicateAction'),
                disabled: isDisabled('duplicate'),
            });
        }
        if (toolboxItems.length > 0) {
            commonItems.push({
                id: 'toolbox',
                label: t('text.toolbox'),
                submenu: toolboxItems,
            });
        }
        // Dice-specific: Group Dice submenu and/or Ungroup
        var isDice = contextMenu.manipulativeId.startsWith('dice_');
        var isDiceGroup = isGroupMenu && dataType === 'dice';
        if (isDice || isDiceGroup) {
            var isGroupedDice = isDiceGroup || !!selectedItem.data.groupId;
            var currentGroupId_1 = isDiceGroup
                ? contextMenu.manipulativeId
                : selectedItem.data.groupId;
            // Show Ungroup if the dice is part of a group
            if (isGroupedDice) {
                commonItems.push({
                    id: isDiceGroup ? 'ungroup' : 'ungroupDice',
                    label: t('text.ungroupDice'),
                });
            }
            // Collect ungrouped dice on the canvas (excluding the current dice)
            var otherUngroupedDice = manipulative.filter(function (m) {
                var _a;
                return (((_a = m.data.id) === null || _a === void 0 ? void 0 : _a.startsWith('dice_')) || m.data.type === 'dice') &&
                    !m.data.groupId &&
                    m.data.id !== contextMenu.manipulativeId;
            });
            // Collect other dice groups (not the current group)
            var otherGroupIds_1 = new Set();
            manipulative.forEach(function (m) {
                var _a;
                var gId = m.data.groupId;
                if (gId &&
                    (((_a = m.data.id) === null || _a === void 0 ? void 0 : _a.startsWith('dice_')) || m.data.type === 'dice') &&
                    gId !== currentGroupId_1) {
                    otherGroupIds_1.add(gId);
                }
            });
            if (otherUngroupedDice.length > 0 || otherGroupIds_1.size > 0) {
                var diceSubmenuItems_1 = [];
                // Individual ungrouped dice
                otherUngroupedDice.forEach(function (d) {
                    var _a;
                    var dd = d.data;
                    var rawColor = dd.color || 'BLUE';
                    // Translate the color enum (e.g. "WHITE") via the existing
                    // text.color<Name> keys, then uppercase so the menu label reads
                    // "WHITE die 1" / "BLANCO dados dado 1" — the spec asks for an
                    // uppercased color regardless of language.
                    var colorKey = "text.color".concat(rawColor.charAt(0)).concat(rawColor.slice(1).toLowerCase());
                    var colorName = t(colorKey, { defaultValue: rawColor }).toUpperCase();
                    var itemLabel = t('text.diceLabel', {
                        color: colorName,
                        face: (_a = dd.activeFaceIndex) !== null && _a !== void 0 ? _a : 1,
                    });
                    diceSubmenuItems_1.push({
                        id: "diceCheck_".concat(dd.id),
                        label: itemLabel,
                        isCheckbox: true,
                        diceId: dd.id,
                    });
                });
                // Other dice groups (shown as single entries)
                otherGroupIds_1.forEach(function (gId) {
                    var groupMembers = manipulative.filter(function (m) { return m.data.groupId === gId; });
                    var itemLabel = t('text.diceGroupLabel', {
                        count: groupMembers.length,
                    });
                    diceSubmenuItems_1.push({
                        id: "diceCheck_".concat(gId),
                        label: itemLabel,
                        isCheckbox: true,
                        diceId: gId,
                    });
                });
                diceSubmenuItems_1.push({
                    id: isGroupedDice ? 'addDiceToGroup' : 'mergeSelectedDice',
                    label: t('text.mergeSelected'),
                    isAction: true,
                    disabled: checkedDiceIds.size === 0,
                });
                commonItems.push({
                    id: 'groupDice',
                    label: t('text.groupDice'),
                    submenu: diceSubmenuItems_1,
                });
            }
        }
        else if ((contextMenu.manipulativeId.startsWith('coinsAndCounters_') ||
            (isGroupMenu && type === 'coinsAndCounters')) &&
            !isHidden('group')) {
            // Coins & Counters: keyboard-friendly group picker (mirrors the Pattern Shapes flow).
            // Handles both individual counters (coinsAndCounters_xxx) and groups (manipulativeGroup_xxx).
            var isCcGroup = isGroupMenu && type === 'coinsAndCounters';
            var isGroupedCc = isCcGroup || !!selectedItem.data.groupId;
            var currentCcGroupId_1 = isCcGroup
                ? contextMenu.manipulativeId
                : selectedItem.data.groupId;
            if (isGroupedCc) {
                commonItems.push({
                    id: 'ungroup',
                    label: t('text.ungroup'),
                });
            }
            var hasOtherGroupableCc = manipulative.some(function (m) {
                var _a;
                return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coinsAndCounters' &&
                    m.data.id !== contextMenu.manipulativeId &&
                    !(currentCcGroupId_1 && m.data.groupId === currentCcGroupId_1);
            });
            if (hasOtherGroupableCc) {
                commonItems.push({
                    id: 'groupCcTo',
                    label: t('text.group'),
                    disabled: positionLock,
                });
            }
        }
        else if ((contextMenu.manipulativeId.startsWith('patternShapes_') ||
            (isGroupMenu && type === 'patternShapes')) &&
            !isHidden('group')) {
            // Pattern Blocks: Group submenu (same pattern as Dice)
            // Handles both individual shapes (patternShapes_xxx) and groups (manipulativeGroup_xxx)
            var isPatternGroup = isGroupMenu && type === 'patternShapes';
            var isGroupedPattern = isPatternGroup || !!selectedItem.data.groupId;
            var currentGroupId_2 = isPatternGroup
                ? contextMenu.manipulativeId
                : selectedItem.data.groupId;
            if (isGroupedPattern) {
                commonItems.push({
                    id: isPatternGroup ? 'ungroup' : 'ungroupPattern',
                    label: t('text.ungroup'),
                });
            }
            var isPatternOutline_1 = function (d) {
                return !!d.isOutline || !!d.isStaticOutline || d.shapeType === 'outline';
            };
            // Exclude shapes that belong to the same group as the source (can't group with yourself)
            var hasOtherGroupablePS = manipulative.some(function (m) {
                var _a;
                return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes' &&
                    m.data.id !== contextMenu.manipulativeId &&
                    !isPatternOutline_1(m.data) &&
                    !(currentGroupId_2 && m.data.groupId === currentGroupId_2);
            });
            // Show "Group" for ungrouped shapes, individual grouped shapes, and the group overlay
            if (!isPatternOutline_1(selectedItem.data) && hasOtherGroupablePS) {
                commonItems.push({
                    id: 'groupPatternShapeTo',
                    label: t('text.group'),
                    disabled: positionLock,
                });
            }
        }
        else if ((contextMenu.manipulativeId.startsWith('coloredTiles_') ||
            (isGroupMenu && type === 'coloredTiles')) &&
            !isHidden('group')) {
            // ColoredTiles: keyboard-friendly group picker (mirrors the Linking Cubes /
            // Pattern Shapes target-picker UX). "Group" opens an on-canvas picker; the
            // pick produces the SAME real manipulativeGroup_ that mouse multi-select +
            // the bottom-panel Group button does (both funnel through groupDiceFromMenu).
            // Per spec the picker lists EVERY other colored tile — random, outline, manual.
            var isCtGroupOverlay = isGroupMenu && type === 'coloredTiles';
            var ctGroupId_1 = isCtGroupOverlay
                ? contextMenu.manipulativeId
                : selectedItem.data.groupId;
            var isInRealGroup = !!ctGroupId_1;
            // Ungroup whenever the focused tile (or the overlay) is a real group.
            if (isInRealGroup) {
                commonItems.push({
                    id: isCtGroupOverlay ? 'ungroup' : 'ungroupColoredTiles',
                    label: t('text.ungroup'),
                });
            }
            // Self-group / self-ungroup toggle: turns the tile's cells into one
            // draggable unit via data.isGroup (same as the bottom-panel Group button).
            // Shown for any single groupable tile that is NOT already in a real
            // manipulativeGroup — independent of whether other tiles exist, so a user
            // can always "group the tile by itself".
            if (!isInRealGroup) {
                var ctData = (_56 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _56 !== void 0 ? _56 : {};
                var ctPartColorsCount = Object.keys((_57 = ctData.partColors) !== null && _57 !== void 0 ? _57 : {}).length;
                var ctIsGroupable = selectedTool.length === 1 &&
                    selectedTool[0].startsWith('coloredTiles_') &&
                    !(ctData.generationMode === 'manual' && ctPartColorsCount <= 1) &&
                    !(ctData.generationMode === 'outline' && ctPartColorsCount === 0);
                if (ctIsGroupable) {
                    var isMerged = ctData.isGroup === true;
                    commonItems.push({
                        id: isMerged ? 'ungroup' : 'group',
                        label: isMerged ? t('text.ungroup') : t('text.group'),
                    });
                }
            }
            // Count eligible group targets: other colored tiles (any mode, excluding the
            // invisible placeholder / preview / the focused tile's own group), other
            // colored-tile groups, and standalone linear pieces. Same set the picker
            // uses to build candidates.
            var hasEligibleTarget = manipulative.some(function (m) {
                var _a;
                return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles' &&
                    m.data.id !== contextMenu.manipulativeId &&
                    !m.data.isInvisible &&
                    !m.data.preview &&
                    !(ctGroupId_1 && m.data.groupId === ctGroupId_1);
            }) ||
                manipulative.some(function (m) {
                    var _a;
                    return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'linearPiece' &&
                        !m.data.groupId &&
                        !m.data.isInvisible &&
                        !m.data.preview;
                });
            // "Group with…" opens the on-canvas picker to merge with another tile/group
            // (the manipulativeGroup path) — separate from the self-group toggle above.
            if (hasEligibleTarget) {
                commonItems.push({
                    id: 'groupColoredTilesTo',
                    label: t('text.coloredTilesGroupWith'),
                    disabled: positionLock,
                });
            }
            // "Snap to…" merges this tile INTO another tile — the source's colours fill
            // the target's empty cells (row-major, growing the target downward on
            // overflow). Works for any source/target mode, so it's offered for a
            // standalone source whenever another standalone tile exists.
            if (!isInRealGroup) {
                var hasSnapTarget = manipulative.some(function (m) {
                    var _a;
                    if (((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) !== 'coloredTiles' || m.data.id === contextMenu.manipulativeId)
                        return false;
                    var td = m.data;
                    return !(td.isInvisible || td.preview || td.groupId);
                });
                if (hasSnapTarget) {
                    commonItems.push({
                        id: 'snapColoredTilesTo',
                        label: t('text.coloredTilesSnapToTile'),
                        disabled: positionLock,
                    });
                }
            }
        }
        else if (contextMenu.manipulativeId.startsWith('linearPiece_')) {
            // LinearPiece: keyboard-friendly snap to a ColoredTiles edge slot (mirrors
            // the mouse drag-snap) via an on-canvas picker, plus Unsnap to detach.
            var lp_1 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            if (lp_1) {
                var hasFreeSlot = manipulative.some(function (m) {
                    var _a;
                    return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coloredTiles' &&
                        !m.data.isInvisible &&
                        !m.data.preview &&
                        listFreeSnapSlots(m.data, lp_1.orientation).length > 0;
                });
                if (hasFreeSlot) {
                    commonItems.push({
                        id: 'snapLinearPieceTo',
                        label: t('text.coloredTilesSnapTo'),
                        disabled: positionLock,
                    });
                }
                var isSnapped = manipulative.some(function (m) {
                    var _a;
                    if (((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) !== 'coloredTiles')
                        return false;
                    var td = m.data;
                    return [
                        td.snappedVerticalPieceIds,
                        td.snappedRightVerticalPieceIds,
                        td.snappedHorizontalPieceIds,
                        td.snappedBottomHorizontalPieceIds,
                    ].some(function (arr) { return (arr !== null && arr !== void 0 ? arr : []).includes(lp_1.id); });
                });
                if (isSnapped) {
                    commonItems.push({ id: 'unsnapLinearPiece', label: t('text.coloredTilesUnsnap') });
                }
            }
        }
        else if (!isHidden('group')) {
            // NumberFrame token counters (shape = "shape0".."shape7") are not part
            // of the generic group/ungroup flow — the frame already owns them via
            // its tokens map, and merging them into a manipulativeGroup breaks that
            // wiring. Skip the item for those counters so the shape context menu
            // only shows frame-relevant actions. Every other manipulative (standalone
            // counter, coin, shape, etc.) still gets the group/ungroup item.
            var isNfCounter = dataType === 'numberFrameCounter' &&
                typeof selectedItem.data.shape === 'string' &&
                selectedItem.data.shape.startsWith('shape');
            // Number & Money Pieces use a keyboard-friendly target picker (mirrors
            // the pattern-shape Group flow) instead of the multi-select-driven
            // generic group action. Both individual NMP pieces and NMP groups
            // (manipulativeGroup_* whose first member is an NMP piece) route
            // through the same NMP-aware branch so an existing group can be
            // merged with another piece or group.
            var isNmpManipulative = contextMenu.manipulativeId.startsWith('numberPiece') ||
                contextMenu.manipulativeId.startsWith('moneyPiece');
            var isNmpGroup_1 = contextMenu.manipulativeId.startsWith('manipulativeGroup_') &&
                !!selectedItem &&
                (selectedItem.data.type === 'numberPiece' ||
                    selectedItem.data.type === 'moneyPiece');
            // Fraction Tiles follow the same keyboard-friendly target-picker flow
            // as NMP (see NumberAndMoneyPieceGroupPicker) rather than the generic
            // multi-select-driven group action.
            var isFtManipulative = contextMenu.manipulativeId.startsWith('fractionTiles_');
            var isFtGroup_1 = contextMenu.manipulativeId.startsWith('manipulativeGroup_') &&
                !!selectedItem &&
                selectedItem.data.type === 'fractionTiles';
            if (!isNfCounter && !isNmpManipulative && !isNmpGroup_1 && !isFtManipulative && !isFtGroup_1) {
                // Non-dice, non-NF-token, non-NMP: keep existing generic group/ungroup.
                //
                // ColoredTiles special case: a single coloredTiles_ tile can be
                // groupable / ungroupable on its own via `data.isGroup` (random
                // tiles, manual tiles with 2+ cells, or non-empty outline tiles).
                // The sidebar enables Group in all those states and marks it active
                // when `isGroup === true` — see GameController.tsx:1755-1791. The
                // 'group' / 'ungroup' bottomPanel handlers at GameController.tsx
                // :3242 and :3302 toggle `isGroup` on the tile (no manipulativeGroup_
                // is created for the single-tile case). Mirror sidebar's enabled
                // states + label here so the context menu agrees with the sidebar:
                //   • disabled only for 1-cell manual or empty outline
                //   • "Ungroup" when isGroup === true
                //   • "Group" (enabled) when isGroup === false and the tile has
                //     enough cells to make grouping meaningful
                var ctData = (_58 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _58 !== void 0 ? _58 : {};
                var ctPartColorsCount = Object.keys((_59 = ctData.partColors) !== null && _59 !== void 0 ? _59 : {}).length;
                var ctIsGroupable = selectedTool.length === 1 &&
                    selectedTool[0].startsWith('coloredTiles_') &&
                    // 1-cell manual: nothing to merge
                    !(ctData.generationMode === 'manual' && ctPartColorsCount <= 1) &&
                    // Empty outline: nothing yet to group
                    !(ctData.generationMode === 'outline' && ctPartColorsCount === 0);
                var ctIsMergedGroup = ctIsGroupable && ctData.isGroup === true;
                var isGroupSelected = ctIsMergedGroup ||
                    (selectedTool.length === 1 && selectedTool[0].startsWith('manipulativeGroup'));
                // Multi-select "merge these" only makes sense when every selected item
                // is the same manipulative type (e.g. all dice, all patternShapes).
                // Mixed selections (e.g. 1 die + 1 fraction tile) must not be groupable.
                var selectedTypes = new Set(selectedTool.map(function (id) { var _a; return (_a = manipulative.find(function (m) { return m.data.id === id; })) === null || _a === void 0 ? void 0 : _a.data.type; }));
                var isSameTypeMultiSelect = selectedTool.length >= 2 && selectedTypes.size === 1;
                commonItems.push({
                    id: isGroupSelected ? 'ungroup' : 'group',
                    label: isGroupSelected ? t('text.ungroup') : t('text.group'),
                    // Enable when:
                    //   • Already group-selected (label is Ungroup), OR
                    //   • 2+ items multi-selected AND all of the same type (the generic
                    //     "merge these" path — mixed-type selections stay disabled), OR
                    //   • Single ColoredTiles tile that's groupable per the rules above
                    disabled: !isGroupSelected && !isSameTypeMultiSelect && !ctIsGroupable,
                });
            }
            else if (isNmpManipulative || isNmpGroup_1) {
                // NMP: show "Ungroup" when the focused entity is part of a group
                // (either the focused piece's groupId is set OR the focused entity
                // IS the group), and "Group" when there's at least one other NMP
                // candidate (standalone or in a different group) on the canvas.
                // The picker handles target selection.
                var focusedNmp_1 = selectedItem.data;
                var focusedGroupId_1 = isNmpGroup_1
                    ? contextMenu.manipulativeId
                    : focusedNmp_1.groupId;
                if (focusedGroupId_1) {
                    commonItems.push({
                        id: 'ungroupNmp',
                        label: t('text.ungroup'),
                        disabled: false,
                    });
                }
                var hasOtherGroupableNmp = manipulative.some(function (m) {
                    var isNmp = m.data.type === 'numberPiece' || m.data.type === 'moneyPiece';
                    if (!isNmp)
                        return false;
                    var d = m.data;
                    // When focused on an individual piece, exclude it; when focused
                    // on the group, every group member is excluded via groupId.
                    if (!isNmpGroup_1 && d.id === focusedNmp_1.id)
                        return false;
                    if (focusedGroupId_1 && d.groupId === focusedGroupId_1)
                        return false;
                    return true;
                });
                if (hasOtherGroupableNmp) {
                    commonItems.push({
                        id: 'groupNmpTo',
                        label: t('text.group'),
                        disabled: positionLock,
                    });
                }
            }
            else if (isFtManipulative || isFtGroup_1) {
                // Fraction Tiles: show "Ungroup" when the focused entity is part of
                // a group (either the focused tile's groupId is set OR the focused
                // entity IS the group), and "Group" when there's at least one other
                // fraction-tile candidate (standalone or in a different group) on
                // the canvas. The picker handles target selection.
                var focusedFt_1 = selectedItem.data;
                var focusedGroupId_2 = isFtGroup_1 ? contextMenu.manipulativeId : focusedFt_1.groupId;
                if (focusedGroupId_2) {
                    commonItems.push({
                        id: 'ungroupFractionTiles',
                        label: t('text.ungroup'),
                        disabled: false,
                    });
                }
                var hasOtherGroupableFt = manipulative.some(function (m) {
                    if (m.data.type !== 'fractionTiles')
                        return false;
                    var d = m.data;
                    // When focused on an individual tile, exclude it; when focused
                    // on the group, every group member is excluded via groupId.
                    if (!isFtGroup_1 && d.id === focusedFt_1.id)
                        return false;
                    if (focusedGroupId_2 && d.groupId === focusedGroupId_2)
                        return false;
                    return true;
                });
                if (hasOtherGroupableFt) {
                    commonItems.push({
                        id: 'groupFractionTilesTo',
                        label: t('text.group'),
                        disabled: positionLock,
                    });
                }
            }
        }
        // "Link to Cube" — for LinkingCubes only. Keyboard users can't drag to snap,
        // so we offer a target-picker mode that places dummy focus buttons directly
        // over every eligible target cube. Only exposed when 2+ same-orientation
        // candidates exist outside the focused cube's chain.
        if (contextMenu.manipulativeId.startsWith('linkingCube')) {
            var focusedCube_1 = selectedItem.data; // linkingCubes
            var focusedOrientation_1 = focusedCube_1.orientation;
            var focusedChainId_1 = focusedCube_1.chainId;
            var candidateCount = manipulative
                .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.id && m.data.id !== focusedCube_1.id; })
                .map(function (m) { return m.data; })
                .filter(function (c) {
                return c.orientation === focusedOrientation_1 &&
                    (!focusedChainId_1 || c.chainId !== focusedChainId_1);
            }).length;
            if (candidateCount > 0) {
                commonItems.push({
                    id: 'linkToCube',
                    label: t('text.linkToCube'),
                    disabled: false,
                });
            }
            // Unlink actions — only when this cube is in a chain.
            //   • "Unlink All Cubes" dissolves the entire chain (legacy behavior).
            //   • "Unlink Cubes" (picker) opens an on-canvas dummy-button picker
            //     mirroring the link-picker UX. Selecting cube k breaks the chain
            //     at k — same semantics as a mouse drag-break of cube k. Only
            //     offered when the chain has at least one valid break point
            //     (chainCount >= 2 → there's at least one mid/tip cube).
            if (focusedChainId_1) {
                var chainMembers = manipulative.filter(function (m) {
                    return m.data.type === 'linkingCubes' &&
                        m.data.chainId === focusedChainId_1;
                });
                var chainSize = chainMembers.length;
                // Block unlink while any cube in the chain is positioning-locked.
                // Lock now applies to the chain as a whole (lockToggle case) so this
                // mirrors the mouse drag-break gate at LinkingCubes.tsx:1238-1242.
                var chainHasLock = chainMembers.some(function (m) { var _a; return (_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning; });
                // Only offer unlink actions when the cube is genuinely in a chain
                // (>= 2 members). A stale chainId left over from a half-completed
                // unlink would otherwise keep these options visible on what's now
                // a standalone cube.
                if (chainSize >= 2) {
                    commonItems.push({
                        id: 'unlinkCubeAt',
                        label: t('text.unlinkCube'),
                        disabled: chainHasLock,
                    });
                    commonItems.push({
                        id: 'unlinkCube',
                        label: t('text.unlinkAllCubes'),
                        disabled: chainHasLock,
                    });
                }
            }
        }
        // "Select similar pieces" — for NMP manipulatives only. Keyboard users
        // can't shift-click, so this launches a picker overlay that puts dummy
        // focus buttons over every peer of the same class (numberPiece, coin, or
        // money-piece bill). See NumberAndMoneyPiecePicker.tsx.
        if (contextMenu.manipulativeId.startsWith('numberPiece') ||
            contextMenu.manipulativeId.startsWith('moneyPiece')) {
            var sourceItem = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            var isCoinSrc_1 = (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.type) === 'moneyPiece' && (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.pieceType) === 'coins';
            var isMoneyBillSrc_1 = (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.type) === 'moneyPiece' && (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.pieceType) !== 'coins';
            var isNumberSrc_1 = (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.type) === 'numberPiece';
            var peerCount = manipulative.filter(function (m) {
                if (m.data.id === contextMenu.manipulativeId)
                    return false;
                if (m.data.groupId)
                    return false;
                if (isNumberSrc_1)
                    return m.data.type === 'numberPiece';
                if (isCoinSrc_1)
                    return m.data.type === 'moneyPiece' && m.data.pieceType === 'coins';
                if (isMoneyBillSrc_1)
                    return m.data.type === 'moneyPiece' && m.data.pieceType !== 'coins';
                return false;
            }).length;
            if (peerCount > 0) {
                commonItems.push({
                    id: 'selectSimilarNmp',
                    label: t('text.selectSimilarPieces'),
                    disabled: false,
                });
            }
        }
        // "Right Panel" — top-level item between Toolbox and Delete for manipulatives
        // that have a configuration drawer. Opens the drawer + moves focus into it.
        var RIGHT_DRAWER_TYPES = [
            'linkingCube',
            // fractionTile excluded: the right panel is not relevant for fraction tiles
            // in the context menu — all actions are handled inline.
            'numberLine',
            'patternShapes',
            // arrayModel, coloredTiles AND linearPiece excluded: all their controls are
            // shown inline in this context menu, and the right panel opens automatically
            // when the manipulative is selected — so the "Right Toolbar" entry is redundant.
            'numberPiece',
            'moneyPiece',
            'coinsAndCounters',
        ];
        var manipIdForDrawer = (_60 = contextMenu.manipulativeId) !== null && _60 !== void 0 ? _60 : '';
        var isPSGroupForDrawer = isGroupMenu && type === 'patternShapes';
        var hasRightDrawer = RIGHT_DRAWER_TYPES.some(function (p) { return manipIdForDrawer.startsWith(p); }) || isPSGroupForDrawer;
        if (hasRightDrawer) {
            commonItems.push({
                id: 'configurePanel',
                label: t('text.rightPanelAction'),
                disabled: false,
            });
        }
        if (!isHidden('delete')) {
            commonItems.push({
                id: 'delete',
                label: t('text.deleteAction'),
                disabled: isDisabled('delete') || positionLock || editingLock || isPresetItem,
            });
        }
        // Array Model: inline controls — grid mode only
        if (contextMenu.manipulativeId.startsWith('arrayModel_')) {
            var arrayData = (_61 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _61 !== void 0 ? _61 : {};
            var amMode = (_62 = arrayData.mode) !== null && _62 !== void 0 ? _62 : 'grid';
            var amPartMode = (_63 = arrayData.partModeEnabled) !== null && _63 !== void 0 ? _63 : true;
            var amSelectMode = (_64 = arrayData.selectModeEnabled) !== null && _64 !== void 0 ? _64 : false;
            if (amMode === 'grid') {
                var amRows = (_65 = arrayData.rows) !== null && _65 !== void 0 ? _65 : 1;
                var amCols = (_66 = arrayData.columns) !== null && _66 !== void 0 ? _66 : 1;
                var amShowExpr = (_67 = arrayData.showExpression) !== null && _67 !== void 0 ? _67 : true;
                var amShowProd = (_68 = arrayData.showProduct) !== null && _68 !== void 0 ? _68 : true;
                commonItems.push({
                    id: 'amAddRow',
                    label: t('text.arrayModelAddRow', { defaultValue: 'Add Row' }) + " (".concat(amRows, ")"),
                }, {
                    id: 'amAddColumn',
                    label: t('text.arrayModelAddColumn', { defaultValue: 'Add Column' }) + " (".concat(amCols, ")"),
                }, {
                    id: 'amToggleExpression',
                    label: t('text.showExpression', { defaultValue: 'Show Expression' }) +
                        ': ' +
                        (amShowExpr ? t('text.on', { defaultValue: 'On' }) : t('text.off', { defaultValue: 'Off' })),
                }, {
                    id: 'amToggleProduct',
                    label: t('text.showProduct', { defaultValue: 'Show Product' }) +
                        ': ' +
                        (amShowProd ? t('text.on', { defaultValue: 'On' }) : t('text.off', { defaultValue: 'Off' })),
                });
            }
            // Parts and Select Part shown for both grid and open mode
            commonItems.push({
                id: 'amPartMode',
                label: t('text.arrayModelCreateParts') +
                    ': ' +
                    (amPartMode ? t('text.on', { defaultValue: 'On' }) : t('text.off', { defaultValue: 'Off' })),
                disabled: editingLock,
            }, {
                id: 'amSelectMode',
                label: t('text.arrayModelSelectPart', { defaultValue: 'Select Parts' }) +
                    ': ' +
                    (amSelectMode ? t('text.on', { defaultValue: 'On' }) : t('text.off', { defaultValue: 'Off' })),
                disabled: editingLock,
            });
        }
        // Colored Tiles outline mode: keyboard-accessible resize. Mirrors the
        // ArrayModel `amAddRow` / `amAddColumn` pattern — each click does one
        // resize step anchored at the top-left (so the tile grows / shrinks at
        // the bottom-right corner, matching mouse-drag on the bottom-right
        // handle). Menu stays open for repeated clicks. Labels show the current
        // row / column count. Items disabled at the row=1 / col=1 lower bound
        // and when the tile is locked.
        // Resolve the colored tile this menu acts on — the tile itself, the colored
        // tile inside a selected group, or the tile a linear piece is snapped to —
        // so the CT-group and LinearPiece menus expose the SAME controls the (shared)
        // right panel does for those selections.
        var ctTarget = resolveColoredTilesTarget(contextMenu.manipulativeId, manipulative);
        if (ctTarget) {
            var ctData_1 = (_69 = ctTarget.data) !== null && _69 !== void 0 ? _69 : {};
            // True only when the menu was opened directly on the tile (not on a group
            // overlay or a linear piece) — gates the tile-only extras (outline resize,
            // Navigate Cell) further below.
            var ctIsDirectTile = contextMenu.manipulativeId === ctData_1.id;
            // Full ColoredTiles context menu — mirrors the ArrayModel "Array Model
            // Actions" menu so every right-panel control is reachable from the menu
            // (keyboard / screen-reader parity). The right panel stays as-is.
            var ctMode = (_70 = ctData_1.mode) !== null && _70 !== void 0 ? _70 : 'grid';
            var ctGenMode = (_71 = ctData_1.generationMode) !== null && _71 !== void 0 ? _71 : 'manual';
            var ctLocked = !!(((_72 = ctData_1.lock) === null || _72 === void 0 ? void 0 : _72.editing) || ((_73 = ctData_1.lock) === null || _73 === void 0 ? void 0 : _73.positioning));
            var ctRows = (_74 = ctData_1.rows) !== null && _74 !== void 0 ? _74 : 1;
            var ctCols = (_75 = ctData_1.columns) !== null && _75 !== void 0 ? _75 : 1;
            var ctShowExpr = (_76 = ctData_1.showExpression) !== null && _76 !== void 0 ? _76 : true;
            var ctShowProd = (_77 = ctData_1.showProduct) !== null && _77 !== void 0 ? _77 : true;
            var ctDimLabels = !!ctData_1.dimensionLabels;
            var ctPartMode = (_78 = ctData_1.partModeEnabled) !== null && _78 !== void 0 ? _78 : false;
            var ctSelectMode = (_79 = ctData_1.selectModeEnabled) !== null && _79 !== void 0 ? _79 : false;
            var ctOnOff = function (v) {
                return v ? t('text.on', { defaultValue: 'On' }) : t('text.off', { defaultValue: 'Off' });
            };
            // When the menu is opened on a LinearPiece, mirror the right panel: only
            // Add Tile and Linear Pieces are enabled there (Rows/Columns, Show
            // Expression/Product, Dimension Labels, Part/Select all gate on a
            // `coloredTiles_` selection, so they're disabled for a piece). So omit the
            // Rows & Columns and Options submenus for a linear-piece target.
            var ctTargetIsLinearPiece = contextMenu.manipulativeId.startsWith('linearPiece_');
            // When the tile is inside a multi-tile group (data.groupId set — i.e. the
            // menu was opened on a manipulativeGroup overlay), the right panel disables
            // Add/Remove Row & Column, Select Mode, and Add All Vertical/Horizontal
            // (BottomPanel.tsx ~1789) because those would tear the group's layout apart.
            // Mirror that here: show those items but disabled.
            var ctTargetInGroup = !!ctData_1.groupId;
            // Part Mode / Select Mode disabled conditions mirror the right panel
            // (BottomPanel.tsx ~1737-1832) — NOT editing-lock (the right panel only
            // lock-gates Delete). Part Mode needs a real, non-single, valid-rectangle
            // tile; Select Mode needs ≥1 coloured cell and is disabled for a group.
            var ctInvisible = !!ctData_1.isInvisible;
            var ctSingleCell = ((_80 = ctData_1.rows) !== null && _80 !== void 0 ? _80 : 1) * ((_81 = ctData_1.columns) !== null && _81 !== void 0 ? _81 : 1) <= 1;
            var ctInvalidRect = !isProperRectangle(ctData_1);
            var ctNoColoredCells = Object.keys((_82 = ctData_1.partColors) !== null && _82 !== void 0 ? _82 : {}).length === 0;
            // rectRequired controls (Part Mode, Add Row, Add Column) need a solid
            // rectangle for random tiles and manual non-group tiles (matches the right
            // panel's `rectRequiredControls`).
            var ctRectRequired = ctGenMode === 'random' || (ctGenMode === 'manual' && !ctData_1.isGroup);
            var ctPartModeDisabled = ctInvisible || ctSingleCell || (ctInvalidRect && ctRectRequired);
            // rectRequiredControls that are disabled on an invalid rectangle (mirrors
            // the right panel): Add Row, Add Column, and Dimension Labels. (Remove
            // Row/Column stay count-based; Show Expression/Product use the render-gate
            // condition below.)
            var ctRectRequiredDisabled = ctInvalidRect && ctRectRequired;
            var ctSelectModeDisabled = ctTargetInGroup || ctInvisible || ctNoColoredCells;
            // Show Expression / Show Product only RENDER for a proper rectangle (or
            // outline mode) — see the render gate in ColoredTiles.tsx — so disable the
            // toggles otherwise (mirrors the right panel). Without this, toggling them
            // "On" for an irregular (e.g. L-shaped) tile silently shows nothing.
            var ctExprProdDisabled = ctInvalidRect && ctGenMode !== 'outline';
            // 1a. Rows & Columns ▸ submenu — Add / Remove Row & Column. Shown for both
            // grid and outline mode tiles (widget handlers work for both since
            // data.mode defaults to 'grid'). Each child keeps the menu open with a
            // live "(N)" count (like amAddRow); submenu children re-render live from
            // menuItems so the count stays current.
            if (!ctTargetIsLinearPiece && ctMode === 'grid') {
                commonItems.push({
                    id: 'ctRowsColumns',
                    label: t('text.coloredTilesRowsColumns', { defaultValue: 'Rows & Columns' }),
                    // Row/column edits are disabled for a grouped tile (would break layout).
                    disabled: ctTargetInGroup,
                    submenu: [
                        {
                            id: 'ctGridAddRow',
                            label: t('text.addRow', { defaultValue: 'Add Row' }) + " (".concat(ctRows, ")"),
                            disabled: ctLocked || ctRectRequiredDisabled,
                        },
                        {
                            id: 'ctGridRemoveRow',
                            label: t('text.removeRow', { defaultValue: 'Remove Row' }) + " (".concat(ctRows, ")"),
                            disabled: ctLocked || ctRows <= 1,
                        },
                        {
                            id: 'ctGridAddColumn',
                            label: t('text.addColumn', { defaultValue: 'Add Column' }) + " (".concat(ctCols, ")"),
                            disabled: ctLocked || ctRectRequiredDisabled,
                        },
                        {
                            id: 'ctGridRemoveColumn',
                            label: t('text.removeColumn', { defaultValue: 'Remove Column' }) + " (".concat(ctCols, ")"),
                            disabled: ctLocked || ctCols <= 1,
                        },
                    ],
                });
            }
            // 1b. Options ▸ submenu — the toggles, grouped. "Label: On/Off" format
            // (ArrayModel convention). Show Expression / Show Product only apply in
            // grid mode (they label the grid product); Dimension Labels / Part Mode /
            // Select Mode apply in grid + open. Each child keeps the submenu OPEN and
            // updates its On/Off label live (submenu re-reads menuItems each render),
            // so the user can flip several without reopening.
            var ctOptionItems = [];
            if (ctMode === 'grid') {
                ctOptionItems.push({
                    id: 'ctToggleExpression',
                    label: t('text.showExpression', { defaultValue: 'Show Expression' }) +
                        ': ' +
                        ctOnOff(ctShowExpr),
                    // Only renders for a proper rectangle / outline (mirrors right panel).
                    disabled: ctExprProdDisabled,
                }, {
                    id: 'ctToggleProduct',
                    label: t('text.showProduct', { defaultValue: 'Show Product' }) + ': ' + ctOnOff(ctShowProd),
                    disabled: ctExprProdDisabled,
                });
            }
            ctOptionItems.push({
                id: 'ctDimensionLabels',
                label: t('text.dimensionLabels', { defaultValue: 'Dimension Labels' }) +
                    ': ' +
                    ctOnOff(ctDimLabels),
                // Disabled on an invalid rectangle (rectRequired) — mirrors the right panel.
                disabled: ctRectRequiredDisabled,
            }, {
                id: 'ctPartMode',
                label: t('text.coloredTilesPartModeMenu', { defaultValue: 'Part Mode' }) +
                    ': ' +
                    ctOnOff(ctPartMode),
                // Mirrors the right panel (NOT editing-lock): single-cell / invalid rect.
                disabled: ctPartModeDisabled,
            }, {
                id: 'ctSelectMode',
                label: t('text.coloredTilesSelectModeMenu', { defaultValue: 'Select Mode' }) +
                    ': ' +
                    ctOnOff(ctSelectMode),
                // Mirrors the right panel: disabled for a group / no coloured cells.
                disabled: ctSelectModeDisabled,
            });
            if (!ctTargetIsLinearPiece) {
                commonItems.push({
                    id: 'ctOptions',
                    label: t('text.coloredTilesOptions', { defaultValue: 'Options' }),
                    submenu: ctOptionItems,
                });
            }
            // 1c. Add Tile ▸ submenu — creates a free-standing 1×1 tile of the chosen
            // colour (red / blue / yellow / green), same colours as the blocks palette.
            commonItems.push({
                id: 'ctAddTile',
                label: t('text.coloredTilesAddTile', { defaultValue: 'Add Tile' }),
                // "Add Tile" creates a new free-standing tile (the blocks palette is a
                // creation tool) — never disabled, matching the right panel.
                submenu: [
                    {
                        id: 'ctAddTile_red',
                        label: t('text.coloredTilesColorRed', { defaultValue: 'Red' }),
                    },
                    {
                        id: 'ctAddTile_blue',
                        label: t('text.coloredTilesColorBlue', { defaultValue: 'Blue' }),
                    },
                    {
                        id: 'ctAddTile_yellow',
                        label: t('text.coloredTilesColorYellow', { defaultValue: 'Yellow' }),
                    },
                    {
                        id: 'ctAddTile_green',
                        label: t('text.coloredTilesColorGreen', { defaultValue: 'Green' }),
                    },
                ],
            });
            // 1d. Linear Pieces ▸ submenu — the four right-panel actions: single-add
            // and add-all for both edges.
            // Single-add Vertical/Horizontal pieces are creation tools — never disabled
            // (matches the right panel, where verticalPieces/horizontalPieces are never
            // in a disable list). Add All is disabled for a grouped tile OR a linear
            // piece selection (the right panel's groupId list / linear-piece
            // tileOnlyButtons both include addAllVertical/addAllHorizontal).
            var ctAddAllDisabled = ctTargetInGroup || ctTargetIsLinearPiece;
            commonItems.push({
                id: 'ctLinearPieces',
                label: t('text.coloredTilesLinearPieces', { defaultValue: 'Linear Pieces' }),
                submenu: [
                    {
                        id: 'ctVerticalPieces',
                        label: t('text.coloredTilesVerticalPieces', { defaultValue: 'Vertical Pieces' }),
                    },
                    {
                        id: 'ctAddAllVertical',
                        label: t('text.coloredTilesVerticalPieces', { defaultValue: 'Vertical Pieces' }) +
                            ' — ' +
                            t('text.coloredTilesAddAll', { defaultValue: 'Add All' }),
                        disabled: ctAddAllDisabled,
                    },
                    {
                        id: 'ctHorizontalPieces',
                        label: t('text.coloredTilesHorizontalPieces', { defaultValue: 'Horizontal Pieces' }),
                    },
                    {
                        id: 'ctAddAllHorizontal',
                        label: t('text.coloredTilesHorizontalPieces', { defaultValue: 'Horizontal Pieces' }) +
                            ' — ' +
                            t('text.coloredTilesAddAll', { defaultValue: 'Add All' }),
                        disabled: ctAddAllDisabled,
                    },
                ],
            });
            // "Navigate Cell" — keyboard entry to the cell picker. Shown only where a
            // cell can actually be extracted, mirroring handleCellDragStart's guard:
            // hidden when `data.isGroup === true` (random tiles AND grouped tiles) or
            // when in a manipulativeGroup (`data.groupId`), and hidden when there's
            // ≤1 visible cell. Disabled by editing-lock since unsnap mutates the tile.
            var ctCellsExtractable = ctData_1.isGroup !== true && !ctData_1.groupId;
            var ctVisibleCellCount = (function () {
                var _a, _b, _d;
                var rows = (_a = ctData_1.rows) !== null && _a !== void 0 ? _a : 0;
                var cols = (_b = ctData_1.columns) !== null && _b !== void 0 ? _b : 0;
                var pc = (_d = ctData_1.partColors) !== null && _d !== void 0 ? _d : {};
                var n = 0;
                for (var r = 0; r < rows; r++) {
                    for (var c = 0; c < cols; c++) {
                        var explicit = pc["".concat(r, "-").concat(c)];
                        // random: unset cells fall back to a default color → visible.
                        // manual/outline: only explicitly-filled cells are visible.
                        if (ctData_1.generationMode === 'random' ? explicit !== 'transparent' : !!explicit) {
                            n++;
                        }
                    }
                }
                return n;
            })();
            if (ctIsDirectTile && ctCellsExtractable && ctVisibleCellCount > 1) {
                commonItems.push({
                    id: 'navigateCell',
                    label: t('text.coloredTilesNavigateCell'),
                    disabled: !!((_83 = ctData_1.lock) === null || _83 === void 0 ? void 0 : _83.editing),
                });
            }
        }
        // PS outline shapes only show the right panel entry — no other actions apply.
        var isPSOutline = contextMenu.manipulativeId.startsWith('patternShapes_') &&
            (selectedItem.data.shapeType === 'outline' ||
                !!selectedItem.data.isOutline ||
                !!selectedItem.data.isStaticOutline);
        if (isPSOutline) {
            var rightPanelItem = commonItems.find(function (item) { return item.id === 'configurePanel'; });
            return __spreadArray(__spreadArray([], (rightPanelItem ? [rightPanelItem] : []), true), [
                { id: 'exit', label: t('text.exitMenu'), isBack: true },
            ], false);
        }
        var finalItems = __spreadArray(__spreadArray(__spreadArray([], typeActions, true), commonItems, true), [
            {
                id: 'exit',
                label: t('text.exitMenu'),
                isBack: true,
            },
        ], false);
        // For ColoredTiles / LinearPiece menus, disable any submenu parent whose
        // children are all disabled so keyboard / screen-reader users can't drill
        // into a dead submenu (e.g. "Rows & Columns" or "Options" when every action
        // inside is locked / unavailable).
        return ctTarget ? disableEmptySubmenuParents(finalItems) : finalItems;
    }, [
        selectedItem,
        contextMenu,
        positionLock,
        editingLock,
        selectedTool,
        t,
        lockDisabledButtons,
        checkedDiceIds,
        checkedPatternIds,
        manipulative,
        isShadeMenu,
        selectedShade,
        isCardMenu,
        isDeckMenu,
        isAnnotationMenu,
        selectedAnnotation,
        choralSpeedLocal,
        choralDirLocal,
        choralIsPlaying,
        color,
    ]);
    var rawMenuItems = buildMenuItems();
    // Global view-mode filter: hide delete / edit / lock and their variants from
    // every manipulative's context menu when the user is in view mode.
    var VIEW_MODE_HIDDEN_IDS = ['edit', 'lock', 'lockPosition', 'lockEditing'];
    var menuItems = isViewMode
        ? rawMenuItems
            .filter(function (item) { return !VIEW_MODE_HIDDEN_IDS.includes(item.id); })
            .map(function (item) {
            return item.submenu
                ? __assign(__assign({}, item), { submenu: item.submenu.filter(function (sub) { return !VIEW_MODE_HIDDEN_IDS.includes(sub.id); }) }) : item;
        })
        : rawMenuItems;
    // Close the menu
    var closeMenu = useCallback(function (focusIdOverride) {
        var manipId = focusIdOverride !== null && focusIdOverride !== void 0 ? focusIdOverride : contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId;
        if (choralActiveRef.current) {
            customEventHandler.dispatchEvent('choralDrawer', { id: 'choralReset' });
            choralActiveRef.current = false;
            setChoralSpeedLocal(null);
            setChoralDirLocal(null);
        }
        highlightDice(null);
        setContextMenu(null);
        setSubmenuOpen(false);
        setActiveIndex(-1);
        setSubmenuIndex(0);
        setTimeout(function () {
            // First try to find the element by data-manipulative-id (survives SVG redraws)
            if (manipId) {
                var wrapper_1 = document.querySelector("[data-manipulative-id=\"".concat(manipId, "\"]"));
                if (wrapper_1) {
                    // Focus the wrapper if it's focusable, otherwise find the first focusable child
                    var target = wrapper_1.tabIndex >= 0
                        ? wrapper_1
                        : wrapper_1.querySelector('[tabindex="0"], button, [role="button"]');
                    (target !== null && target !== void 0 ? target : wrapper_1).focus();
                    // CustomTick: the kebab button only renders after the wrapper receives
                    // focus (which sets isKeyboardFocused → true). Do a second pass one
                    // frame later so the kebab has been committed to the DOM before we
                    // move focus onto it.
                    if (manipId === null || manipId === void 0 ? void 0 : manipId.startsWith('customTick_')) {
                        requestAnimationFrame(function () {
                            var kebab = wrapper_1.querySelector('.numberLineKebabBtn');
                            if (kebab)
                                kebab.focus();
                        });
                    }
                    return;
                }
                // Table cell context menus use a synthetic id "tableText_N_cell_R_C" that has no
                // matching DOM element. Fall back to the table's sentinel button "tableText_N".
                if (manipId.includes('_cell_')) {
                    var tableId = manipId.split('_cell_')[0];
                    var sentinel = document.querySelector("[data-manipulative-id=\"".concat(tableId, "\"]"));
                    if (sentinel) {
                        sentinel.focus();
                        return;
                    }
                }
            }
            // Fallback to the original trigger element
            var el = triggerRef.current;
            if ((el === null || el === void 0 ? void 0 : el.focus) && document.body.contains(el)) {
                el.focus();
            }
        }, 50);
    }, [setContextMenu, contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId]);
    // Force a second render after the DOM commits when the underlying
    // manipulative changes while the menu is open. The menu's x/y position is
    // computed in render via `document.querySelector(...).getBoundingClientRect()`
    // — but during a render triggered by a `manipulative` change, React hasn't
    // committed the new DOM yet, so the first render sees stale wrapper bounds
    // (e.g. the tile's old width before Add Column added a cell). useLayoutEffect
    // runs after commit; bumping this counter schedules an extra synchronous
    // render where document.querySelector returns the fresh post-commit bounds
    // so the menu repositions to track the tile.
    var _55 = useState(0), bumpPosition = _55[1];
    useLayoutEffect(function () {
        if (!(contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId))
            return;
        bumpPosition(function (v) { return v + 1; });
    }, [manipulative, table, contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId]);
    // Reset choral counting when leaving submenu
    useEffect(function () {
        if (!submenuOpen && choralActiveRef.current) {
            customEventHandler.dispatchEvent('choralDrawer', { id: 'choralReset' });
            choralActiveRef.current = false;
            setChoralSpeedLocal(null);
            setChoralDirLocal(null);
        }
        // Reset play state when submenu closes so Play is enabled on reopen
        if (!submenuOpen) {
            setChoralIsPlaying(false);
        }
    }, [submenuOpen]);
    // Capture trigger on open — focus the header/title so screen reader announces it
    useEffect(function () {
        var _a;
        if (contextMenu) {
            triggerRef.current = document.activeElement;
            setActiveIndex(IS_TOUCH_DEVICE ? 0 : -1);
            setSubmenuOpen(false);
            setSubmenuIndex(0);
            // Pre-check all currently selected fraction tiles in the "Combine Tiles"
            // list so lasso-selected tiles appear pre-checked when the menu opens.
            // Always include the tile that was tapped to open the menu.
            var ctxIsFractionTile = (_a = contextMenu.manipulativeId) === null || _a === void 0 ? void 0 : _a.startsWith('fractionTiles_');
            if (ctxIsFractionTile) {
                var lassoSelected = selectedTool.filter(function (id) { return id.startsWith('fractionTiles_'); });
                var initial = new Set(lassoSelected);
                initial.add(contextMenu.manipulativeId);
                setCheckedDiceIds(initial);
            }
            else {
                setCheckedDiceIds(new Set());
            }
            setCheckedPatternIds(new Set());
            // ── iPad / VoiceOver: trap navigation inside the menu ──────────────
            // aria-modal is unreliable on iOS VoiceOver. The only certain way to
            // stop VO from reaching elements outside the menu is the `inert`
            // attribute, the same technique used for the JAWS custom-content path.
            //
            // IMPORTANT: store the rAF id so the cleanup can cancel it if the menu
            // closes before the frame fires.  Without cancelAnimationFrame the rAF
            // would fire after cleanup, permanently inert-ing the page and freezing
            // the app (required hard-refresh).
            var inertedElements_1 = [];
            var inertRafId_1;
            if (IS_TOUCH_DEVICE) {
                inertRafId_1 = requestAnimationFrame(function () {
                    var _a;
                    inertRafId_1 = undefined;
                    var menuOverlay = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu-overlay');
                    Array.from(document.body.children).forEach(function (child) {
                        var el = child;
                        if (el === menuOverlay ||
                            el.contains(menuOverlay !== null && menuOverlay !== void 0 ? menuOverlay : null) ||
                            el.tagName === 'SCRIPT' ||
                            el.tagName === 'STYLE' ||
                            el.tagName === 'LINK')
                            return;
                        if (!el.hasAttribute('inert')) {
                            el.setAttribute('inert', '');
                            el.setAttribute('aria-hidden', 'true');
                            inertedElements_1.push(el);
                        }
                    });
                });
            }
            var delay = IS_TOUCH_DEVICE ? 100 : 50;
            var timerId_1 = setTimeout(function () {
                var _a, _b;
                if (IS_TOUCH_DEVICE) {
                    var firstItem = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button[role="menuitem"]');
                    firstItem === null || firstItem === void 0 ? void 0 : firstItem.focus();
                }
                else {
                    var header = (_b = menuRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('.shape-context-menu__header');
                    header === null || header === void 0 ? void 0 : header.focus();
                }
            }, delay);
            return function () {
                clearTimeout(timerId_1);
                // Cancel the rAF if it hasn't fired yet — prevents the race condition
                // where cleanup runs with an empty inertedElements[], then the rAF fires
                // and permanently inerts the page.
                if (inertRafId_1 !== undefined) {
                    cancelAnimationFrame(inertRafId_1);
                }
                inertedElements_1.forEach(function (el) {
                    el.removeAttribute('inert');
                    el.removeAttribute('aria-hidden');
                });
            };
        }
    }, [contextMenu]);
    // Close on selection change (but not when the menu was just opened in this render)
    var prevSelectedLengthRef = useRef(selectedTool.length);
    var wasMenuOpenRef = useRef(false);
    useEffect(function () {
        if (prevSelectedLengthRef.current !== selectedTool.length &&
            contextMenu &&
            wasMenuOpenRef.current) {
            setContextMenu(null);
        }
        prevSelectedLengthRef.current = selectedTool.length;
        wasMenuOpenRef.current = !!contextMenu;
    }, [selectedTool.length, contextMenu]);
    // Close on click outside (mousedown for desktop, touchstart for touch devices)
    useEffect(function () {
        if (!contextMenu)
            return;
        var handleOutsideInteraction = function (e) {
            var _a;
            var target = (e instanceof TouchEvent ? (_a = e.touches[0]) === null || _a === void 0 ? void 0 : _a.target : e.target);
            if (target && !target.closest('.shape-context-menu')) {
                closeMenu();
            }
        };
        document.addEventListener('mousedown', handleOutsideInteraction);
        document.addEventListener('touchstart', handleOutsideInteraction);
        return function () {
            document.removeEventListener('mousedown', handleOutsideInteraction);
            document.removeEventListener('touchstart', handleOutsideInteraction);
        };
    }, [contextMenu, closeMenu]);
    // Execute action
    var executeAction = useCallback(function (actionId) {
        var _a, _b, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79, _80, _81, _82, _83, _84, _85;
        console.log('[executeAction] called', { actionId: actionId, manipulativeId: contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId, selectedItem: !!selectedItem, isTableTextMenu: isTableTextMenu, isTableCellMenu: isTableCellMenu });
        if (!contextMenu)
            return;
        // ColoredTiles cell-menu has a synthetic manipulativeId (foo_cell_R_C)
        // that has no entry in `manipulative`, so selectedItem is null. Allow
        // the cell-menu's actions to run regardless of selectedItem.
        var isCellMenu = /^coloredTiles_[^_]+_cell_\d+_\d+$/.test(contextMenu.manipulativeId);
        if (!selectedItem &&
            !isShadeMenu &&
            !isAnnotationMenu &&
            !isTextMenu &&
            !isBeadMenu &&
            !isCellMenu &&
            !isTableTextMenu &&
            !isTableCellMenu &&
            !isCanvasPanMenu) {
            console.log('[executeAction] EARLY RETURN — no selectedItem and not a special menu type', { isShadeMenu: isShadeMenu, isAnnotationMenu: isAnnotationMenu, isTextMenu: isTextMenu, isBeadMenu: isBeadMenu, isCellMenu: isCellMenu, isTableTextMenu: isTableTextMenu, isTableCellMenu: isTableCellMenu });
            return;
        }
        var id = contextMenu.manipulativeId;
        // Colored tile this menu acts on (the tile itself, a tile inside a selected
        // group, or the tile a linear piece is snapped to). The ct* action cases
        // target THIS tile, not necessarily the menu's selected id.
        var ctTargetItem = resolveColoredTilesTarget(id, manipulative);
        var ctTargetData = (_a = ctTargetItem === null || ctTargetItem === void 0 ? void 0 : ctTargetItem.data) !== null && _a !== void 0 ? _a : null;
        var ctTargetId = (_b = ctTargetData === null || ctTargetData === void 0 ? void 0 : ctTargetData.id) !== null && _b !== void 0 ? _b : id;
        // Ensure the target tile passes its widget's isFocusedRef gate before we
        // dispatch a bottomPanel event to it: a selected group already focuses its
        // member tiles (via groupId), but a selected linear piece or a different
        // selection does not — select the tile in that case.
        var ensureCtTileFocused = function () {
            if (!ctTargetData)
                return;
            var focused = selectedTool.includes(ctTargetData.id) ||
                (!!ctTargetData.groupId && selectedTool.includes(ctTargetData.groupId));
            if (!focused)
                setSelectedTool([ctTargetData.id]);
        };
        // ColoredTiles "Navigate Cell" — close the tile-level menu and open the
        // cell picker (a dialog of focusable buttons, one per visible cell). We
        // clear the menu state directly instead of calling closeMenu() to skip
        // its 50ms focus-restore to the controller button, so the picker's own
        // focus (60ms) wins. Real <button>s in a dialog are what VoiceOver can
        // navigate — in-place grid/roving approaches did not work.
        if (actionId === 'navigateCell') {
            var items = buildMenuItems();
            var item = items.find(function (m) { return m.id === actionId; });
            if (item === null || item === void 0 ? void 0 : item.disabled)
                return;
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            customEventHandler.dispatchEvent('openColoredTilesCellPicker', { manipulativeId: id });
            return;
        }
        // ColoredTiles cell-menu Unsnap — extract the cell at (R,C) as a
        // standalone 1×1 tile. The tile listens for `coloredTilesUnsnapCell`,
        // mutates its source data, and dispatches createColoredTile with
        // openContextMenuOnCreate so the new tile's context menu opens.
        if (actionId === 'unsnapCell' && isCellMenu) {
            var m = id.match(/^(coloredTiles_[^_]+)_cell_(\d+)_(\d+)$/);
            if (!m)
                return;
            var tileId_1 = m[1], rStr_1 = m[2], cStr_1 = m[3];
            closeMenu();
            // Delay slightly so closeMenu's focus restore finishes before we
            // mutate the manipulative array (which would otherwise re-render
            // mid-focus-restore).
            setTimeout(function () {
                customEventHandler.dispatchEvent('coloredTilesUnsnapCell', {
                    manipulativeId: tileId_1,
                    row: Number(rStr_1),
                    col: Number(cStr_1),
                });
            }, 80);
            return;
        }
        // Colored Tiles outline-mode resize: dispatch a single resize step
        // anchored at the top-left corner (so the bottom-right grows / shrinks,
        // matching mouse drag on the bottom-right handle). Menu stays open so
        // the user can click repeatedly — same pattern as bead Shift Left /
        // Shift Right and ArrayModel's amAddRow / amAddColumn.
        var ctResizeMap = {
            ctAddRow: { dRows: 1, dCols: 0 },
            ctRemoveRow: { dRows: -1, dCols: 0 },
            ctAddColumn: { dRows: 0, dCols: 1 },
            ctRemoveColumn: { dRows: 0, dCols: -1 },
        };
        if (actionId in ctResizeMap) {
            var items = buildMenuItems();
            var item = items.find(function (m) { return m.id === actionId; });
            if (item === null || item === void 0 ? void 0 : item.disabled)
                return; // honour visually-disabled state
            var _86 = ctResizeMap[actionId], dRows = _86.dRows, dCols = _86.dCols;
            customEventHandler.dispatchEvent('coloredTilesResize', {
                manipulativeId: id,
                dRows: dRows,
                dCols: dCols,
            });
            return;
        }
        // Handle addTile_<fractionType> pattern
        if (actionId.startsWith('addTile_')) {
            var fractionType_1 = actionId.replace('addTile_', '');
            var canvasEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
            var r = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.getBoundingClientRect();
            var dropX_1 = r ? r.left + r.width / 2 : 400;
            var dropY_1 = r ? r.top + r.height / 2 : 300;
            // Close menu without restoring focus to the original tile so the
            // new tile's auto-focus can take over cleanly (mirrors delete pattern).
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            // Dispatch after menu state clears so new tile receives focus uncontested.
            // Announcement fires inside the same setTimeout so it executes after the menu
            // unmounts — while the menu is still in the DOM its aria-modal="true" blocks
            // document.body aria-live regions from being read by VoiceOver / JAWS.
            setTimeout(function () {
                announceAssertive(t('text.fractionTileAdded', { defaultValue: 'Fraction tile added' }));
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'createFractionTile',
                    type: 'fractionBtn',
                    data: { fractionType: fractionType_1, dropX: dropX_1, dropY: dropY_1 },
                });
            }, 0);
        }
        // ── Full ColoredTiles context-menu actions (mirror of the ArrayModel
        // cases). The ColoredTiles widget's bottomPanel listener is gated by
        // `isFocusedRef.current` (driven by selectedTool), so each case selects
        // the tile first, then dispatches on a deferred tick once the selection
        // has committed and the widget's focus effect has run. ──
        // Keep-open: grid Add / Remove Row & Column. Live "(N)" count updates and
        // an sr-only announcement via the shared amAnnouncement region. Menu stays
        // open (same as ArrayModel amAddRow / amAddColumn).
        var ctGridResizeMap = {
            ctGridAddRow: 'addRow',
            ctGridRemoveRow: 'removeRow',
            ctGridAddColumn: 'addColumn',
            ctGridRemoveColumn: 'removeColumn',
        };
        if (actionId in ctGridResizeMap) {
            // ctGrid* items live inside the "Rows & Columns" submenu, so search
            // submenu children too when honouring the visually-disabled state.
            var items = buildMenuItems();
            var findDeep = function (its) {
                var _a;
                for (var _b = 0, its_1 = its; _b < its_1.length; _b++) {
                    var m = its_1[_b];
                    if (m.id === actionId)
                        return m;
                    var child = (_a = m.submenu) === null || _a === void 0 ? void 0 : _a.find(function (s) { return s.id === actionId; });
                    if (child)
                        return child;
                }
                return undefined;
            };
            if ((_d = findDeep(items)) === null || _d === void 0 ? void 0 : _d.disabled)
                return; // honour disabled state
            ensureCtTileFocused();
            var d = (_e = ctTargetData !== null && ctTargetData !== void 0 ? ctTargetData : selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _e !== void 0 ? _e : {};
            var rows = (_f = d.rows) !== null && _f !== void 0 ? _f : 1;
            var cols = (_g = d.columns) !== null && _g !== void 0 ? _g : 1;
            var nextCount = {
                ctGridAddRow: { r: rows + 1, c: cols },
                ctGridRemoveRow: { r: rows - 1, c: cols },
                ctGridAddColumn: { r: rows, c: cols + 1 },
                ctGridRemoveColumn: { r: rows, c: cols - 1 },
            };
            var _87 = nextCount[actionId], nr = _87.r, nc = _87.c;
            var msg_1 = t('text.coloredTilesGridResized', { rows: nr, columns: nc });
            setAmAnnouncement('');
            requestAnimationFrame(function () {
                customEventHandler.dispatchEvent('bottomPanel', { id: ctGridResizeMap[actionId] });
                setAmAnnouncement(msg_1);
            });
            return;
        }
        // Keep-open: Options submenu toggles (Show Expression / Product, Dimension
        // Labels, Part / Select Mode). Each flip keeps the submenu open and the
        // On/Off label refreshes live (submenu re-reads menuItems on re-render), so
        // users can change several at once. Announced via the shared amAnnouncement
        // region (the widget's own announce is blocked while the menu's aria-modal
        // is mounted). Mirrors the Rows & Columns keep-open pattern.
        var ctToggleMap = {
            ctToggleExpression: 'toggleExpression',
            ctToggleProduct: 'toggleProduct',
            ctDimensionLabels: 'dimensionLabels',
            ctPartMode: 'partMode',
            ctSelectMode: 'selectMode',
        };
        if (actionId in ctToggleMap) {
            var items = buildMenuItems();
            var findDeep = function (its) {
                var _a;
                for (var _b = 0, its_2 = its; _b < its_2.length; _b++) {
                    var m = its_2[_b];
                    if (m.id === actionId)
                        return m;
                    var child = (_a = m.submenu) === null || _a === void 0 ? void 0 : _a.find(function (s) { return s.id === actionId; });
                    if (child)
                        return child;
                }
                return undefined;
            };
            if ((_h = findDeep(items)) === null || _h === void 0 ? void 0 : _h.disabled)
                return; // honour disabled state (editing lock)
            ensureCtTileFocused();
            var d = (_j = ctTargetData !== null && ctTargetData !== void 0 ? ctTargetData : selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _j !== void 0 ? _j : {};
            var curState = {
                ctToggleExpression: (_k = d.showExpression) !== null && _k !== void 0 ? _k : true,
                ctToggleProduct: (_l = d.showProduct) !== null && _l !== void 0 ? _l : true,
                ctDimensionLabels: !!d.dimensionLabels,
                ctPartMode: (_m = d.partModeEnabled) !== null && _m !== void 0 ? _m : false,
                ctSelectMode: (_o = d.selectModeEnabled) !== null && _o !== void 0 ? _o : false,
            };
            var labelKey = {
                ctToggleExpression: 'text.showExpression',
                ctToggleProduct: 'text.showProduct',
                ctDimensionLabels: 'text.dimensionLabels',
                ctPartMode: 'text.coloredTilesPartModeMenu',
                ctSelectMode: 'text.coloredTilesSelectModeMenu',
            };
            var newState = !curState[actionId];
            var msg_2 = t(labelKey[actionId]) + ': ' + (newState ? t('text.on') : t('text.off'));
            // When turning a value display ON, also read the value itself (the
            // expression / product / dimension labels), not just "On".
            if (newState) {
                var valueAria = actionId === 'ctToggleExpression'
                    ? buildColoredTilesExpressionAria(d, t)
                    : actionId === 'ctToggleProduct'
                        ? buildColoredTilesProductAria(d, t)
                        : actionId === 'ctDimensionLabels'
                            ? buildColoredTilesDimensionLabelsAria(d, t)
                            : '';
                if (valueAria)
                    msg_2 = "".concat(msg_2, ". ").concat(valueAria);
            }
            setAmAnnouncement('');
            // When enabling Create Parts or Select Part, close without the focus-restore
            // setTimeout in closeMenu() — the CT widget's useEffect handles focus instead.
            if (newState && (actionId === 'ctPartMode' || actionId === 'ctSelectMode')) {
                // Pre-focus the CT widget BEFORE closing so iOS VoiceOver's inert-removal
                // focus restoration targets the CT wrapper (not the right tool panel).
                // The CT useEffect then moves focus to the cut arrow / first cell.
                if (ctTargetData === null || ctTargetData === void 0 ? void 0 : ctTargetData.id) {
                    var ctWrapper = document.querySelector("[data-manipulative-id=\"".concat(ctTargetData.id, "\"]"));
                    ctWrapper === null || ctWrapper === void 0 ? void 0 : ctWrapper.focus();
                }
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
            }
            requestAnimationFrame(function () {
                customEventHandler.dispatchEvent('bottomPanel', { id: ctToggleMap[actionId] });
                setAmAnnouncement(msg_2);
            });
            return;
        }
        // Single-add Linear Pieces (Vertical Pieces / Horizontal Pieces) — use the
        // SAME shared helper the right tool panel uses, so both place one piece in
        // the first free edge slot (left→right / top→bottom), then ring-place when
        // edges are full. The helper resolves the tile from the passed selection
        // and dispatches `createLinearPiece` (controller snaps + adopts scale).
        var ctSingleLinearMap = {
            ctVerticalPieces: 'verticalPieces',
            ctHorizontalPieces: 'horizontalPieces',
        };
        if (actionId in ctSingleLinearMap) {
            var items = buildMenuItems();
            if ((_p = items.find(function (m) { return m.id === actionId; })) === null || _p === void 0 ? void 0 : _p.disabled)
                return; // honour disabled state
            var ctManipId_1 = id;
            var toolId_1 = ctSingleLinearMap[actionId];
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            setTimeout(function () {
                // Pass the resolved target tile as the selection so the helper acts on
                // THIS tile regardless of the async selectedTool state (works whether
                // the menu was opened on the tile, its group, or a linear piece).
                createLinearPieceForSelectedTile(toolId_1, manipulative, [ctTargetId]);
                announceAssertive(t('text.coloredTilesLinearPieceAdded', { defaultValue: 'Linear piece added' }));
                var wrapper = document.querySelector("[data-manipulative-id=\"".concat(ctManipId_1, "\"]"));
                if (wrapper) {
                    var target = wrapper.tabIndex >= 0
                        ? wrapper
                        : wrapper.querySelector('[tabindex="0"], button, [role="button"]');
                    (target !== null && target !== void 0 ? target : wrapper).focus();
                }
            }, 0);
            return;
        }
        // Close + restore focus to the tile wrapper (ArrayModel close pattern):
        // the two add-all linear-piece actions. They announce here.
        var ctCloseActionMap = {
            ctAddAllVertical: 'addAllVertical',
            ctAddAllHorizontal: 'addAllHorizontal',
        };
        if (actionId in ctCloseActionMap) {
            var items = buildMenuItems();
            var findDeep = function (its) {
                var _a;
                for (var _b = 0, its_3 = its; _b < its_3.length; _b++) {
                    var m = its_3[_b];
                    if (m.id === actionId)
                        return m;
                    var child = (_a = m.submenu) === null || _a === void 0 ? void 0 : _a.find(function (s) { return s.id === actionId; });
                    if (child)
                        return child;
                }
                return undefined;
            };
            if ((_q = findDeep(items)) === null || _q === void 0 ? void 0 : _q.disabled)
                return; // honour disabled state
            var ctManipId_2 = id;
            // addAll* go through the tile's focus-gated widget listener, so focus the
            // resolved target tile (group already focuses members; linear piece does not).
            ensureCtTileFocused();
            var bottomId_1 = ctCloseActionMap[actionId];
            var isLinear_1 = actionId === 'ctAddAllVertical' || actionId === 'ctAddAllHorizontal';
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            setTimeout(function () {
                customEventHandler.dispatchEvent('bottomPanel', { id: bottomId_1 });
                if (isLinear_1) {
                    announceAssertive(t('text.coloredTilesLinearPieceAdded', { defaultValue: 'Linear piece added' }));
                }
                var wrapper = document.querySelector("[data-manipulative-id=\"".concat(ctManipId_2, "\"]"));
                if (wrapper) {
                    var target = wrapper.tabIndex >= 0
                        ? wrapper
                        : wrapper.querySelector('[tabindex="0"], button, [role="button"]');
                    (target !== null && target !== void 0 ? target : wrapper).focus();
                }
            }, 0);
            return;
        }
        // Add Tile ▸ colour — create a free-standing 1×1 tile of the chosen colour
        // (same colours as the blocks palette). `createColoredTile` reads x/y as a
        // CANVAS-space TOP-LEFT (the same coordinate space as `tile.center`), NOT
        // viewport pixels. Place the new tile just to the RIGHT of the selected
        // tile, with a gap larger than the 40px snap-merge threshold so it lands
        // as a fresh, non-merging tile (mirrors BlocksComponent.findAroundSpot).
        // Fall back to getPositionForNewItem() when no tile geometry is available.
        if (actionId.startsWith('ctAddTile_')) {
            var ctColorMap = {
                ctAddTile_red: '#f73530',
                ctAddTile_blue: '#2a90f7',
                ctAddTile_yellow: '#f4c500',
                ctAddTile_green: '#1dac78',
            };
            var colorHex_1 = ctColorMap[actionId];
            if (!colorHex_1)
                return;
            // Scan a row-major grid to the RIGHT of the selected tile for the first
            // spot that won't snap-merge into an existing tile, then fall back to
            // diagonal/below if that row is full. Without this, repeated adds all
            // target the same occupied spot and the controller's occupied-cell check
            // silently aborts every add after the first.
            var CELL = 110;
            var SNAP_THRESHOLD = 40;
            var FREE_STEP = CELL + SNAP_THRESHOLD + 10; // 160 — guarantees no merge
            // Anchor on the resolved target tile (works for tile / group / linear piece).
            var selData = ctTargetData !== null && ctTargetData !== void 0 ? ctTargetData : selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
            var dropX_2;
            var dropY_2;
            if ((selData === null || selData === void 0 ? void 0 : selData.center) && selData.columns != null && selData.rows != null) {
                var scaledCell = CELL * ((_r = selData.scale) !== null && _r !== void 0 ? _r : 1);
                var startX = selData.center.x + (selData.columns * scaledCell) / 2 + CELL;
                var startY = selData.center.y - (selData.rows * scaledCell) / 2;
                dropX_2 = startX;
                dropY_2 = startY;
                var found = false;
                for (var r = 0; r < 20 && !found; r++) {
                    for (var c = 0; c < 6 && !found; c++) {
                        var cand = { x: startX + c * FREE_STEP, y: startY + r * FREE_STEP };
                        var target = findSnapTargetManualTile(cand, { width: CELL, height: CELL }, manipulative, undefined, SNAP_THRESHOLD, CELL, 'manual');
                        if (!target) {
                            dropX_2 = cand.x;
                            dropY_2 = cand.y;
                            found = true;
                        }
                    }
                }
                console.log('[ctAddTile] selected-tile placement', {
                    color: colorHex_1,
                    selectedId: selData.id,
                    startX: startX,
                    startY: startY,
                    dropX: dropX_2,
                    dropY: dropY_2,
                    foundFreeSpot: found,
                });
            }
            else {
                var pos = getPositionForNewItem();
                dropX_2 = pos.x;
                dropY_2 = pos.y;
                console.log('[ctAddTile] no-selection fallback placement', {
                    color: colorHex_1,
                    dropX: dropX_2,
                    dropY: dropY_2,
                });
            }
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            setTimeout(function () {
                announceAssertive(t('text.coloredTilesTileAdded', { defaultValue: 'Colored tile added' }));
                console.log('[ctAddTile] dispatching createColoredTile', {
                    color: colorHex_1,
                    x: dropX_2,
                    y: dropY_2,
                });
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'createColoredTile',
                    data: { color: colorHex_1, x: dropX_2, y: dropY_2 },
                });
            }, 0);
            return;
        }
        // Canvas pan menu — scroll the viewport and keep the menu open so the
        // user can pan repeatedly. Only Exit (and Escape / outside click) closes it.
        if (isCanvasPanMenu) {
            var PAN_STEP = 120;
            var dirMap = {
                panUp: { dx: 0, dy: -PAN_STEP, label: t('text.scrolledUp') },
                panDown: { dx: 0, dy: PAN_STEP, label: t('text.scrolledDown') },
                panLeft: { dx: -PAN_STEP, dy: 0, label: t('text.scrolledLeft') },
                panRight: { dx: PAN_STEP, dy: 0, label: t('text.scrolledRight') },
            };
            var dir = dirMap[actionId];
            if (dir) {
                var scrollEl = (_s = document.getElementById('wrapperDiv')) === null || _s === void 0 ? void 0 : _s.parentElement;
                scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollBy({ left: dir.dx, top: dir.dy, behavior: 'smooth' });
                announce(dir.label);
                return;
            }
            if (actionId === 'exit') {
                closeMenu();
                // closeMenu restores focus to triggerRef (whiteboard) after 50ms;
                // override at 60ms to return focus to the panning button instead.
                setTimeout(function () {
                    var _a;
                    (_a = document.querySelector('.panningBtn')) === null || _a === void 0 ? void 0 : _a.focus();
                }, 60);
            }
            return;
        }
        // NumberRack bead menu — dispatches a custom event that the widget listens for.
        // Shift Left / Shift Right keep the menu open so the user can repeatedly
        // nudge the bead without reopening. Only Exit (and Escape / outside click)
        // closes the menu.
        if (isBeadMenu) {
            if (actionId === 'beadShiftLeft' || actionId === 'beadShiftRight') {
                // Guard: if the current menu item is disabled (bead at clamp edge),
                // don't dispatch. buildMenuItems already disables the label visually.
                var items = buildMenuItems();
                var item = items.find(function (m) { return m.id === actionId; });
                if (item === null || item === void 0 ? void 0 : item.disabled)
                    return;
                customEventHandler.dispatchEvent('numberRackBeadAction', {
                    rackId: beadRackId,
                    rodIndex: beadRodIdx,
                    beadIndex: beadBeadIdx,
                    action: actionId,
                });
                return;
            }
            if (actionId === 'exit') {
                closeMenu();
                return;
            }
        }
        // NumberRack Select Row submenu → focus first bead of chosen row
        if (actionId.startsWith('selectRow_')) {
            var rowIdx = parseInt(actionId.split('_')[1], 10);
            customEventHandler.dispatchEvent('numberRackSelectRow', {
                rackId: id,
                rodIndex: rowIdx,
            });
            closeMenu();
            return;
        }
        // Handle ncStamp_N pattern (stamp shape selection)
        if (actionId.startsWith('ncStamp_')) {
            var stampIdx = parseInt(actionId.split('_')[1], 10);
            // Announcement handled by NumberCharts widget's `stamp` case (it has
            // shape-name context we'd otherwise lose here).
            customEventHandler.dispatchEvent('bottomPanel', {
                id: 'stamp',
                panel: 'bottomPanel',
                data: stampIdx,
            });
            closeMenu();
            // Enter grid mode after closing
            setTimeout(function () {
                customEventHandler.dispatchEvent('ncEnterGridMode', {});
            }, 100);
            return;
        }
        // Handle stamp every OK from custom content form
        if (actionId === 'ncStampEveryOk') {
            var ncData = (_t = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _t !== void 0 ? _t : {};
            var _88 = stampEveryInputRef.current, interval = _88.interval, integer = _88.integer, numerator = _88.numerator, denominator = _88.denominator;
            var nt = (_u = ncData.numberType) !== null && _u !== void 0 ? _u : 0;
            var startNumerator = numerator;
            var startDenominator = denominator;
            if (nt === 0) {
                startNumerator = integer;
                startDenominator = 1;
            }
            else if (nt === 2) {
                startNumerator = integer * denominator + numerator;
                startDenominator = denominator;
            }
            else if (nt === 3) {
                for (var _89 = 0, _90 = (_v = ncData.cells) !== null && _v !== void 0 ? _v : []; _89 < _90.length; _89++) {
                    var row = _90[_89];
                    for (var _91 = 0, row_1 = row; _91 < row_1.length; _91++) {
                        var cell = row_1[_91];
                        var cv = cell.denominator !== 0 ? cell.numerator / cell.denominator : cell.numerator;
                        if (cv === integer) {
                            startNumerator = cell.numerator;
                            startDenominator = cell.denominator;
                            break;
                        }
                    }
                }
            }
            customEventHandler.dispatchEvent('choralDrawer', {
                id: 'stampEvery',
                val: {
                    interval: interval,
                    startNumerator: startNumerator,
                    startDenominator: startDenominator,
                    numberType: nt,
                    chartId: ncData.id,
                },
            });
            announce(t('text.stampedEveryCel'));
            closeMenu();
            return;
        }
        // Handle stamp color selection
        if (actionId.startsWith('ncColor_')) {
            var colorKey = actionId.replace('ncColor_', '');
            setColor(colorKey);
            announce(t('text.colorChanged', {
                color: t("text.color".concat(colorKey.charAt(0)).concat(colorKey.slice(1).toLowerCase())),
            }));
            return;
        }
        // Handle clock color selection
        if (actionId.startsWith('clockColor_')) {
            var colorKey = actionId.replace('clockColor_', '');
            setColor(colorKey);
            announce(t('text.colorChanged', {
                color: t("text.color".concat(colorKey.charAt(0)).concat(colorKey.slice(1).toLowerCase())),
            }));
            return;
        }
        // Track choral activity for reset-on-close
        if (actionId.startsWith('ncChoral')) {
            choralActiveRef.current = true;
        }
        // Handle ncChoralSpeed_N pattern
        if (actionId.startsWith('ncChoralSpeed_')) {
            var speedIdx = parseInt(actionId.split('_')[1], 10);
            var ncData = (_w = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _w !== void 0 ? _w : {};
            setChoralSpeedLocal(speedIdx);
            // Speed change resets the animation, same as direction change —
            // re-enable Play / disable Pause so the user can restart.
            setChoralIsPlaying(false);
            customEventHandler.dispatchEvent('choralDrawer', {
                id: 'choralSpeed',
                val: {
                    direction: (_x = choralDirLocal !== null && choralDirLocal !== void 0 ? choralDirLocal : ncData.choralDirectionIndex) !== null && _x !== void 0 ? _x : 0,
                    speed: speedIdx,
                },
            });
            announce(t('text.choralSpeedChanged'));
            return;
        }
        // Handle ncChoralDir_N pattern
        if (actionId.startsWith('ncChoralDir_')) {
            var dirIdx = parseInt(actionId.split('_')[1], 10);
            var ncData = (_y = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _y !== void 0 ? _y : {};
            setChoralDirLocal(dirIdx);
            // Changing direction restarts the animation from the beginning, so
            // mirror the ChoralControlsDrawer's behavior here: clear the
            // playing state so the Play button re-enables and Pause disables.
            setChoralIsPlaying(false);
            customEventHandler.dispatchEvent('choralDrawer', {
                id: 'choralDirection',
                val: {
                    direction: dirIdx,
                    speed: (_z = choralSpeedLocal !== null && choralSpeedLocal !== void 0 ? choralSpeedLocal : ncData.choralSpeed) !== null && _z !== void 0 ? _z : 1,
                },
            });
            announce(t('text.choralDirectionChanged'));
            return;
        }
        // Handle timer actions (Start, Pause, Stop)
        if (actionId === 'timerStart' || actionId === 'timerPause' || actionId === 'timerStop') {
            var eventMap = {
                timerStart: 'start',
                timerPause: 'pause',
                timerStop: 'stop',
            };
            customEventHandler.dispatchEvent('bottomPanel', {
                id: eventMap[actionId],
                panel: 'bottomPanel',
            });
            closeMenu();
            return;
        }
        // Handle clock time update actions (don't close menu — user may want multiple adjustments)
        var clockTimeActions = [
            'clockIncreaseHour',
            'clockDecreaseHour',
            'clockIncreaseMinute',
            'clockDecreaseMinute',
            'clockTogglePeriod',
            'clockToggleFormat',
        ];
        if (clockTimeActions.includes(actionId)) {
            customEventHandler.dispatchEvent('bottomPanel', {
                id: actionId,
                panel: 'bottomPanel',
            });
            return;
        }
        // Handle clock toggle actions (don't close menu — user may want multiple toggles)
        if (actionId === 'clockDigitalDisplay' ||
            actionId === 'clockRunAndJumpToggle' ||
            actionId === 'clockElapsedTime' ||
            actionId === 'clockRunPlay' ||
            actionId === 'clockRunStop') {
            var eventMap = {
                clockDigitalDisplay: 'digitalDisplay',
                clockRunAndJumpToggle: 'runAndJump',
                clockElapsedTime: 'elapsedTime',
                clockRunPlay: 'runPlay',
                clockRunStop: 'runStop',
            };
            customEventHandler.dispatchEvent('bottomPanel', {
                id: eventMap[actionId],
                panel: 'bottomPanel',
            });
            return;
        }
        // Handle clock fraction overlay
        if (actionId.startsWith('clockFraction_')) {
            var suffix = actionId.replace('clockFraction_', '');
            if (suffix === 'none') {
                // Dispatch index 0 twice to toggle off (same parts value → removes overlay)
                var currentParts = (_0 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _0 === void 0 ? void 0 : _0.fractionOverlayParts;
                if (currentParts !== null) {
                    var fractionParts = [2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60];
                    var currentIdx = fractionParts.indexOf(currentParts);
                    if (currentIdx >= 0) {
                        customEventHandler.dispatchEvent('bottomPanel', {
                            id: 'fractionOverlay',
                            panel: 'bottomPanel',
                            data: currentIdx,
                        });
                    }
                }
                closeMenu();
            }
            else {
                var idx = parseInt(suffix, 10);
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'fractionOverlay',
                    panel: 'bottomPanel',
                    data: idx,
                });
            }
            closeMenu();
            return;
        }
        // Handle clock fraction shading
        if (actionId === 'clockFractionShading') {
            customEventHandler.dispatchEvent('bottomPanel', {
                id: 'fractionOverlayShading',
                panel: 'bottomPanel',
            });
            return;
        }
        // "Unlink Cube" — dissolve the entire chain the focused cube belongs to,
        // and visibly space the cubes apart so they no longer look connected.
        if (actionId === 'unlinkCube') {
            var targetId_1 = contextMenu.manipulativeId;
            if (targetId_1) {
                var targetItem = manipulative.find(function (m) { return m.data.id === targetId_1; });
                var targetData = (targetItem === null || targetItem === void 0 ? void 0 : targetItem.data.type) === 'linkingCubes'
                    ? targetItem.data
                    : undefined;
                var cubeChainId_1 = targetData === null || targetData === void 0 ? void 0 : targetData.chainId;
                // Defensive: refuse to unlink when any chain member is locked,
                // matching the disabled state on the menu item and the mouse
                // drag-break gate.
                if (cubeChainId_1) {
                    var chainHasLock = manipulative.some(function (m) {
                        var _a;
                        return m.data.type === 'linkingCubes' &&
                            m.data.chainId === cubeChainId_1 &&
                            ((_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning);
                    });
                    if (chainHasLock) {
                        closeMenu();
                        return;
                    }
                }
                if (cubeChainId_1) {
                    // Find every member of this chain (sorted by chainIndex), strip
                    // chainId/chainIndex from each, and reposition them with a clear
                    // gap so they read as separate cubes after the unlink.
                    var chainMembers = manipulative
                        .filter(function (m) {
                        return m.data.type === 'linkingCubes' &&
                            m.data.chainId === cubeChainId_1;
                    })
                        .map(function (m) { return m.data; })
                        .sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
                    var orientation_1 = (_2 = (_1 = chainMembers[0]) === null || _1 === void 0 ? void 0 : _1.orientation) !== null && _2 !== void 0 ? _2 : 'vertical';
                    var scale = (_4 = (_3 = chainMembers[0]) === null || _3 === void 0 ? void 0 : _3.scale) !== null && _4 !== void 0 ? _4 : 1;
                    // Spacing = full visual cube width plus a gap so cubes don't
                    // touch (clearly separated, no longer interlocked).
                    var gap_1 = 12;
                    var step_1 = CSS_CUBE_SIZE * scale + gap_1;
                    var baseCenter_1 = (_6 = (_5 = chainMembers[0]) === null || _5 === void 0 ? void 0 : _5.center) !== null && _6 !== void 0 ? _6 : { x: 0, y: 0 };
                    var newCenters_1 = new Map();
                    chainMembers.forEach(function (c, i) {
                        var newCenter = orientation_1 === 'horizontal'
                            ? { x: baseCenter_1.x + i * step_1, y: baseCenter_1.y }
                            : { x: baseCenter_1.x, y: baseCenter_1.y - i * step_1 };
                        newCenters_1.set(c.id, newCenter);
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.type !== 'linkingCubes')
                                return item;
                            var d = item.data;
                            if (d.chainId !== cubeChainId_1)
                                return item;
                            var newCenter = (_a = newCenters_1.get(d.id)) !== null && _a !== void 0 ? _a : d.center;
                            var _c = d.chainId, _i = d.chainIndex, rest = __rest(d, ["chainId", "chainIndex"]);
                            return __assign(__assign({}, item), { data: __assign(__assign({}, rest), { center: newCenter, position: newCenter }) });
                        });
                    });
                    announce(t('text.cubeUnlinked'));
                }
                else {
                    // Defensive: if it was already standalone, fall through to the
                    // existing single-cube unlink path.
                    unlinkSelectedCubes([targetId_1], manipulative, setManipulative);
                    announce(t('text.cubeUnlinked'));
                }
            }
            closeMenu();
            return;
        }
        // "Link to Cube" — activate the on-canvas target picker. Dummy buttons
        // appear over every eligible cube; user arrows/Tabs between them, Enter
        // picks one, Escape cancels. LinkingCubePicker owns the rest of the flow.
        // We tear down the context menu INLINE (skipping closeMenu's focus
        // restore) so focus can move cleanly to the first dummy button instead
        // of bouncing back to the source cube.
        if (actionId === 'linkToCube') {
            var focusedId = contextMenu.manipulativeId;
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            if (focusedId) {
                customEventHandler.dispatchEvent('openLinkingCubePicker', {
                    focusedCubeId: focusedId,
                });
            }
            return;
        }
        // "Unlink Cubes" (picker) — activate the on-canvas chain-split picker.
        // Only the chain BASE responds to linkingCubeStartUnlink, so resolve
        // the focused cube's chainId and dispatch with the base's id. Same
        // inline teardown as linkToCube so focus can move to the first dummy
        // instead of bouncing back to the source.
        if (actionId === 'unlinkCubeAt') {
            var targetId_2 = contextMenu.manipulativeId;
            var targetItem = manipulative.find(function (m) { return m.data.id === targetId_2; });
            var targetChainId_1 = (targetItem === null || targetItem === void 0 ? void 0 : targetItem.data.type) === 'linkingCubes'
                ? targetItem.data.chainId
                : undefined;
            // Defensive: refuse to unlink when any chain member is locked,
            // matching the disabled state on the menu item and the mouse
            // drag-break gate.
            if (targetChainId_1) {
                var chainHasLock = manipulative.some(function (m) {
                    var _a;
                    return m.data.type === 'linkingCubes' &&
                        m.data.chainId === targetChainId_1 &&
                        ((_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning);
                });
                if (chainHasLock) {
                    closeMenu();
                    return;
                }
            }
            var baseId = targetChainId_1
                ? (_7 = manipulative.find(function (m) {
                    var _a;
                    return m.data.type === 'linkingCubes' &&
                        m.data.chainId === targetChainId_1 &&
                        ((_a = m.data.chainIndex) !== null && _a !== void 0 ? _a : 0) === 0;
                })) === null || _7 === void 0 ? void 0 : _7.data.id
                : undefined;
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            if (baseId) {
                customEventHandler.dispatchEvent('linkingCubeStartUnlink', {
                    id: baseId,
                });
            }
            return;
        }
        // "Select similar pieces" — activate the NMP peer picker. Same inline
        // teardown pattern as linkToCube so the menu's focus-restore timeout
        // doesn't steal focus from the first dummy button.
        if (actionId === 'selectSimilarNmp') {
            var sourceId = contextMenu.manipulativeId;
            highlightDice(null);
            setContextMenu(null);
            setSubmenuOpen(false);
            setActiveIndex(-1);
            setSubmenuIndex(0);
            if (sourceId) {
                customEventHandler.dispatchEvent('openNmpPicker', { sourceId: sourceId });
            }
            return;
        }
        switch (actionId) {
            case 'properties': {
                // Ensure the array is selected so the right-side panel shows its tools
                var manipId = contextMenu.manipulativeId;
                if (manipId && !selectedTool.includes(manipId)) {
                    setSelectedTool([manipId]);
                }
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                // Move focus to the right-side tool panel after the panel renders
                setTimeout(function () {
                    var panel = document.querySelector('#rightSideDrawerContainer .drawer-content');
                    panel === null || panel === void 0 ? void 0 : panel.focus();
                }, 50);
                break;
            }
            case 'amAddRow':
            case 'amAddColumn':
            case 'amToggleExpression':
            case 'amToggleProduct':
            case 'amPartMode':
            case 'amSelectMode': {
                var amManipId_1 = contextMenu.manipulativeId;
                if (amManipId_1 && !selectedTool.includes(amManipId_1)) {
                    setSelectedTool([amManipId_1]);
                }
                var amEventMap = {
                    amAddRow: 'addRow',
                    amAddColumn: 'addColumn',
                    amToggleExpression: 'toggleExpression',
                    amToggleProduct: 'toggleProduct',
                    amPartMode: 'partMode',
                    amSelectMode: 'selectMode',
                };
                var bottomId_2 = amEventMap[actionId];
                var amData = (_8 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _8 !== void 0 ? _8 : {};
                var amRows = (_9 = amData.rows) !== null && _9 !== void 0 ? _9 : 1;
                var amCols = (_10 = amData.columns) !== null && _10 !== void 0 ? _10 : 1;
                var amShowExpr = (_11 = amData.showExpression) !== null && _11 !== void 0 ? _11 : true;
                var amShowProd = (_12 = amData.showProduct) !== null && _12 !== void 0 ? _12 : true;
                var amPartMode = (_13 = amData.partModeEnabled) !== null && _13 !== void 0 ? _13 : true;
                var amSelectMode = (_14 = amData.selectModeEnabled) !== null && _14 !== void 0 ? _14 : false;
                var amMsgMap_1 = {
                    amAddRow: t('text.arrayModelRowAdded', { total: amRows + 1 }),
                    amAddColumn: t('text.arrayModelColumnAdded', { total: amCols + 1 }),
                    amToggleExpression: amShowExpr
                        ? t('text.arrayModelExpressionOff')
                        : t('text.arrayModelExpressionOn'),
                    amToggleProduct: amShowProd
                        ? t('text.arrayModelProductOff')
                        : t('text.arrayModelProductOn'),
                    amPartMode: amPartMode ? t('text.arrayModelPartsOff') : t('text.arrayModelPartsOn'),
                    amSelectMode: amSelectMode
                        ? t('text.arrayModelSelectOff')
                        : t('text.arrayModelSelectOn'),
                };
                var keepMenuOpen = actionId === 'amAddRow' || actionId === 'amAddColumn';
                if (keepMenuOpen) {
                    // Dispatch action; menu stays open and focus stays on the menu item
                    customEventHandler.dispatchEvent('bottomPanel', { id: bottomId_2 });
                    setAmAnnouncement('');
                    requestAnimationFrame(function () { var _a; return setAmAnnouncement((_a = amMsgMap_1[actionId]) !== null && _a !== void 0 ? _a : ''); });
                }
                else {
                    // Close menu, return focus to the array model, announce via widget's assertive region
                    highlightDice(null);
                    setContextMenu(null);
                    setSubmenuOpen(false);
                    setActiveIndex(-1);
                    setSubmenuIndex(0);
                    setTimeout(function () {
                        customEventHandler.dispatchEvent('bottomPanel', { id: bottomId_2 });
                        var wrapper = document.querySelector("[data-manipulative-id=\"".concat(amManipId_1, "\"]"));
                        if (wrapper) {
                            var target = wrapper.tabIndex >= 0
                                ? wrapper
                                : wrapper.querySelector('[tabindex="0"], button, [role="button"]');
                            (target !== null && target !== void 0 ? target : wrapper).focus();
                        }
                    }, 0);
                }
                break;
            }
            case 'edit':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            // Coins & Counters: random flip (dice-style). Listened for by
            // CoinsAndCountersManipulative (single counter) and
            // CoinsAndCountersGroupOverlay (group).
            case 'flip':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            // Text formatting actions — fire the same bottomPanel events the
            // formatting toolbar buttons dispatch. TextManipulativeComp / MathEditor
            // are already wired to handle these IDs. Menu stays open (same pattern
            // as Move / Change Size) so users can stack multiple formats without
            // reopening the menu each time.
            //
            // The BottomPanel state-change observer doesn't fire when the action
            // originates from the context menu (focus is on the menu, not the
            // editor), so announce here directly so SR users always get feedback.
            // B/I/U use "toggled" because we can't read the new Quill inline state
            // synchronously from this site.
            case 'bold':
            case 'italic':
            case 'underline':
            case 'larger':
            case 'smaller':
            case 'leftAlign':
            case 'centerAlign':
            case 'rightAlign': {
                var formatAnnouncements = {
                    bold: 'text.boldToggled',
                    italic: 'text.italicToggled',
                    underline: 'text.underlineToggled',
                    larger: 'text.fontSizeIncreased',
                    smaller: 'text.fontSizeDecreased',
                    leftAlign: 'text.alignedLeft',
                    centerAlign: 'text.alignedCenter',
                    rightAlign: 'text.alignedRight',
                };
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                var key = formatAnnouncements[actionId];
                if (key)
                    announce(t(key));
                break;
            }
            case 'configurePanel': {
                // Ensure the context-menu target is in selectedTool so the right drawer
                // renders the correct config for this manipulative.
                var cfgId = contextMenu.manipulativeId;
                if (cfgId && !selectedTool.includes(cfgId)) {
                    setSelectedTool([cfgId]);
                }
                closeMenu();
                // Open the right drawer + move focus to its toggle button. Multiple
                // attempts so we win against closeMenu's setTimeout(0) trigger-focus
                // restore and the drawer's 300ms slide-in transition.
                customEventHandler.dispatchEvent('openRightDrawer', {});
                var focusToggle_1 = function () {
                    var toggle = document.querySelector('#rightSideDrawerContainer .drawer-toggle-btn');
                    toggle === null || toggle === void 0 ? void 0 : toggle.focus();
                };
                [50, 200, 400, 600, 900].forEach(function (delay) {
                    setTimeout(focusToggle_1, delay);
                });
                break;
            }
            case 'duplicate': {
                // Ensure the context-menu target is in selectedTool — with VoiceOver on,
                // opening the menu doesn't always push the item in via synthetic click,
                // so the duplicate handler would otherwise see an empty selection.
                var dupId = contextMenu.manipulativeId;
                // For grouped pattern shapes / NMP pieces, duplicate via the group
                // ID so all members copy together. Single-piece duplicates of
                // grouped items would otherwise leave the group fragmented.
                var focusedDupType = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type;
                var isGroupableTypeForDup = focusedDupType === 'patternShapes' ||
                    focusedDupType === 'numberPiece' ||
                    focusedDupType === 'moneyPiece';
                var dupGroupId = isGroupableTypeForDup
                    ? (_15 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _15 === void 0 ? void 0 : _15.groupId
                    : undefined;
                var effectiveDupId = dupGroupId !== null && dupGroupId !== void 0 ? dupGroupId : dupId;
                // For a linking-cube chain, expand the duplicate target to ALL chain
                // members so the whole chain gets duplicated as one unit (mirrors
                // how Delete handles chains). Otherwise the GameController only
                // duplicates the one cube in selectedTool.
                var dupCubeChainId_1 = ((_16 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _16 === void 0 ? void 0 : _16.type) === 'linkingCubes'
                    ? (_17 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _17 === void 0 ? void 0 : _17.chainId
                    : undefined;
                var idsToDuplicate = [effectiveDupId];
                if (dupCubeChainId_1) {
                    idsToDuplicate = manipulative
                        .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === dupCubeChainId_1; })
                        .map(function (m) { return m.data.id; })
                        .filter(Boolean);
                    if (idsToDuplicate.length === 0)
                        idsToDuplicate = [dupId];
                }
                else if (!isGroupableTypeForDup &&
                    dupId &&
                    selectedTool.length > 1 &&
                    selectedTool.includes(dupId)) {
                    // Ad-hoc multi-selection (e.g. several ungrouped coloredTiles): duplicate
                    // the whole selection together instead of collapsing to just the item the
                    // menu was opened on, so the copies keep moving as a set afterwards.
                    idsToDuplicate = __spreadArray([], selectedTool, true);
                }
                var sameSelection = selectedTool.length === idsToDuplicate.length &&
                    idsToDuplicate.every(function (id) { return selectedTool.includes(id); });
                if (!sameSelection) {
                    setSelectedTool(idsToDuplicate);
                }
                // Snapshot all data-manipulative-id values currently in the DOM so we
                // can identify the new duplicate after the state commits, then focus it.
                // Querying the DOM (rather than just manipulative state) covers tables
                // and other items whose IDs live outside the manipulative array.
                var idsBefore_1 = new Set(Array.from(document.querySelectorAll('[data-manipulative-id]'))
                    .map(function (el) { return el.getAttribute('data-manipulative-id'); })
                    .filter(Boolean));
                // Close menu without focus restore — we'll focus the new duplicate
                // ourselves once it appears in the DOM.
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                // Defer the dispatch so React commits the setSelectedTool first.
                // Without this, GameController's duplicate handler reads the stale
                // selectedTool (one cube) and only duplicates that one — chains
                // would lose every member except the focused base.
                if (!sameSelection) {
                    setTimeout(function () {
                        customEventHandler.dispatchEvent('bottomPanel', {
                            id: actionId,
                            panel: 'bottomPanel',
                        });
                    }, 0);
                }
                else {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: actionId,
                        panel: 'bottomPanel',
                    });
                }
                // Poll for the new manipulative(s) to mount, then focus the new
                // unit. For a chain duplicate this needs to be the chain BASE
                // (only that cube's button has tabIndex=0); other chain members
                // are aria-hidden / non-focusable. Standalone duplicates work
                // identically — newEls is just length 1.
                var attempts_1 = 0;
                var findAndFocus_1 = function () {
                    var _a, _b, _d, _e, _f;
                    attempts_1 += 1;
                    var newEls = Array.from(document.querySelectorAll('[data-manipulative-id]')).filter(function (el) {
                        var id = el.getAttribute('data-manipulative-id');
                        return id && !idsBefore_1.has(id);
                    });
                    if (newEls.length > 0) {
                        // Table containers should be focused directly — the container
                        // itself is the tab stop, not an inner button.
                        var tableEl = newEls.find(function (el) { var _a; return (_a = el.getAttribute('data-manipulative-id')) === null || _a === void 0 ? void 0 : _a.startsWith('tableText_'); });
                        if (tableEl) {
                            tableEl.focus();
                            var tableContainer = (_a = tableEl.closest('[data-table-index]')) !== null && _a !== void 0 ? _a : tableEl.parentElement;
                            var tRows = (_d = (_b = tableContainer === null || tableContainer === void 0 ? void 0 : tableContainer.querySelector('tbody')) === null || _b === void 0 ? void 0 : _b.querySelectorAll('tr').length) !== null && _d !== void 0 ? _d : 0;
                            var tCols = (_f = (_e = tableContainer === null || tableContainer === void 0 ? void 0 : tableContainer.querySelector('tr')) === null || _e === void 0 ? void 0 : _e.querySelectorAll('td').length) !== null && _f !== void 0 ? _f : 0;
                            announce(t('text.tableDuplicated', { rows: tRows, cols: tCols }));
                            return;
                        }
                        // Search across ALL new wrappers for the first one with a
                        // truly focusable inner button. For a chain, only the base
                        // satisfies this; for a standalone the lone wrapper does.
                        for (var _g = 0, newEls_1 = newEls; _g < newEls_1.length; _g++) {
                            var wrapper = newEls_1[_g];
                            var innerBtn = wrapper.querySelector('button[tabindex="0"], [tabindex="0"]');
                            if (innerBtn) {
                                innerBtn.focus();
                                return;
                            }
                        }
                        // Defensive fallback: focus the first new wrapper itself.
                        newEls[0].focus();
                        return;
                    }
                    if (attempts_1 < 10)
                        setTimeout(findAndFocus_1, 50);
                };
                setTimeout(findAndFocus_1, 50);
                break;
            }
            case 'delete': {
                if (positionLock || editingLock || isPresetItem)
                    break;
                var deletedId_1 = contextMenu.manipulativeId;
                // For grouped pattern shapes / NMP pieces, delete via the group ID
                // so the whole group goes together. Without this, opening the menu
                // on a single member of an NMP group and pressing Delete would
                // remove only that one piece while the rest of the group lingered.
                var focusedDataType = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type;
                var isGroupableTypeForDelete = focusedDataType === 'patternShapes' ||
                    focusedDataType === 'numberPiece' ||
                    focusedDataType === 'moneyPiece';
                var deleteGroupId = isGroupableTypeForDelete
                    ? (_18 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _18 === void 0 ? void 0 : _18.groupId
                    : undefined;
                var effectiveDeleteId = deleteGroupId !== null && deleteGroupId !== void 0 ? deleteGroupId : deletedId_1;
                // For a linking-cube chain, expand the deletion target to ALL chain
                // members so Delete removes the whole chain (not just the focused base cube).
                var cubeChainId_2 = ((_19 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _19 === void 0 ? void 0 : _19.type) === 'linkingCubes'
                    ? (_20 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _20 === void 0 ? void 0 : _20.chainId
                    : undefined;
                var idsToDelete = [effectiveDeleteId];
                if (cubeChainId_2) {
                    idsToDelete = manipulative
                        .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === cubeChainId_2; })
                        .map(function (m) { return m.data.id; })
                        .filter(Boolean);
                    if (idsToDelete.length === 0)
                        idsToDelete = [deletedId_1];
                }
                // Ensure exactly the desired ids are selected before deleting
                var sameSelection = selectedTool.length === idsToDelete.length &&
                    idsToDelete.every(function (id) { return selectedTool.includes(id); });
                if (!sameSelection) {
                    setSelectedTool(idsToDelete);
                }
                // Immediately park focus on canvas to prevent VoiceOver from reading page title
                var canvas = document.querySelector('.whiteBoardContainer');
                if (canvas)
                    canvas.focus();
                // Close menu state without focus restore (element will be deleted)
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                // Dispatch delete after state update
                setTimeout(function () {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: actionId,
                        panel: 'bottomPanel',
                    });
                }, 0);
                // Focus last remaining manipulative after React re-render
                setTimeout(function () {
                    var allManips = document.querySelectorAll('[data-manipulative-id]');
                    var remaining = Array.from(allManips).filter(function (el) { return el.getAttribute('data-manipulative-id') !== deletedId_1; });
                    if (remaining.length > 0) {
                        remaining[remaining.length - 1].focus();
                    }
                }, 200);
                break;
            }
            case 'scalePlus':
            case 'scaleMinus': {
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                if (selectedItem) {
                    var currScale = Number((_21 = selectedItem.data.scale) !== null && _21 !== void 0 ? _21 : 1);
                    var newScale = actionId === 'scalePlus'
                        ? Math.min(5, Math.round((currScale + 0.1) * 10) / 10)
                        : Math.max(0.5, Math.round((currScale - 0.1) * 10) / 10);
                    var scaleTypeMap = {
                        deck_pool: 'text.manipulativeCardDeck',
                        analog: 'text.manipulativeClock',
                        clock: 'text.manipulativeTimer',
                        spinner: 'text.manipulativeSpinner',
                        arrayModel: 'text.arrayModels',
                    };
                    var scaleName = t(scaleTypeMap[selectedItem.type] || "text.".concat(selectedItem.type)) ||
                        selectedItem.type;
                    var scaleMsg_1 = t('text.manipulativeScaled', {
                        name: scaleName,
                        direction: actionId === 'scalePlus' ? t('text.up') : t('text.down'),
                        value: newScale.toFixed(1),
                    });
                    setAmAnnouncement('');
                    requestAnimationFrame(function () { return setAmAnnouncement(scaleMsg_1); });
                }
                break;
            }
            // NumberChart-specific actions
            case 'ncGridToggle': {
                // Announcement handled by NumberCharts widget's `gride` case.
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'gride',
                    panel: 'bottomPanel',
                });
                break;
            }
            // ncStampEvery_N handled above via pattern match
            case 'ncEraseStamp':
                // Announcement handled by NumberCharts widget's `stampEraser` case.
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'stampEraser',
                    panel: 'bottomPanel',
                });
                closeMenu();
                // Enter grid mode after closing
                setTimeout(function () {
                    customEventHandler.dispatchEvent('ncEnterGridMode', {});
                }, 100);
                break;
            case 'ncEraseAllStamps':
                // Announcement handled inside NumberCharts widget's `stampAction`
                // (fires `allStampsErased` when `hideType === 'all'`).
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'stampEraserAll',
                    panel: 'bottomPanel',
                });
                break;
            case 'ncShowHideNumbers':
                // Announcement handled by NumberCharts widget's `showHideNumbers` case.
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'showHideNumbers',
                    panel: 'bottomPanel',
                });
                closeMenu();
                // Enter grid mode after closing
                setTimeout(function () {
                    customEventHandler.dispatchEvent('ncEnterGridMode', {});
                }, 100);
                break;
            case 'ncShowAllNumbers':
                // Announcement handled by NumberCharts widget's `showAllHideNumbers` case.
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'showAllHideNumbers',
                    panel: 'bottomPanel',
                });
                break;
            case 'ncChoralPlay': {
                var ncDataPlay = (_22 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _22 !== void 0 ? _22 : {};
                customEventHandler.dispatchEvent('choralDrawer', {
                    id: 'choralPlay',
                    val: {
                        direction: (_23 = ncDataPlay.choralDirectionIndex) !== null && _23 !== void 0 ? _23 : 0,
                        speed: (_24 = ncDataPlay.choralSpeed) !== null && _24 !== void 0 ? _24 : 1,
                    },
                });
                setChoralIsPlaying(true);
                announce(t('text.choralPlaying'));
                break;
            }
            case 'ncChoralPause':
                customEventHandler.dispatchEvent('choralDrawer', {
                    id: 'choralPause',
                });
                setChoralIsPlaying(false);
                announce(t('text.choralPaused'));
                break;
            case 'ncChoralNext': {
                var ncDataNext = (_25 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _25 !== void 0 ? _25 : {};
                customEventHandler.dispatchEvent('choralDrawer', {
                    id: 'choralNext',
                    val: {
                        direction: (_26 = ncDataNext.choralDirectionIndex) !== null && _26 !== void 0 ? _26 : 0,
                        speed: (_27 = ncDataNext.choralSpeed) !== null && _27 !== void 0 ? _27 : 1,
                    },
                });
                announce(t('text.choralNextCell'));
                break;
            }
            case 'ncChoralReset':
                customEventHandler.dispatchEvent('choralDrawer', {
                    id: 'choralReset',
                });
                setChoralIsPlaying(false);
                announce(t('text.choralResetDone'));
                closeMenu();
                break;
            case 'ncGameStart':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'game',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'ncReplay':
                // Announcement handled by NumberCharts widget's `replay` case so both
                // context-menu and right-panel paths announce via the same source.
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'replay',
                    panel: 'bottomPanel',
                });
                break;
            case 'ncExitGame':
                // Announcement handled by NumberCharts widget's `exitGames` case.
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'exitGames',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'colorRed':
            case 'colorYellow':
            case 'colorBlue':
            case 'colorBrown':
            case 'colorOrange':
            case 'colorGreen':
            case 'colorPurple':
            case 'colorBlack':
            case 'colorWhite':
            case 'colorGray': {
                var colorMap = {
                    colorRed: { hex: ColorEnum.RED, label: 'text.red' },
                    colorYellow: { hex: ColorEnum.YELLOW, label: 'text.yellow' },
                    colorBlue: { hex: ColorEnum.BLUE, label: 'text.blue' },
                    colorBrown: { hex: ColorEnum.BROWN, label: 'text.brown' },
                    colorOrange: { hex: ColorEnum.ORANGE, label: 'text.orange' },
                    colorGreen: { hex: ColorEnum.GREEN, label: 'text.green' },
                    colorPurple: { hex: ColorEnum.PURPLE, label: 'text.purple' },
                    colorBlack: { hex: ColorEnum.BLACK, label: 'text.black' },
                    colorWhite: { hex: ColorEnum.WHITE, label: 'text.white' },
                    colorGray: { hex: ColorEnum.GRAY, label: 'text.gray' },
                };
                var chosen_1 = colorMap[actionId];
                if (!chosen_1)
                    break;
                if (isTextMenu && textIndex >= 0) {
                    if (editingLock)
                        break;
                    // Update data.color so the context menu checkmark reflects the new color.
                    setText(function (prev) {
                        var current = prev[textIndex];
                        if (!current)
                            return prev;
                        var updated = __assign(__assign({}, current), { data: __assign(__assign({}, current.data), { color: chosen_1.hex }) });
                        saveState({
                            type: 'text',
                            id: String(textIndex),
                            propertyName: 'completeData',
                            old: JSON.stringify(current),
                            new: JSON.stringify(updated),
                        });
                        var next = __spreadArray([], prev, true);
                        next[textIndex] = updated;
                        return next;
                    });
                    // Dispatch colourChange so MathEditor applies the color to the DOM
                    // (selected text only, or all text when no selection).
                    customEventHandler.dispatchEvent('colourChange', {
                        color: chosen_1.hex,
                        colorKey: Object.keys(ColorEnum).find(function (k) { return ColorEnum[k] === chosen_1.hex; }) || 'BLACK',
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    announce(t('text.colorChanged', { color: t(chosen_1.label) }));
                    break;
                }
                // Dice listens for the same `colourChange` event annotations use, so
                // a single dispatch covers both. Dice has no editing-lock check at
                // this level — the dice's own listener decides whether to apply.
                var targetItemDataType = (_28 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _28 === void 0 ? void 0 : _28.type;
                var isDiceTarget = targetItemDataType === 'dice';
                var isAnnotationTarget = isAnnotationMenu && annotationIndex >= 0 && !editingLock;
                if (!isAnnotationTarget && !isDiceTarget)
                    break;
                customEventHandler.dispatchEvent('colourChange', {
                    color: chosen_1.hex,
                    colorKey: Object.keys(ColorEnum).find(function (k) { return ColorEnum[k] === chosen_1.hex; }) || 'BLACK',
                });
                announce(t('text.colorChanged', { color: t(chosen_1.label) }));
                break;
            }
            case 'rotateClockwise':
            case 'rotateAnticlockwise':
                if (isAnnotationMenu && annotationIndex >= 0) {
                    var ann = annotations[annotationIndex];
                    if (!ann || positionLock)
                        break;
                    var dir = actionId === 'rotateClockwise' ? 1 : -1;
                    var newRot = ((_29 = ann.data.rotation) !== null && _29 !== void 0 ? _29 : 0) + dir;
                    var newAnn_1 = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { rotation: newRot }) });
                    saveState({
                        type: 'annotations',
                        id: String(annotationIndex),
                        propertyName: 'completeData',
                        old: JSON.stringify(ann),
                        new: JSON.stringify(newAnn_1),
                    });
                    setAnnotations(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[annotationIndex] = newAnn_1;
                        return next;
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    announceAssertive(t('text.annotationRotated', {
                        name: getAnnotationTypeName(),
                        direction: actionId === 'rotateClockwise' ? t('text.clockwise') : t('text.anticlockwise'),
                        degrees: newRot,
                    }));
                    break;
                }
                if (isTextMenu && textIndex >= 0 && selectedTextItem) {
                    if (editingLock)
                        break;
                    var dir = actionId === 'rotateClockwise' ? 1 : -1;
                    var newRot = ((_31 = (_30 = selectedTextItem.data) === null || _30 === void 0 ? void 0 : _30.rotation) !== null && _31 !== void 0 ? _31 : 0) + dir;
                    var updated_1 = __assign(__assign({}, selectedTextItem), { data: __assign(__assign({}, selectedTextItem.data), { rotation: newRot }) });
                    saveState({
                        type: 'text',
                        id: String(textIndex),
                        propertyName: 'completeData',
                        old: JSON.stringify(selectedTextItem),
                        new: JSON.stringify(updated_1),
                    });
                    setText(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[textIndex] = updated_1;
                        return next;
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    break;
                }
                // NumberFrame / Protractor — dispatch directly so there is no reliance
                // on implicit switch fall-through (which was fragile under minification
                // and made the listener miss the event intermittently).
                if (selectedItem) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: actionId,
                        panel: 'bottomPanel',
                    });
                }
                break;
            case 'angleClockwise':
            case 'angleAnticlockwise':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                break;
            case 'lockPosition': {
                var newState_1 = !positionLock;
                if (isTableTextMenu && tableTextIndex >= 0 && selectedTableItem) {
                    var updated_2 = __assign(__assign({}, selectedTableItem), { data: __assign(__assign({}, selectedTableItem.data), { lock: __assign(__assign({}, selectedTableItem.data.lock), { positioning: newState_1 }) }) });
                    saveState({ type: 'table', id: String(tableTextIndex), propertyName: 'lockPosition', old: JSON.stringify(selectedTableItem), new: JSON.stringify(updated_2) });
                    setTable(function (prev) { var nt = __spreadArray([], prev, true); nt[tableTextIndex] = updated_2; return nt; });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    announce(t(newState_1 ? 'text.lockPositionAction' : 'text.unlockPositionAction'));
                    break;
                }
                if (isAnnotationMenu && annotationIndex >= 0) {
                    setAnnotations(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[annotationIndex] = __assign(__assign({}, next[annotationIndex]), { data: __assign(__assign({}, next[annotationIndex].data), { lock: __assign(__assign({}, next[annotationIndex].data.lock), { positioning: newState_1 }) }) });
                        return next;
                    });
                }
                else if (isTextMenu && textIndex >= 0) {
                    setText(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[textIndex] = __assign(__assign({}, next[textIndex]), { data: __assign(__assign({}, next[textIndex].data), { lock: __assign(__assign({}, next[textIndex].data.lock), { positioning: newState_1 }) }) });
                        return next;
                    });
                }
                else {
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.id === id
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { positioning: newState_1 }) }) }) : item;
                        });
                    });
                }
                setAmAnnouncement('');
                requestAnimationFrame(function () {
                    return setAmAnnouncement(newState_1 ? t('text.lockPositionAction') : t('text.unlockPositionAction'));
                });
                // Also push through the GLOBAL announcer (document.body live
                // regions) — the menu often closes after the click, which
                // unmounts the local amAnnouncement live region before any SR
                // can read it. The global region survives the unmount, matching
                // the pattern used by lockToggle / lockLength below.
                announce(t(newState_1 ? 'text.manipulativeLockedWithType' : 'text.manipulativeUnlockedWithType', {
                    manipulative: getManipulativeFriendlyName(),
                    lockType: t('text.lockTypePosition'),
                }));
                break;
            }
            case 'lockEditing': {
                var newState_2 = !editingLock;
                if (isTableTextMenu && tableTextIndex >= 0) {
                    // Table uses tableContentLockToggle for cell-level editing lock
                    customEventHandler.dispatchEvent('tableContentLockToggle', { tableIndex: tableTextIndex });
                    {
                        var lockMsg_1 = editingLock ? t('text.unlockEditingAction') : t('text.lockEditingAction');
                        setAmAnnouncement('');
                        requestAnimationFrame(function () { return setAmAnnouncement(lockMsg_1); });
                        announce(lockMsg_1);
                    }
                    break;
                }
                if (isAnnotationMenu && annotationIndex >= 0) {
                    setAnnotations(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[annotationIndex] = __assign(__assign({}, next[annotationIndex]), { data: __assign(__assign({}, next[annotationIndex].data), { lock: __assign(__assign({}, next[annotationIndex].data.lock), { editing: newState_2 }) }) });
                        return next;
                    });
                }
                else if (isTextMenu && textIndex >= 0) {
                    setText(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[textIndex] = __assign(__assign({}, next[textIndex]), { data: __assign(__assign({}, next[textIndex].data), { lock: __assign(__assign({}, next[textIndex].data.lock), { editing: newState_2 }) }) });
                        return next;
                    });
                }
                else {
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.id === id
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { editing: newState_2 }) }) }) : item;
                        });
                    });
                }
                {
                    var usesPartsTerminology = id.startsWith('arrayModel_') || id.startsWith('coloredTiles_');
                    var lockMsg_2 = newState_2
                        ? t(usesPartsTerminology ? 'text.lockPartsAction' : 'text.lockEditingAction')
                        : t(usesPartsTerminology ? 'text.unlockPartsAction' : 'text.unlockEditingAction');
                    setAmAnnouncement('');
                    requestAnimationFrame(function () { return setAmAnnouncement(lockMsg_2); });
                    // Mirror to the GLOBAL announcer so the message survives the
                    // menu portal unmount that follows the click. Without this,
                    // SRs miss the state-change confirmation for Lock / Unlock
                    // Editing (and Lock / Unlock Parts on Array Models). Same
                    // pattern used by lockToggle + lockLength.
                    announce(t(newState_2
                        ? 'text.manipulativeLockedWithType'
                        : 'text.manipulativeUnlockedWithType', {
                        manipulative: getManipulativeFriendlyName(),
                        lockType: t(usesPartsTerminology ? 'text.lockTypeParts' : 'text.lockTypeEditing'),
                    }));
                }
                break;
            }
            case 'lockToggle': {
                // Simple toggle: flip both positioning and editing together
                var newLockState_1 = !(positionLock || editingLock);
                if (isCardMenu) {
                    // Card lock — update lock inside parent deck's cards[] array
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.type !== 'deck')
                                return item;
                            var hasCard = item.data.cards.some(function (c) { return c.id === id; });
                            if (!hasCard)
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { cards: item.data.cards.map(function (c) {
                                        return c.id === id
                                            ? __assign(__assign({}, c), { lock: {
                                                    positioning: newLockState_1,
                                                    editing: newLockState_1,
                                                } }) : c;
                                    }) }) });
                        });
                    });
                }
                else if (id.startsWith('manipulativeGroup_')) {
                    // Group lock — update lock on all members (dice) with groupId === id
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.groupId === id
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { positioning: newLockState_1, editing: newLockState_1 }) }) }) : item;
                        });
                    });
                }
                else if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type) === 'patternShapes' &&
                    selectedItem.data.groupId) {
                    // Grouped pattern shapes — lock all members
                    var lockGroupId_1 = selectedItem.data.groupId;
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.groupId === lockGroupId_1 && item.data.type === 'patternShapes'
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { positioning: newLockState_1, editing: newLockState_1 }) }) }) : item;
                        });
                    });
                }
                else if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type) === 'linkingCubes' &&
                    selectedItem.data.chainId) {
                    // Linked cubes — lock the entire chain together. Locking one
                    // cube while leaving its chain-mates unlocked produced
                    // inconsistent menu state (positionLock reflects only the
                    // focused cube) and let the user mid-drag-break the chain
                    // because the per-cube lock check at handleMouseDown only
                    // saw the unlocked siblings.
                    var chainLockId_1 = selectedItem.data.chainId;
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.type === 'linkingCubes' &&
                                item.data.chainId === chainLockId_1
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { positioning: newLockState_1, editing: newLockState_1 }) }) }) : item;
                        });
                    });
                }
                else {
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.id === id
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { positioning: newLockState_1, editing: newLockState_1 }) }) }) : item;
                        });
                    });
                }
                announce(t(newLockState_1 ? 'text.manipulativeLocked' : 'text.manipulativeUnlocked', {
                    manipulative: getManipulativeFriendlyName(),
                }));
                // PS shapes and PS groups keep the menu open (matching lockPosition/lockEditing behavior)
                var isPSLockTarget = (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type) === 'patternShapes' ||
                    (id.startsWith('manipulativeGroup_') &&
                        manipulative.some(function (m) { return m.data.groupId === id && m.data.type === 'patternShapes'; }));
                // Clocks (analog + timer) also keep the menu open after lock so the
                // user can chain other clock actions without reopening it.
                var isClockLockTarget = id.startsWith('analog_') || id.startsWith('timer_');
                // LinearPiece: keep the menu open so the user can immediately see the
                // toggled label ("Lock" ↔ "Unlock") and chain other piece actions
                // without reopening the menu — same UX as patternShapes / clocks.
                var isLinearPieceLockTarget = id.startsWith('linearPiece_');
                // Coins & Counters: keep open so user can chain Flip, Configure, etc.
                var isCoinsLockTarget = id.startsWith('coinsAndCounters_');
                if (!isPSLockTarget && !isClockLockTarget && !isLinearPieceLockTarget && !isCoinsLockTarget)
                    closeMenu();
                break;
            }
            case 'lockLength': {
                var jumpBar = manipulative.find(function (m) { return m.data.id === id; });
                var currentLengthLock = (_34 = (_33 = (_32 = jumpBar === null || jumpBar === void 0 ? void 0 : jumpBar.data) === null || _32 === void 0 ? void 0 : _32.lock) === null || _33 === void 0 ? void 0 : _33.length) !== null && _34 !== void 0 ? _34 : false;
                var newLengthState_1 = !currentLengthLock;
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === id
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lock: __assign(__assign({}, item.data.lock), { length: newLengthState_1 }) }) }) : item;
                    });
                });
                announce(t(newLengthState_1
                    ? 'text.manipulativeLockedWithType'
                    : 'text.manipulativeUnlockedWithType', {
                    manipulative: getManipulativeFriendlyName(),
                    lockType: t('text.lockTypeLength'),
                }));
                break;
            }
            case 'toggleSnapToLine': {
                // JumpBar-only. Toggling snap:
                //   • Currently snapped → set snappedPosition = null (unsnap).
                //   • Currently unsnapped → snap onto the parent line. Placement
                //     follows the chain rule: 1st JB on this NL lands at
                //     parentLine.startNum; each subsequent JB lands at the end
                //     of the previous snapped JB on the same NL. The bar's
                //     center/x is also updated so the visual position matches
                //     the new snappedPosition.
                var jumpBar = manipulative.find(function (m) { return m.data.id === id; });
                if (!jumpBar)
                    break;
                var jbData_1 = jumpBar.data;
                var parentNL = manipulative.find(function (m) { return m.data.id === (jbData_1 === null || jbData_1 === void 0 ? void 0 : jbData_1.numberLineId); });
                if (!parentNL)
                    break;
                var parentData = parentNL.data;
                var currentlySnapped = ((_35 = jbData_1.snappedPosition) !== null && _35 !== void 0 ? _35 : null) !== null;
                var oldData = __assign({}, jbData_1);
                if (currentlySnapped) {
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            return item.data.id === id
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedPosition: null }) }) : item;
                        });
                    });
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldData),
                        new: JSON.stringify(__assign(__assign({}, oldData), { snappedPosition: null })),
                    });
                    announce(t('text.unsnappedFromLine'));
                }
                else {
                    // Find the right-most end-position among already-snapped JBs on
                    // the same parent line. Reduce over snappedPosition + unitLength
                    // (length on the line in units). New JB starts right after.
                    var siblings = manipulative.filter(function (m) {
                        var _a, _b, _d, _e;
                        return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'jumpBar' &&
                            ((_b = m.data) === null || _b === void 0 ? void 0 : _b.numberLineId) === jbData_1.numberLineId &&
                            m.data.id !== id &&
                            ((_e = (_d = m.data) === null || _d === void 0 ? void 0 : _d.snappedPosition) !== null && _e !== void 0 ? _e : null) !== null;
                    });
                    var placement = parentData.startNum;
                    for (var _92 = 0, siblings_1 = siblings; _92 < siblings_1.length; _92++) {
                        var s = siblings_1[_92];
                        var sd = s.data;
                        var endU = ((_36 = sd.snappedPosition) !== null && _36 !== void 0 ? _36 : 0) + ((_37 = sd.unitLength) !== null && _37 !== void 0 ? _37 : 0);
                        if (endU > placement)
                            placement = endU;
                    }
                    // Clamp to parent line range (leave room for this JB's length so
                    // it doesn't overshoot — but only if it fits at all).
                    var myLen = (_38 = jbData_1.unitLength) !== null && _38 !== void 0 ? _38 : 0;
                    var lineEnd = parentData.endNum;
                    if (placement + myLen > lineEnd)
                        placement = Math.max(parentData.startNum, lineEnd - myLen);
                    // Inverse of the drag-to-snap formula in NumberLineJumpBar.tsx
                    // (~line 910): place the bar's visual center.x so the snapped
                    // unit position maps to the same px coordinate the drag handler
                    // would have produced. Uses computePxPerUnit + jumpBarScale to
                    // match the JB's rendering pipeline exactly.
                    var pxPerUnit = computePxPerUnit((_39 = parentData.gridSquaresPerUnit) !== null && _39 !== void 0 ? _39 : 1, (_40 = parentData.intervalNum) !== null && _40 !== void 0 ? _40 : 1, !!parentData.scalingToManipulatives);
                    var jumpBarScale = jbData_1.scale || 1;
                    var halfLineW = ((parentData.endNum - parentData.startNum) * pxPerUnit) / 2;
                    var lineStartVisual = parentData.center.x - halfLineW * jumpBarScale;
                    var newCenterX = lineStartVisual + (placement - parentData.startNum) * pxPerUnit * jumpBarScale;
                    // Place the bar ABOVE the parent line. center.y is the bar's
                    // top edge (per JUMP_BAR_BAR_HEIGHT comments) so subtracting
                    // the visual bar height lands the bar's BOTTOM on the line.
                    // This matches the conventional "jump arc above the number
                    // line" orientation; user explicitly requested above, not
                    // straddling the line.
                    var JB_BAR_HEIGHT = 30; // mirrors JUMP_BAR_BAR_HEIGHT in NumberLineJumpBar.tsx
                    var newCenterY = parentData.center.y - JB_BAR_HEIGHT * jumpBarScale;
                    var newData_1 = __assign(__assign({}, jbData_1), { snappedPosition: parseFloat(placement.toFixed(6)), center: { x: newCenterX, y: newCenterY } });
                    setManipulative(function (prev) {
                        return prev.map(function (item) { return (item.data.id === id ? __assign(__assign({}, item), { data: newData_1 }) : item); });
                    });
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldData),
                        new: JSON.stringify(newData_1),
                    });
                    announce(t('text.snappedToLine'));
                }
                closeMenu();
                break;
            }
            case 'moveUp':
            case 'moveDown':
            case 'moveLeft':
            case 'moveRight': {
                if (positionLock)
                    break;
                // WCAG 2.2 SC 2.5.7 keyboard alternative to drag. NumberLine +
                // JumpBar honor the underlying grid: when EITHER the canvas
                // background grid is visible (playerBgPattern !== 'none') OR the
                // parent number line's jumpBarSnapToGrid toggle is on, Move-N
                // steps by GRID_SIZE so the result lands on a grid line.
                // Otherwise (and for CustomTick + every other manipulative)
                // movement stays at the existing free 10 px MOVE_STEP.
                var moveDataType = (_41 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _41 === void 0 ? void 0 : _41.type;
                var step = MOVE_STEP;
                if (moveDataType === 'numberLine') {
                    var gridVisible = (playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none') !== 'none';
                    var nlSnap = !!((_42 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _42 === void 0 ? void 0 : _42.jumpBarSnapToGrid);
                    if (gridVisible || nlSnap)
                        step = GRID_SIZE;
                }
                else if (moveDataType === 'jumpBar') {
                    var jbData_2 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data;
                    var parentNL = manipulative.find(function (m) { return m.data.id === (jbData_2 === null || jbData_2 === void 0 ? void 0 : jbData_2.numberLineId); });
                    var parentNlSnap = !!((_43 = parentNL === null || parentNL === void 0 ? void 0 : parentNL.data) === null || _43 === void 0 ? void 0 : _43.jumpBarSnapToGrid);
                    var gridVisible = (playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none') !== 'none';
                    if (gridVisible || parentNlSnap)
                        step = GRID_SIZE;
                }
                var delta = {
                    moveUp: { x: 0, y: -step },
                    moveDown: { x: 0, y: step },
                    moveLeft: { x: -step, y: 0 },
                    moveRight: { x: step, y: 0 },
                };
                var d_1 = delta[actionId];
                // Only set for the in-frame counter branch below, where the stored
                // center read by the announce block is stale.
                var moveAnchorOverride = null;
                // Ad-hoc multi-select (Ctrl/lasso selection, not a formal group): the
                // keyboard/context-menu Move alternative moved only the single item the
                // menu was opened on, unlike mouse drag where the whole selection moves
                // together. Broadcast the same multiSelectMove* events a drag source
                // dispatches so every other selected item (centrally-managed types via
                // useMultiSelectFollowerEngine, self-managed types via their own
                // listeners) follows this discrete step too.
                var isAdhocMultiSelect_1 = selectedTool.length > 1 && selectedTool.includes(id);
                var broadcastMultiMove = function () {
                    if (!isAdhocMultiSelect_1)
                        return;
                    customEventHandler.dispatchEvent('multiSelectMove', {
                        sourceId: id,
                        deltaX: d_1.x,
                        deltaY: d_1.y,
                    });
                    customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id });
                };
                if (isAdhocMultiSelect_1) {
                    customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
                }
                if (isShadeMenu && shadeIndex >= 0) {
                    // Move a shade
                    var shade = shades[shadeIndex];
                    if (!shade)
                        break;
                    var oldPos = shade.data.position;
                    var newPos_1 = __assign(__assign({}, oldPos), { x: oldPos.x + d_1.x, y: oldPos.y + d_1.y });
                    // Capture the origin on the first Move in this menu session so
                    // the announcement can report cumulative delta.
                    if (!shadeMoveOriginRef.current) {
                        shadeMoveOriginRef.current = { x: oldPos.x, y: oldPos.y };
                    }
                    saveState({
                        type: 'shades',
                        id: String(shadeIndex),
                        propertyName: 'completeData',
                        old: JSON.stringify({ data: __assign({}, shade.data) }),
                        new: JSON.stringify({
                            data: __assign(__assign({}, shade.data), { position: newPos_1 }),
                        }),
                    });
                    setShades(function (prev) {
                        var newState = __spreadArray([], prev, true);
                        newState[shadeIndex] = __assign(__assign({}, newState[shadeIndex]), { data: __assign(__assign({}, newState[shadeIndex].data), { position: newPos_1 }) });
                        return newState;
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    // Match the spinner's movement announcement pattern so JAWS /
                    // VoiceOver hear a consistent "{name} moved {direction N} to
                    // {x}, {y}" message. The N reflects total delta from the
                    // position when the context menu first opened — so repeated
                    // Move clicks accumulate instead of always saying "10".
                    var origin_1 = (_44 = shadeMoveOriginRef.current) !== null && _44 !== void 0 ? _44 : oldPos;
                    var dxTotal = Math.round(newPos_1.x - origin_1.x);
                    var dyTotal = Math.round(newPos_1.y - origin_1.y);
                    var parts = [];
                    if (dyTotal < 0)
                        parts.push("".concat(t('text.up'), " ").concat(Math.abs(dyTotal)));
                    if (dyTotal > 0)
                        parts.push("".concat(t('text.down'), " ").concat(dyTotal));
                    if (dxTotal < 0)
                        parts.push("".concat(t('text.left'), " ").concat(Math.abs(dxTotal)));
                    if (dxTotal > 0)
                        parts.push("".concat(t('text.right'), " ").concat(dxTotal));
                    var dirLabel = parts.length
                        ? parts.join(" ".concat(t('text.and'), " "))
                        : t(actionId === 'moveUp'
                            ? 'text.up'
                            : actionId === 'moveDown'
                                ? 'text.down'
                                : actionId === 'moveLeft'
                                    ? 'text.left'
                                    : 'text.right');
                    {
                        var msg_3 = t('text.manipulativeMoved', {
                            name: t('text.shade'),
                            direction: dirLabel,
                            x: Math.round(newPos_1.x),
                            y: Math.round(newPos_1.y),
                        });
                        setAmAnnouncement('');
                        requestAnimationFrame(function () { return setAmAnnouncement(msg_3); });
                    }
                    break;
                }
                if (isAnnotationMenu && annotationIndex >= 0) {
                    customEventHandler.dispatchEvent('annotationMove', {
                        index: annotationIndex,
                        dx: d_1.x,
                        dy: d_1.y,
                        direction: actionId,
                    });
                    broadcastMultiMove();
                    break;
                }
                if (isTextMenu && textIndex >= 0 && selectedTextItem) {
                    var oldPos = (_46 = (_45 = selectedTextItem.data) === null || _45 === void 0 ? void 0 : _45.position) !== null && _46 !== void 0 ? _46 : { x: 0, y: 0, z: 0 };
                    var newPos = __assign(__assign({}, oldPos), { x: oldPos.x + d_1.x, y: oldPos.y + d_1.y });
                    var updated_3 = __assign(__assign({}, selectedTextItem), { data: __assign(__assign({}, selectedTextItem.data), { position: newPos }) });
                    if (!textMoveOriginRef.current) {
                        textMoveOriginRef.current = { x: oldPos.x, y: oldPos.y };
                    }
                    saveState({
                        type: 'text',
                        id: String(textIndex),
                        propertyName: 'completeData',
                        old: JSON.stringify(selectedTextItem),
                        new: JSON.stringify(updated_3),
                    });
                    setText(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[textIndex] = updated_3;
                        return next;
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    var origin_2 = (_47 = textMoveOriginRef.current) !== null && _47 !== void 0 ? _47 : oldPos;
                    var dxTotal = Math.round(newPos.x - origin_2.x);
                    var dyTotal = Math.round(newPos.y - origin_2.y);
                    var parts = [];
                    if (dyTotal < 0)
                        parts.push("".concat(t('text.up'), " ").concat(Math.abs(dyTotal)));
                    if (dyTotal > 0)
                        parts.push("".concat(t('text.down'), " ").concat(dyTotal));
                    if (dxTotal < 0)
                        parts.push("".concat(t('text.left'), " ").concat(Math.abs(dxTotal)));
                    if (dxTotal > 0)
                        parts.push("".concat(t('text.right'), " ").concat(dxTotal));
                    var dirLabel = parts.length
                        ? parts.join(" ".concat(t('text.and'), " "))
                        : t(actionId === 'moveUp'
                            ? 'text.up'
                            : actionId === 'moveDown'
                                ? 'text.down'
                                : actionId === 'moveLeft'
                                    ? 'text.left'
                                    : 'text.right');
                    {
                        var msg_4 = t('text.manipulativeMoved', {
                            name: t('text.text'),
                            direction: dirLabel,
                            x: Math.round(newPos.x),
                            y: Math.round(newPos.y),
                        });
                        setAmAnnouncement('');
                        requestAnimationFrame(function () { return setAmAnnouncement(msg_4); });
                    }
                    break;
                }
                if (isTableTextMenu && tableTextIndex >= 0 && selectedTableItem) {
                    var oldPos = (_48 = selectedTableItem.data.position) !== null && _48 !== void 0 ? _48 : { x: 0, y: 0 };
                    var newPos = __assign(__assign({}, oldPos), { x: oldPos.x + d_1.x, y: oldPos.y + d_1.y });
                    var updated_4 = __assign(__assign({}, selectedTableItem), { data: __assign(__assign({}, selectedTableItem.data), { position: newPos }) });
                    if (!tableMoveOriginRef.current)
                        tableMoveOriginRef.current = { x: oldPos.x, y: oldPos.y };
                    saveState({ type: 'table', id: String(tableTextIndex), propertyName: 'completeData', old: JSON.stringify(selectedTableItem), new: JSON.stringify(updated_4) });
                    setTable(function (prev) { var nt = __spreadArray([], prev, true); nt[tableTextIndex] = updated_4; return nt; });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    {
                        var origin_3 = (_49 = tableMoveOriginRef.current) !== null && _49 !== void 0 ? _49 : oldPos;
                        var dxTotal = Math.round(newPos.x - origin_3.x);
                        var dyTotal = Math.round(newPos.y - origin_3.y);
                        var parts = [];
                        if (dyTotal < 0)
                            parts.push("".concat(t('text.up'), " ").concat(Math.abs(dyTotal)));
                        if (dyTotal > 0)
                            parts.push("".concat(t('text.down'), " ").concat(dyTotal));
                        if (dxTotal < 0)
                            parts.push("".concat(t('text.left'), " ").concat(Math.abs(dxTotal)));
                        if (dxTotal > 0)
                            parts.push("".concat(t('text.right'), " ").concat(dxTotal));
                        var dirLabel = parts.length
                            ? parts.join(" ".concat(t('text.and'), " "))
                            : t(actionId === 'moveUp' ? 'text.up' : actionId === 'moveDown' ? 'text.down' : actionId === 'moveLeft' ? 'text.left' : 'text.right');
                        var msg_5 = t('text.manipulativeMoved', {
                            name: t('text.table'),
                            direction: dirLabel,
                            x: Math.round(newPos.x),
                            y: Math.round(newPos.y),
                        });
                        setAmAnnouncement('');
                        requestAnimationFrame(function () { return setAmAnnouncement(msg_5); });
                    }
                    break;
                }
                if (isCardMenu) {
                    // Move a card — cards store position inside parent deck's cards[] array
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.type !== 'deck')
                                return item;
                            var cardIdx = item.data.cards.findIndex(function (c) { return c.id === id; });
                            if (cardIdx < 0)
                                return item;
                            var card = item.data.cards[cardIdx];
                            var oldPos = card.position;
                            var newPos = { x: oldPos.x + d_1.x, y: oldPos.y + d_1.y };
                            saveState({
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'position',
                                old: JSON.stringify(oldPos),
                                new: JSON.stringify(newPos),
                            });
                            var newCards = item.data.cards.map(function (c) {
                                return c.id === id ? __assign(__assign({}, c), { position: newPos }) : c;
                            });
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { cards: newCards }) });
                        });
                    });
                    var directionMap_1 = {
                        moveUp: 'up',
                        moveDown: 'down',
                        moveLeft: 'left',
                        moveRight: 'right',
                    };
                    var cardPos = (_50 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _50 === void 0 ? void 0 : _50.position;
                    {
                        var msg_6 = t('text.manipulativeMoved', {
                            name: t('text.playingCard'),
                            direction: directionMap_1[actionId] || actionId,
                            x: cardPos ? Math.round(cardPos.x + d_1.x) : '',
                            y: cardPos ? Math.round(cardPos.y + d_1.y) : '',
                        });
                        setAmAnnouncement('');
                        requestAnimationFrame(function () { return setAmAnnouncement(msg_6); });
                    }
                    broadcastMultiMove();
                    break;
                }
                var finalCenter = void 0;
                var didPSSnap = false;
                var isGroup = id.startsWith('manipulativeGroup_');
                // Snap delta that won't reverse the intended move direction on any axis
                var psDirectionalSnap = function (movedShapes, excludeIds) {
                    var _a = computeGroupSnapDelta(movedShapes, excludeIds, manipulativeRef.current, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none'), snapDx = _a.dx, snapDy = _a.dy;
                    // Only apply snap on an axis if it doesn't reverse the intended movement
                    var finalDx = d_1.x !== 0 && Math.sign(snapDx) !== 0 && Math.sign(d_1.x + snapDx) !== Math.sign(d_1.x)
                        ? 0
                        : snapDx;
                    var finalDy = d_1.y !== 0 && Math.sign(snapDy) !== 0 && Math.sign(d_1.y + snapDy) !== Math.sign(d_1.y)
                        ? 0
                        : snapDy;
                    return { snapDx: finalDx, snapDy: finalDy };
                };
                // If the target is a grouped pattern shape, treat as a group move
                var psGroupId_1 = !isGroup && (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type) === 'patternShapes'
                    ? selectedItem.data.groupId
                    : undefined;
                var psGroupMembers = psGroupId_1
                    ? manipulative.filter(function (m) { return m.data.groupId === psGroupId_1 && m.data.type === 'patternShapes'; })
                    : [];
                // ColoredTiles / LinearPiece groups: the context menu opens on the
                // individual tile id (not the manipulativeGroup_ id), so `isGroup` is
                // false. Detect the focused tile's groupId and move every member of
                // that group together — mirroring the patternShapes group branch.
                var ctGroupId_2 = !isGroup &&
                    ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type) === 'coloredTiles' ||
                        (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data.type) === 'linearPiece')
                    ? selectedItem.data.groupId
                    : undefined;
                var ctGroupMembers = ctGroupId_2
                    ? manipulative.filter(function (m) { return m.data.groupId === ctGroupId_2; })
                    : [];
                if (psGroupMembers.length > 1) {
                    var oldData = psGroupMembers.map(function (m) { return (__assign({}, m)); });
                    var movedShapes = psGroupMembers.map(function (m) {
                        var _a, _b;
                        var md = m.data;
                        return {
                            id: m.data.id,
                            shapeType: md.shapeType,
                            center: { x: md.center.x + d_1.x, y: md.center.y + d_1.y },
                            rotation: (_a = md.rotation) !== null && _a !== void 0 ? _a : 0,
                            scale: (_b = md.scale) !== null && _b !== void 0 ? _b : 1,
                        };
                    });
                    var excludeIds = __spreadArray([psGroupId_1], psGroupMembers.map(function (m) { return m.data.id; }), true);
                    var _93 = psDirectionalSnap(movedShapes, excludeIds), snapDx = _93.snapDx, snapDy = _93.snapDy;
                    var totalDx_1 = d_1.x + snapDx;
                    var totalDy_1 = d_1.y + snapDy;
                    if (snapDx !== 0 || snapDy !== 0) {
                        playAudio('PatternAdd');
                        didPSSnap = true;
                    }
                    if (didPSSnap && psGroupMembers[0]) {
                        var fc = psGroupMembers[0].data.center;
                        finalCenter = { x: fc.x + totalDx_1, y: fc.y + totalDy_1 };
                    }
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.groupId !== psGroupId_1 || item.data.type !== 'patternShapes')
                                return item;
                            var c = item.data.center;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + totalDx_1, y: c.y + totalDy_1 } }) });
                        });
                    });
                    saveState({
                        type: psGroupId_1,
                        id: psGroupId_1.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldData),
                        new: JSON.stringify(psGroupMembers.map(function (m) {
                            var c = m.data.center;
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: { x: c.x + totalDx_1, y: c.y + totalDy_1 } }) });
                        })),
                    });
                }
                else if (ctGroupMembers.length > 1) {
                    // Move every member of a ColoredTiles / LinearPiece group together.
                    // No grid / number-line snapping (matches the mouse group-drag path,
                    // which skips snap for group moves). Locked members stay put.
                    var movable = ctGroupMembers.filter(function (m) { var _a; return !((_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning); });
                    if (movable.length === 0)
                        break;
                    var oldData = movable.map(function (m) { return (__assign({}, m)); });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.groupId !== ctGroupId_2)
                                return item;
                            if ((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                                return item;
                            var c = item.data.center;
                            var gc = item.data.groupCenter;
                            return __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { center: { x: c.x + d_1.x, y: c.y + d_1.y } }), (gc ? { groupCenter: { x: gc.x + d_1.x, y: gc.y + d_1.y } } : {})) });
                        });
                    });
                    saveState({
                        type: ctGroupId_2,
                        id: ctGroupId_2.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldData),
                        new: JSON.stringify(movable.map(function (m) {
                            var c = m.data.center;
                            var gc = m.data.groupCenter;
                            return __assign(__assign({}, m), { data: __assign(__assign(__assign({}, m.data), { center: { x: c.x + d_1.x, y: c.y + d_1.y } }), (gc ? { groupCenter: { x: gc.x + d_1.x, y: gc.y + d_1.y } } : {})) });
                        })),
                    });
                }
                else if (isGroup) {
                    // Move all members of the group
                    var groupMembers = manipulative.filter(function (m) { return m.data.groupId === id; });
                    if (groupMembers.length === 0)
                        break;
                    var oldData = groupMembers.map(function (m) { return (__assign({}, m)); });
                    var isPSGroup = groupMembers.every(function (m) { return m.data.type === 'patternShapes'; });
                    var totalDx_2 = d_1.x;
                    var totalDy_2 = d_1.y;
                    if (isPSGroup) {
                        var movedShapes = groupMembers.map(function (m) {
                            var _a, _b;
                            var md = m.data;
                            return {
                                id: m.data.id,
                                shapeType: md.shapeType,
                                center: { x: md.center.x + d_1.x, y: md.center.y + d_1.y },
                                rotation: (_a = md.rotation) !== null && _a !== void 0 ? _a : 0,
                                scale: (_b = md.scale) !== null && _b !== void 0 ? _b : 1,
                            };
                        });
                        var excludeIds = __spreadArray([id], groupMembers.map(function (m) { return m.data.id; }), true);
                        var _94 = psDirectionalSnap(movedShapes, excludeIds), snapDx = _94.snapDx, snapDy = _94.snapDy;
                        totalDx_2 = d_1.x + snapDx;
                        totalDy_2 = d_1.y + snapDy;
                        if (snapDx !== 0 || snapDy !== 0) {
                            playAudio('PatternAdd');
                            didPSSnap = true;
                        }
                        if (didPSSnap && groupMembers[0]) {
                            var fc = groupMembers[0].data.center;
                            finalCenter = { x: fc.x + totalDx_2, y: fc.y + totalDy_2 };
                        }
                    }
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.groupId === id) {
                                var c = item.data.center;
                                var gc_1 = item.data.groupCenter;
                                return __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { center: { x: c.x + totalDx_2, y: c.y + totalDy_2 } }), (gc_1 ? { groupCenter: { x: gc_1.x + totalDx_2, y: gc_1.y + totalDy_2 } } : {})) });
                            }
                            return item;
                        });
                    });
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldData),
                        new: JSON.stringify(groupMembers.map(function (m) {
                            var c = m.data.center;
                            var gc = m.data.groupCenter;
                            return __assign(__assign({}, m), { data: __assign(__assign(__assign({}, m.data), { center: { x: c.x + totalDx_2, y: c.y + totalDy_2 } }), (gc ? { groupCenter: { x: gc.x + totalDx_2, y: gc.y + totalDy_2 } } : {})) });
                        })),
                    });
                }
                else if (selectedItem) {
                    // LinearPiece intercept: the generic single-manipulative path
                    // below writes `center + d` with no snap and no snap-array
                    // bookkeeping — so a piece moved by context-menu skips snap
                    // detection AND leaves the previously snapped tile pointing at
                    // this piece (the tile re-snaps it back on the next resize /
                    // re-render). Delegate to LinearPiece's shared commit pipeline,
                    // which handles snap, scale adoption, and stale-reference
                    // cleanup the same way the keyboard arrow drop does.
                    if (selectedItem.data.type === 'linearPiece') {
                        customEventHandler.dispatchEvent('linearPieceContextMove', {
                            manipulativeId: id,
                            dx: d_1.x,
                            dy: d_1.y,
                        });
                        broadcastMultiMove();
                        break;
                    }
                    // CustomTick: position is a numeric value along the number line, not
                    // a canvas center. Updating `center` has no visual effect — step by
                    // one interval unit instead, clamped to [startNum, endNum].
                    if (selectedItem.data.type === 'customTick') {
                        var ctData_2 = selectedItem.data;
                        var parentNL = manipulative.find(function (m) { return m.data.id === ctData_2.numberLineId; });
                        var nlData = parentNL === null || parentNL === void 0 ? void 0 : parentNL.data;
                        var stepUnit = (_51 = nlData === null || nlData === void 0 ? void 0 : nlData.intervalNum) !== null && _51 !== void 0 ? _51 : 1;
                        var startNum = (_52 = nlData === null || nlData === void 0 ? void 0 : nlData.startNum) !== null && _52 !== void 0 ? _52 : 0;
                        var endNum = (_53 = nlData === null || nlData === void 0 ? void 0 : nlData.endNum) !== null && _53 !== void 0 ? _53 : 10;
                        var oldPos = (_54 = ctData_2.position) !== null && _54 !== void 0 ? _54 : 0;
                        var newPos_2 = parseFloat(Math.max(startNum, Math.min(endNum, oldPos + (d_1.x < 0 ? -stepUnit : stepUnit))).toFixed(6));
                        if (newPos_2 === oldPos)
                            break;
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'position',
                            old: JSON.stringify(oldPos),
                            new: JSON.stringify(newPos_2),
                        });
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === id
                                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { position: newPos_2 }) })
                                    : item;
                            });
                        });
                        customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                        var isIntNL = ((_55 = nlData === null || nlData === void 0 ? void 0 : nlData.numberType) !== null && _55 !== void 0 ? _55 : 0) === 0;
                        var formattedPos = isIntNL
                            ? String(Math.round(newPos_2))
                            : (newPos_2.toFixed(2).replace(/\.?0+$/, '') || '0');
                        announce(t('text.customTickMoved', { position: formattedPos }));
                        break;
                    }
                    // Move a single manipulative.
                    // Special case: a counter placed in a NumberFrame cell is rendered
                    // at calculateTokenPosition(frame, cellIndex), not at its stored
                    // `center` — so writing a new `center` alone has no visible effect.
                    // Detach the counter from the frame (clear frameId + remove from
                    // frame.tokens) and anchor the move to its current visual position,
                    // mirroring the drag-out-of-frame flow.
                    var sel = selectedItem.data;
                    var isCounterLike = sel.type === 'numberFrameCounter';
                    var selFrameId_1 = isCounterLike ? sel.frameId : undefined;
                    var parentFrameItem = selFrameId_1
                        ? manipulative.find(function (m) { return m.data.type === 'numberFrames' && m.data.id === selFrameId_1; })
                        : undefined;
                    if (parentFrameItem && isCounterLike) {
                        var frame_1 = parentFrameItem.data;
                        var cellIndex = -1;
                        if (frame_1.tokens) {
                            for (var _95 = 0, _96 = Object.entries(frame_1.tokens); _95 < _96.length; _95++) {
                                var _97 = _96[_95], k = _97[0], v = _97[1];
                                if (v === id) {
                                    cellIndex = parseInt(k, 10);
                                    break;
                                }
                            }
                        }
                        var visualPos = cellIndex >= 0 ? calculateTokenPosition(frame_1, cellIndex) : sel.center;
                        var newCenter_1 = {
                            x: visualPos.x + d_1.x,
                            y: visualPos.y + d_1.y,
                        };
                        var newTokens_1 = __assign({}, (frame_1.tokens || {}));
                        if (cellIndex >= 0)
                            delete newTokens_1[cellIndex];
                        saveState([
                            {
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(sel.center),
                                new: JSON.stringify(newCenter_1),
                            },
                            {
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'frameId',
                                old: JSON.stringify(selFrameId_1),
                                new: JSON.stringify(undefined),
                            },
                            {
                                type: frame_1.id,
                                id: frame_1.id,
                                propertyName: 'tokens',
                                old: JSON.stringify(frame_1.tokens || {}),
                                new: JSON.stringify(newTokens_1),
                            },
                        ]);
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                if (item.data.id === id) {
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_1, frameId: undefined, cellIndex: undefined }) });
                                }
                                if (item.data.id === frame_1.id && item.data.type === 'numberFrames') {
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { tokens: newTokens_1 }) });
                                }
                                return item;
                            });
                        });
                        // Override the shared announce block below — the stored center
                        // (what that block reads) is stale for an in-frame counter.
                        moveAnchorOverride = visualPos;
                    }
                    else {
                        // Linking-cube chain: move every member of the chain together so
                        // the chain doesn't get torn apart by moving only the focused cube.
                        var cubeChainId_3 = sel.type === 'linkingCubes' ? sel.chainId : undefined;
                        if (cubeChainId_3) {
                            var chainItems = manipulative.filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === cubeChainId_3; });
                            if (chainItems.length === 0)
                                break;
                            var oldChain = chainItems.map(function (m) { return JSON.parse(JSON.stringify(m)); });
                            setManipulative(function (prev) {
                                return prev.map(function (item) {
                                    if (item.data.type !== 'linkingCubes')
                                        return item;
                                    if (item.data.chainId !== cubeChainId_3)
                                        return item;
                                    var c = item.data.center;
                                    var chainNewCenter = { x: c.x + d_1.x, y: c.y + d_1.y };
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: chainNewCenter, position: chainNewCenter }) });
                                });
                            });
                            saveState({
                                type: 'multipleManipulatives',
                                id: cubeChainId_3,
                                propertyName: 'multipleCompleteData',
                                old: JSON.stringify(oldChain),
                                new: JSON.stringify(chainItems.map(function (m) {
                                    var c = m.data.center;
                                    var chainNewCenter = { x: c.x + d_1.x, y: c.y + d_1.y };
                                    return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: chainNewCenter, position: chainNewCenter }) });
                                })),
                            });
                            break;
                        }
                        // Move a single manipulative
                        var center = sel.center;
                        if (!center)
                            break;
                        var newCenter_2;
                        var isProtractor = selectedItem.data.type === 'protractor';
                        if (isProtractor && playerBgPattern !== 'none') {
                            var nudge = GRID_SIZE * 0.6;
                            var nudged = {
                                x: center.x + Math.sign(d_1.x) * nudge,
                                y: center.y + Math.sign(d_1.y) * nudge,
                            };
                            newCenter_2 =
                                playerBgPattern === 'triangle'
                                    ? snapToTriangleGrid(nudged.x, nudged.y)
                                    : snapToBackgroundGrid(nudged.x, nudged.y);
                        }
                        else if (selectedItem.data.type === 'patternShapes') {
                            var sd = selectedItem.data;
                            var moved = { x: center.x + d_1.x, y: center.y + d_1.y };
                            var snapped = computePatternShapeSnap(moved.x, moved.y, sd.shapeType, (_56 = sd.rotation) !== null && _56 !== void 0 ? _56 : 0, (_57 = sd.scale) !== null && _57 !== void 0 ? _57 : 1, [id], manipulativeRef.current, playerBgPattern !== null && playerBgPattern !== void 0 ? playerBgPattern : 'none');
                            // Only apply snap per axis if it doesn't reverse the intended movement
                            var snapDx = snapped.x - moved.x;
                            var snapDy = snapped.y - moved.y;
                            var finalSnapDx = d_1.x !== 0 && Math.sign(snapDx) !== 0 && Math.sign(d_1.x + snapDx) !== Math.sign(d_1.x)
                                ? 0
                                : snapDx;
                            var finalSnapDy = d_1.y !== 0 && Math.sign(snapDy) !== 0 && Math.sign(d_1.y + snapDy) !== Math.sign(d_1.y)
                                ? 0
                                : snapDy;
                            newCenter_2 = { x: moved.x + finalSnapDx, y: moved.y + finalSnapDy };
                            if (finalSnapDx !== 0 || finalSnapDy !== 0) {
                                playAudio('PatternAdd');
                                didPSSnap = true;
                            }
                        }
                        else {
                            newCenter_2 = { x: center.x + d_1.x, y: center.y + d_1.y };
                        }
                        finalCenter = newCenter_2;
                        if (newCenter_2.x !== center.x || newCenter_2.y !== center.y) {
                            saveState({
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(center),
                                new: JSON.stringify(newCenter_2),
                            });
                            setManipulative(function (prev) {
                                return prev.map(function (item) {
                                    return item.data.id === id
                                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_2 }) }) : item;
                                });
                            });
                        }
                    }
                }
                var directionMap = {
                    moveUp: 'up',
                    moveDown: 'down',
                    moveLeft: 'left',
                    moveRight: 'right',
                };
                var typeNameMap = {
                    deck_pool: 'text.manipulativeCardDeck',
                    analog: 'text.manipulativeClock',
                    clock: 'text.manipulativeTimer',
                    spinner: 'text.manipulativeSpinner',
                    patternShapes: 'text.patternShape',
                    arrayModel: 'text.arrayModels',
                };
                // For groups, use a more specific label when the group's members are all
                // pattern shapes; otherwise fall back to the generic "Select Group".
                var groupTypeName = t('text.selectGroup');
                if (isGroup) {
                    var groupMembers = manipulativeRef.current.filter(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.groupId) === id; });
                    if (groupMembers.length > 0 &&
                        groupMembers.every(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes'; })) {
                        groupTypeName = t('text.patternBlockGroup');
                    }
                }
                var typeName = isGroup
                    ? groupTypeName
                    : t(typeNameMap[selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.type] || "text.".concat(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.type)) ||
                        (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.type);
                var dir = directionMap[actionId] || actionId;
                // Compute new position — try multiple paths to find center.
                // moveAnchorOverride is set for in-frame counters where the stored
                // `center` is stale (the counter renders from calculateTokenPosition).
                var centerSources = [
                    moveAnchorOverride,
                    (_58 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _58 === void 0 ? void 0 : _58.center,
                    (_59 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _59 === void 0 ? void 0 : _59.position,
                ];
                var foundCenter = centerSources.find(function (c) { return (c === null || c === void 0 ? void 0 : c.x) !== undefined && (c === null || c === void 0 ? void 0 : c.y) !== undefined; });
                if (didPSSnap) {
                    announce(finalCenter
                        ? t('text.patternShapeSnappedAt', {
                            name: typeName,
                            x: Math.round(finalCenter.x),
                            y: Math.round(finalCenter.y),
                        })
                        : t('text.patternShapeSnapped'));
                }
                else {
                    var moveMsg_1;
                    if (foundCenter) {
                        var announcedPos = finalCenter !== null && finalCenter !== void 0 ? finalCenter : {
                            x: foundCenter.x + d_1.x,
                            y: foundCenter.y + d_1.y,
                        };
                        moveMsg_1 = t('text.manipulativeMoved', {
                            name: typeName,
                            direction: dir,
                            x: Math.round(announcedPos.x),
                            y: Math.round(announcedPos.y),
                        });
                    }
                    else {
                        // Last resort: find center from DOM element
                        var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                        if (el) {
                            var rect = el.getBoundingClientRect();
                            moveMsg_1 = t('text.manipulativeMoved', {
                                name: typeName,
                                direction: dir,
                                x: Math.round(rect.left + rect.width / 2),
                                y: Math.round(rect.top + rect.height / 2),
                            });
                        }
                        else {
                            moveMsg_1 = "".concat(typeName, " ").concat(t('text.moved'), " ").concat(dir);
                        }
                    }
                    setAmAnnouncement('');
                    requestAnimationFrame(function () { return setAmAnnouncement(moveMsg_1); });
                }
                // Skip the broadcast when this move already went through a formal-group
                // branch (patternShapes group, coloredTiles/linearPiece group, or
                // manipulativeGroup) above — those already moved every member of the
                // group directly, so re-broadcasting would double-apply the delta to
                // any self-managed-type member that also listens for multiSelectMove
                // on its own. Ad-hoc (non-grouped) multi-selects still need it.
                if (!(psGroupMembers.length > 1 || ctGroupMembers.length > 1 || isGroup)) {
                    broadcastMultiMove();
                }
                break;
            }
            case 'group':
            case 'ungroup':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'mergeSelectedDice': {
                var diceIds = __spreadArray([contextMenu.manipulativeId], Array.from(checkedDiceIds), true);
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'groupDiceFromMenu',
                    panel: 'bottomPanel',
                    diceIds: diceIds,
                });
                announce(t('text.diceGrouped', { count: diceIds.length }));
                closeMenu();
                break;
            }
            case 'addDiceToGroup': {
                // Add ungrouped dice to the existing group
                var existingGroupId = contextMenu.manipulativeId.startsWith('manipulativeGroup_')
                    ? contextMenu.manipulativeId
                    : (_60 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _60 === void 0 ? void 0 : _60.groupId;
                if (existingGroupId) {
                    var newDiceIds = Array.from(checkedDiceIds);
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'groupDiceFromMenu',
                        panel: 'bottomPanel',
                        diceIds: __spreadArray([existingGroupId], newDiceIds, true),
                    });
                    announce(t('text.diceGrouped', { count: newDiceIds.length + 1 }));
                }
                closeMenu();
                break;
            }
            case 'ungroupDice': {
                var diceItem = manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; });
                var groupIdToUngroup = (_61 = diceItem === null || diceItem === void 0 ? void 0 : diceItem.data) === null || _61 === void 0 ? void 0 : _61.groupId;
                if (groupIdToUngroup) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'ungroupDiceFromMenu',
                        panel: 'bottomPanel',
                        groupId: groupIdToUngroup,
                    });
                    announce(t('text.diceUngrouped'));
                }
                closeMenu();
                break;
            }
            case 'mergeSelectedPatterns': {
                var patternIds = __spreadArray([contextMenu.manipulativeId], Array.from(checkedPatternIds), true);
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'groupDiceFromMenu',
                    panel: 'bottomPanel',
                    diceIds: patternIds,
                });
                announce(t('text.diceGrouped', { count: patternIds.length }));
                closeMenu();
                break;
            }
            case 'addPatternToGroup': {
                var existingGroupId = (_62 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _62 === void 0 ? void 0 : _62.groupId;
                if (existingGroupId) {
                    var newIds = Array.from(checkedPatternIds);
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'groupDiceFromMenu',
                        panel: 'bottomPanel',
                        diceIds: __spreadArray([existingGroupId], newIds, true),
                    });
                    announce(t('text.diceGrouped', { count: newIds.length + 1 }));
                }
                closeMenu();
                break;
            }
            case 'ungroupPattern': {
                var patternItem = manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; });
                var groupIdToUngroup = (_63 = patternItem === null || patternItem === void 0 ? void 0 : patternItem.data) === null || _63 === void 0 ? void 0 : _63.groupId;
                if (groupIdToUngroup) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'ungroupDiceFromMenu',
                        panel: 'bottomPanel',
                        groupId: groupIdToUngroup,
                    });
                    announce(t('text.diceUngrouped'));
                }
                closeMenu();
                break;
            }
            case 'groupNmpTo': {
                // Open the keyboard target picker so the user can choose which
                // other NMP piece (or NMP group) to merge with. The picker
                // expects a piece id, so resolve a manipulativeGroup_* focus to
                // its first NMP member — the picker then derives the source's
                // groupId from that member and pulls every group sibling along
                // when the user confirms a target.
                var focusedPieceId = contextMenu.manipulativeId;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    var member = manipulative.find(function (m) {
                        return (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece') &&
                            m.data.groupId === contextMenu.manipulativeId;
                    });
                    if (member)
                        focusedPieceId = (_64 = member.data.id) !== null && _64 !== void 0 ? _64 : focusedPieceId;
                }
                customEventHandler.dispatchEvent('openNmpGroupPicker', {
                    focusedPieceId: focusedPieceId,
                });
                closeMenu();
                break;
            }
            case 'ungroupNmp': {
                // Resolve the focused entity's groupId and route through the
                // shared ungroupDiceFromMenu handler. When the user opened the
                // menu on the group overlay itself, manipulativeId IS the
                // groupId; otherwise look up the focused piece's groupId.
                var groupIdToUngroup_1;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    groupIdToUngroup_1 = contextMenu.manipulativeId;
                }
                else {
                    var target = manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; });
                    groupIdToUngroup_1 = (_65 = target === null || target === void 0 ? void 0 : target.data) === null || _65 === void 0 ? void 0 : _65.groupId;
                }
                if (groupIdToUngroup_1) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'ungroupDiceFromMenu',
                        panel: 'bottomPanel',
                        groupId: groupIdToUngroup_1,
                    });
                }
                // After ungrouping, the manipulativeGroup_ overlay no longer
                // exists in the DOM, so closeMenu's normal focus-by-manipId
                // lookup would find nothing. Redirect focus to the first member
                // (lowest groupPosition) — its own tile survives ungroup as a
                // standalone piece at the same id.
                var firstNmpMember = groupIdToUngroup_1
                    ? (_66 = manipulative
                        .filter(function (m) {
                        return (m.data.type === 'numberPiece' || m.data.type === 'moneyPiece') &&
                            m.data.groupId === groupIdToUngroup_1;
                    })
                        .sort(function (a, b) { var _a, _b; return ((_a = a.data.groupPosition) !== null && _a !== void 0 ? _a : 0) - ((_b = b.data.groupPosition) !== null && _b !== void 0 ? _b : 0); })[0]) === null || _66 === void 0 ? void 0 : _66.data.id
                    : undefined;
                closeMenu(firstNmpMember);
                break;
            }
            case 'groupFractionTilesTo': {
                // Open the keyboard target picker so the user can choose which
                // other fraction tile (or fraction-tile group) to merge with. The
                // picker expects a tile id, so resolve a manipulativeGroup_* focus
                // to its first fraction-tile member — the picker then derives the
                // source's groupId from that member and pulls every group sibling
                // along when the user confirms a target.
                var focusedTileId = contextMenu.manipulativeId;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    var member = manipulative.find(function (m) {
                        return m.data.type === 'fractionTiles' &&
                            m.data.groupId === contextMenu.manipulativeId;
                    });
                    if (member)
                        focusedTileId = (_67 = member.data.id) !== null && _67 !== void 0 ? _67 : focusedTileId;
                }
                customEventHandler.dispatchEvent('openFractionTilesGroupPicker', {
                    focusedPieceId: focusedTileId,
                });
                closeMenu();
                break;
            }
            case 'ungroupFractionTiles': {
                // Resolve the focused entity's groupId and route through the
                // shared ungroupDiceFromMenu handler. When the user opened the
                // menu on the group overlay itself, manipulativeId IS the
                // groupId; otherwise look up the focused tile's groupId.
                var groupIdToUngroup_2;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    groupIdToUngroup_2 = contextMenu.manipulativeId;
                }
                else {
                    var target = manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; });
                    groupIdToUngroup_2 = (_68 = target === null || target === void 0 ? void 0 : target.data) === null || _68 === void 0 ? void 0 : _68.groupId;
                }
                if (groupIdToUngroup_2) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'ungroupDiceFromMenu',
                        panel: 'bottomPanel',
                        groupId: groupIdToUngroup_2,
                    });
                }
                // After ungrouping, the manipulativeGroup_ overlay no longer
                // exists in the DOM, so closeMenu's normal focus-by-manipId
                // lookup would find nothing. Redirect focus to the first member
                // (lowest groupPosition) — its own tile survives ungroup as a
                // standalone tile at the same id.
                var firstFtMember = groupIdToUngroup_2
                    ? (_69 = manipulative
                        .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === groupIdToUngroup_2; })
                        .sort(function (a, b) { var _a, _b; return ((_a = a.data.groupPosition) !== null && _a !== void 0 ? _a : 0) - ((_b = b.data.groupPosition) !== null && _b !== void 0 ? _b : 0); })[0]) === null || _69 === void 0 ? void 0 : _69.data.id
                    : undefined;
                closeMenu(firstFtMember);
                break;
            }
            case 'groupCcTo': {
                // For the group overlay (manipulativeGroup_xxx), resolve a member counter ID
                // so the picker can derive focusedCc.groupId and merge the whole group.
                var ccIdToFocus = contextMenu.manipulativeId;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    var member = manipulative.find(function (m) {
                        return m.data.type === 'coinsAndCounters' &&
                            m.data.groupId === contextMenu.manipulativeId;
                    });
                    if (member)
                        ccIdToFocus = (_70 = member.data.id) !== null && _70 !== void 0 ? _70 : ccIdToFocus;
                }
                customEventHandler.dispatchEvent('openCcGroupPicker', {
                    focusedCcId: ccIdToFocus,
                });
                closeMenu();
                break;
            }
            case 'groupPatternShapeTo': {
                // For the group overlay (manipulativeGroup_xxx), resolve a member shape ID
                // so the picker can derive focusedShape.groupId and merge the whole group.
                var psIdToFocus = contextMenu.manipulativeId;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    var member = manipulative.find(function (m) {
                        return m.data.type === 'patternShapes' &&
                            m.data.groupId === contextMenu.manipulativeId;
                    });
                    if (member)
                        psIdToFocus = (_71 = member.data.id) !== null && _71 !== void 0 ? _71 : psIdToFocus;
                }
                customEventHandler.dispatchEvent('openPatternShapeLinkPicker', {
                    focusedShapeId: psIdToFocus,
                });
                closeMenu();
                break;
            }
            case 'groupColoredTilesTo': {
                // Activate the on-canvas ColoredTiles group picker. For the group
                // overlay (manipulativeGroup_xxx), resolve a member tile id so the
                // picker can derive the source's groupId and merge the whole group.
                var tileIdToFocus = contextMenu.manipulativeId;
                if (contextMenu.manipulativeId.startsWith('manipulativeGroup_')) {
                    var member = manipulative.find(function (m) {
                        return m.data.type === 'coloredTiles' &&
                            m.data.groupId === contextMenu.manipulativeId;
                    });
                    if (member)
                        tileIdToFocus = (_72 = member.data.id) !== null && _72 !== void 0 ? _72 : tileIdToFocus;
                }
                customEventHandler.dispatchEvent('openColoredTilesLinkPicker', {
                    focusedTileId: tileIdToFocus,
                });
                closeMenu();
                break;
            }
            case 'snapColoredTilesTo': {
                // Open the picker in snap mode — pick another tile to merge this one into.
                customEventHandler.dispatchEvent('openColoredTilesLinkPicker', {
                    focusedTileId: contextMenu.manipulativeId,
                    mode: 'snap',
                });
                closeMenu();
                break;
            }
            case 'ungroupColoredTiles': {
                var tileItem = manipulative.find(function (m) { return m.data.id === contextMenu.manipulativeId; });
                var groupIdToUngroup = (_73 = tileItem === null || tileItem === void 0 ? void 0 : tileItem.data) === null || _73 === void 0 ? void 0 : _73.groupId;
                if (groupIdToUngroup) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'ungroupDiceFromMenu',
                        panel: 'bottomPanel',
                        groupId: groupIdToUngroup,
                    });
                    announce(t('text.coloredTilesUngrouped'));
                }
                closeMenu();
                break;
            }
            case 'snapLinearPieceTo': {
                // Open the on-canvas snap picker for the focused linear piece.
                customEventHandler.dispatchEvent('openLinearPieceSnapPicker', {
                    focusedPieceId: contextMenu.manipulativeId,
                });
                closeMenu();
                break;
            }
            case 'unsnapLinearPiece': {
                customEventHandler.dispatchEvent('unsnapLinearPiece', {
                    pieceId: contextMenu.manipulativeId,
                });
                closeMenu();
                break;
            }
            case 'spinWheel':
                customEventHandler.dispatchEvent('spinWheel', { id: id });
                announce(t('text.manipulativeSpun'));
                closeMenu();
                break;
            case 'rollDice':
                customEventHandler.dispatchEvent('rollDice', {
                    id: id,
                    isGroup: id.startsWith('manipulativeGroup_'),
                });
                announce(t('text.rollDice'));
                closeMenu();
                break;
            case 'linkingCubeDissolveChain': {
                // LinkingCubes base-cube listens by chainId and unlinks every member.
                var chainId = (_74 = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) === null || _74 === void 0 ? void 0 : _74.chainId;
                if (chainId) {
                    customEventHandler.dispatchEvent('linkingCubeDissolveChain', { chainId: chainId });
                }
                closeMenu();
                break;
            }
            // ── NumberRack-specific actions ──
            // All three (addRow / removeRow / reverseBeads) keep the menu open so
            // the user can perform multiple rack operations without reopening.
            case 'addRow':
            case 'removeRow':
            case 'reverseBeads':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                break;
            // NumberFrame: 5×5 grid overlay toggle — forwards to the same
            // bottomPanel event the left-toolbox button dispatches.
            case 'show5x5Grid':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                break;
            // NumberLine extent actions — forwarded to the NL's bottomPanel listener.
            case 'nlExtendLeft':
            case 'nlShrinkLeft':
            case 'nlExtendRight':
            case 'nlShrinkRight':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: actionId,
                    panel: 'bottomPanel',
                });
                break;
            // ── NumberFrame-specific action ──
            // "Token Settings" moves focus to the top token-setting-btn in the
            // right-side drawer. Bypasses closeMenu()'s focus-restore because we
            // want focus to land on the token-setting-btn, not back on the frame.
            // Only fires for numberFrame menus (typeAction gated on dataType).
            case 'openTokenSettings': {
                // Minimal cleanup mirroring closeMenu()'s state resets (sans focus
                // restore).
                if (choralActiveRef.current) {
                    customEventHandler.dispatchEvent('choralDrawer', {
                        id: 'choralReset',
                    });
                    choralActiveRef.current = false;
                    setChoralSpeedLocal(null);
                    setChoralDirLocal(null);
                }
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                // Ensure the drawer is open so the button isn't inside an inert
                // subtree when we try to focus it.
                customEventHandler.dispatchEvent('toggleRightSidePanel', {
                    isOpen: true,
                });
                // Let React flush the drawer-open re-render before focusing.
                setTimeout(function () {
                    var btn = document.querySelector('.tokenSettingBtnWrapper.token-setting-top .token-setting-btn');
                    btn === null || btn === void 0 ? void 0 : btn.focus();
                }, 120);
                break;
            }
            case 'linkingCubeStartLink':
                // LinkingCubes.tsx enters its keyboard target-picker mode and
                // handles candidate cycling + snap/link.
                customEventHandler.dispatchEvent('linkingCubeStartLink', { id: id });
                closeMenu();
                break;
            // ── Card/Deck-specific actions ──
            case 'flipCard':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'flip',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'stackCard':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'stack',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'dealCard':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'dealCard',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'shuffleDeck':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'suffle',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            // ── Shade-specific actions ──
            case 'shadeIncreaseWidth':
            case 'shadeDecreaseWidth':
            case 'shadeIncreaseHeight':
            case 'shadeDecreaseHeight': {
                if (!isShadeMenu || shadeIndex < 0 || positionLock)
                    break;
                var shade = shades[shadeIndex];
                if (!shade)
                    break;
                var resizeStep = 10;
                var minDim = 40;
                var oldDims = shade.data.dimensions;
                var newDims_1 = oldDims;
                if (actionId === 'shadeIncreaseWidth') {
                    newDims_1 = __assign(__assign({}, oldDims), { width: oldDims.width + resizeStep });
                }
                else if (actionId === 'shadeDecreaseWidth') {
                    newDims_1 = __assign(__assign({}, oldDims), { width: Math.max(minDim, oldDims.width - resizeStep) });
                }
                else if (actionId === 'shadeIncreaseHeight') {
                    newDims_1 = __assign(__assign({}, oldDims), { height: oldDims.height + resizeStep });
                }
                else if (actionId === 'shadeDecreaseHeight') {
                    newDims_1 = __assign(__assign({}, oldDims), { height: Math.max(minDim, oldDims.height - resizeStep) });
                }
                if (newDims_1 === oldDims)
                    break;
                saveState({
                    type: 'shades',
                    id: String(shadeIndex),
                    propertyName: 'completeData',
                    old: JSON.stringify({ data: __assign({}, shade.data) }),
                    new: JSON.stringify({
                        data: __assign(__assign({}, shade.data), { dimensions: newDims_1 }),
                    }),
                });
                setShades(function (prev) {
                    var newState = __spreadArray([], prev, true);
                    newState[shadeIndex] = __assign(__assign({}, newState[shadeIndex]), { data: __assign(__assign({}, newState[shadeIndex].data), { dimensions: newDims_1 }) });
                    return newState;
                });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                var isWidthAction = actionId === 'shadeIncreaseWidth' || actionId === 'shadeDecreaseWidth';
                var announceKey = actionId === 'shadeIncreaseWidth'
                    ? 'text.shadeWidthIncreased'
                    : actionId === 'shadeDecreaseWidth'
                        ? 'text.shadeWidthDecreased'
                        : actionId === 'shadeIncreaseHeight'
                            ? 'text.shadeHeightIncreased'
                            : 'text.shadeHeightDecreased';
                announce(t(announceKey, {
                    value: Math.round(isWidthAction ? newDims_1.width : newDims_1.height),
                }));
                break;
            }
            case 'shadeCopy': {
                if (!isShadeMenu || shadeIndex < 0)
                    break;
                var shadeId = contextMenu.manipulativeId;
                if (!selectedTool.includes(shadeId)) {
                    setSelectedTool([shadeId]);
                }
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'duplicate',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            }
            case 'shadeLock': {
                if (!isShadeMenu || shadeIndex < 0)
                    break;
                var shade = shades[shadeIndex];
                if (!shade)
                    break;
                // Shade uses positioning as its single lock flag.
                var wasLocked = positionLock;
                var newLock_1 = __assign(__assign({}, shade.data.lock), { positioning: !wasLocked });
                var oldData = __assign({}, shade.data);
                setShades(function (prev) {
                    var newState = __spreadArray([], prev, true);
                    newState[shadeIndex] = __assign(__assign({}, newState[shadeIndex]), { data: __assign(__assign({}, newState[shadeIndex].data), { lock: newLock_1 }) });
                    return newState;
                });
                saveState({
                    type: 'shades',
                    id: String(shadeIndex),
                    propertyName: 'completeData',
                    old: JSON.stringify({ data: oldData }),
                    new: JSON.stringify({ data: __assign(__assign({}, oldData), { lock: newLock_1 }) }),
                });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                announce(wasLocked ? t('text.shadeUnlocked') : t('text.shadeLocked'));
                break;
            }
            case 'shadeVisibility': {
                if (!isShadeMenu || shadeIndex < 0)
                    break;
                var shade = shades[shadeIndex];
                if (!shade)
                    break;
                var oldRevealed_1 = shade.data.revealed;
                setShades(function (prev) {
                    var newState = __spreadArray([], prev, true);
                    newState[shadeIndex] = __assign(__assign({}, newState[shadeIndex]), { data: __assign(__assign({}, newState[shadeIndex].data), { revealed: !oldRevealed_1 }) });
                    return newState;
                });
                saveState({
                    type: 'shades',
                    id: String(shadeIndex),
                    propertyName: 'completeData',
                    old: JSON.stringify({
                        data: __assign(__assign({}, shade.data), { revealed: oldRevealed_1 }),
                    }),
                    new: JSON.stringify({
                        data: __assign(__assign({}, shade.data), { revealed: !oldRevealed_1 }),
                    }),
                });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                announce(oldRevealed_1 ? t('text.shadeHidden') : t('text.shadeRevealed'));
                closeMenu();
                break;
            }
            case 'shadeDelete': {
                if (!isShadeMenu || shadeIndex < 0)
                    break;
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'delete',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            }
            case 'increaseWidth':
            case 'decreaseWidth':
            case 'increaseHeight':
            case 'decreaseHeight': {
                if (isTextMenu && textIndex >= 0 && selectedTextItem) {
                    if (editingLock)
                        break;
                    var SIZE_STEP_1 = 20;
                    var isWidth_1 = actionId === 'increaseWidth' || actionId === 'decreaseWidth';
                    var isIncrease_1 = actionId === 'increaseWidth' || actionId === 'increaseHeight';
                    var dir_1 = isIncrease_1 ? SIZE_STEP_1 : -SIZE_STEP_1;
                    var updatedData = isWidth_1
                        ? { lineWidth: Math.max(50, ((_76 = (_75 = selectedTextItem.data) === null || _75 === void 0 ? void 0 : _75.lineWidth) !== null && _76 !== void 0 ? _76 : 200) + dir_1) }
                        : { height: Math.max(30, ((_78 = (_77 = selectedTextItem.data) === null || _77 === void 0 ? void 0 : _77.height) !== null && _78 !== void 0 ? _78 : 100) + dir_1) };
                    var updated_5 = __assign(__assign({}, selectedTextItem), { data: __assign(__assign({}, selectedTextItem.data), updatedData) });
                    saveState({
                        type: 'text',
                        id: String(textIndex),
                        propertyName: 'completeData',
                        old: JSON.stringify(selectedTextItem),
                        new: JSON.stringify(updated_5),
                    });
                    setText(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[textIndex] = updated_5;
                        return next;
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    break;
                }
                if (!isAnnotationMenu || annotationIndex < 0 || positionLock)
                    break;
                var ann = annotations[annotationIndex];
                if (!ann)
                    break;
                var SIZE_STEP = 20;
                var isWidth = actionId === 'increaseWidth' || actionId === 'decreaseWidth';
                var isIncrease = actionId === 'increaseWidth' || actionId === 'increaseHeight';
                var dir = isIncrease ? SIZE_STEP : -SIZE_STEP;
                var newAnn_2;
                if (ann.type === 'line' && ann.data.points && ann.data.points.length >= 2) {
                    // Line: extend/shrink from end point (keep start fixed)
                    var pts = ann.data.points;
                    var dx = pts[1].x - pts[0].x;
                    var dy = pts[1].y - pts[0].y;
                    var len = Math.sqrt(dx * dx + dy * dy);
                    var newLen = Math.max(20, len + dir);
                    var ux = dx / (len || 1);
                    var uy = dy / (len || 1);
                    var newPts = [
                        __assign({}, pts[0]),
                        { x: pts[0].x + ux * newLen, y: pts[0].y + uy * newLen },
                    ];
                    newAnn_2 = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { points: newPts }) });
                }
                else if (ann.data.dimensions && ann.data.center) {
                    // Shape: expand from right side (width) or bottom side (height)
                    var dims = ann.data.dimensions;
                    var center = ann.data.center;
                    if (isWidth) {
                        var newW = Math.max(20, (dims.width || 50) + dir);
                        var shift = (newW - (dims.width || 50)) / 2;
                        newAnn_2 = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { center: __assign(__assign({}, center), { x: center.x + shift }), dimensions: __assign(__assign({}, dims), { width: newW }) }) });
                    }
                    else {
                        var newH = Math.max(20, (dims.height || 50) + dir);
                        var shift = (newH - (dims.height || 50)) / 2;
                        newAnn_2 = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { center: __assign(__assign({}, center), { y: center.y + shift }), dimensions: __assign(__assign({}, dims), { height: newH }) }) });
                    }
                }
                if (newAnn_2) {
                    saveState({
                        type: 'annotations',
                        id: String(annotationIndex),
                        propertyName: 'completeData',
                        old: JSON.stringify(ann),
                        new: JSON.stringify(newAnn_2),
                    });
                    setAnnotations(function (prev) {
                        var next = __spreadArray([], prev, true);
                        next[annotationIndex] = newAnn_2;
                        return next;
                    });
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                    var prop = isWidth ? t('text.width') : t('text.height');
                    var sizeW = void 0, sizeH = void 0;
                    if (newAnn_2.type === 'line' && newAnn_2.data.points) {
                        var pts = newAnn_2.data.points;
                        sizeW = Math.round(Math.abs(pts[1].x - pts[0].x));
                        sizeH = Math.round(Math.abs(pts[1].y - pts[0].y));
                    }
                    else {
                        sizeW = Math.round((_80 = (_79 = newAnn_2.data.dimensions) === null || _79 === void 0 ? void 0 : _79.width) !== null && _80 !== void 0 ? _80 : 0);
                        sizeH = Math.round((_82 = (_81 = newAnn_2.data.dimensions) === null || _81 === void 0 ? void 0 : _81.height) !== null && _82 !== void 0 ? _82 : 0);
                    }
                    var sizeKey = ann.type === 'line' ? 'text.annotationLineSizeChanged' : 'text.annotationSizeChanged';
                    announce(t(sizeKey, {
                        name: getAnnotationTypeName(),
                        property: prop,
                        direction: isIncrease ? t('text.increased') : t('text.decreased'),
                        width: sizeW,
                        height: sizeH,
                    }));
                }
                break;
            }
            case 'thicknessExtraFine':
            case 'thicknessFine':
            case 'thicknessMedium':
            case 'thicknessBroad': {
                if (!isAnnotationMenu || annotationIndex < 0)
                    break;
                // Map to highlighterCollapsible index (same as bottom panel)
                var thicknessIndexMap = {
                    thicknessExtraFine: 0,
                    thicknessFine: 1,
                    thicknessMedium: 2,
                    thicknessBroad: 3,
                };
                var thicknessLabelMap = {
                    thicknessExtraFine: t('text.thicknessExtraFine'),
                    thicknessFine: t('text.thicknessFine'),
                    thicknessMedium: t('text.thicknessMedium'),
                    thicknessBroad: t('text.thicknessBroad'),
                };
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: "highlighterCollapsible_".concat(thicknessIndexMap[actionId]),
                    panel: 'bottomPanel',
                });
                announce(t('text.thicknessChanged', {
                    type: getAnnotationTypeName(),
                    name: thicknessLabelMap[actionId],
                }));
                break;
            }
            case 'annotationDuplicate':
                announce(t('text.annotationDuplicated', { name: getAnnotationTypeName() }));
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'duplicate',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'annotationDelete':
                announce(t('text.annotationDeleted', { name: getAnnotationTypeName() }));
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'delete',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'textDuplicate':
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'duplicate',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'addLink':
                // Tell BottomPanel to surface its inline URL input.
                customEventHandler.dispatchEvent('openLinkPopup', {});
                announce(t('text.linkInputOpened'));
                closeMenu();
                // closeMenu schedules a 50ms focus restore back to the manipulative,
                // which would steal focus away from the link input that just
                // mounted with autoFocus. Re-focus after that restore runs.
                setTimeout(function () {
                    var linkInputEl = document.getElementById('linkInput_input');
                    linkInputEl === null || linkInputEl === void 0 ? void 0 : linkInputEl.focus();
                }, 100);
                break;
            case 'textDelete':
                if (positionLock || editingLock)
                    break;
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'delete',
                    panel: 'bottomPanel',
                });
                closeMenu();
                break;
            case 'addCustomLabel': {
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                setTimeout(function () {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'focusCustomLabel',
                        data: { tileId: id },
                    });
                }, 0);
                break;
            }
            case 'addFractionTile':
                // parent submenu item — no action needed
                break;
            case 'splitFractionTile': {
                var tileItem = manipulative.find(function (m) { return m.data.id === id && m.data.type === 'fractionTiles'; });
                if (!tileItem)
                    break;
                if (!selectedTool.includes(id) || selectedTool.length > 1) {
                    setSelectedTool([id]);
                }
                var tileData = tileItem.data;
                var fractionType = ((_83 = tileData.gluedFrom) === null || _83 === void 0 ? void 0 : _83.length) > 0
                    ? sumAndReduceFractions(tileData.gluedFrom.map(function (f) { return f.fractionSize; }))
                    : tileData.fractionSize;
                var numerator_1 = (_85 = (_84 = parseFraction(fractionType)) === null || _84 === void 0 ? void 0 : _84.numerator) !== null && _85 !== void 0 ? _85 : 1;
                var denominator_1 = getDenominatorFromFractionType(fractionType);
                // Close menu without restoring focus so the split modal's FocusLock
                // captures the clean active element (same pattern as addTile/glue).
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                setTimeout(function () {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'splitFractionTile',
                        data: { currentDenominator: denominator_1, currentNumerator: numerator_1 },
                    });
                }, 0);
                break;
            }
            case 'glueFractionTiles': {
                // Close menu without restoring focus so the new glued tile's
                // auto-focus can take over cleanly (same pattern as addTile).
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                setTimeout(function () {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'glueFractionTiles',
                        type: 'fractionBtn',
                    });
                }, 0);
                break;
            }
            case 'combineFractionTilesAction': {
                // Context tile is pre-checked in checkedDiceIds; collect all checked fraction tiles.
                var checkedFractionIds = Array.from(checkedDiceIds).filter(function (tid) {
                    return tid.startsWith('fractionTiles_');
                });
                if (checkedFractionIds.length < 2)
                    break; // Need at least 2 tiles to combine
                var allIds_1 = checkedFractionIds;
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                setCheckedDiceIds(new Set());
                // Pass tileIds explicitly so FractionTileController doesn't rely on
                // selectedTool (which hasn't been updated by the time the event fires).
                setTimeout(function () {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'glueFractionTiles',
                        type: 'fractionBtn',
                        data: { tileIds: allIds_1 },
                    });
                }, 0);
                break;
            }
            case 'tableToolbar': {
                // Inline teardown (skip closeMenu's 50ms focus-restore) so focus goes
                // directly to the bottom panel toolbar instead of bouncing back to the table.
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                // If invoked from a cell context menu, activate that cell so toolbar
                // actions apply to it specifically. focusEditor:false prevents
                // TableManipulativeComp's pendingFocusRef from focusing the editorDiv,
                // but MathEditor's own 50ms auto-focus effect still fires — our toolbar
                // focus is delayed to 100ms so it always runs last.
                if (isTableCellMenu && cellMenuTableIndex >= 0) {
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'tableEditCell',
                        row: cellMenuRow,
                        col: cellMenuCol,
                        manipulativeId: "tableText_".concat(cellMenuTableIndex),
                        focusEditor: false,
                    });
                }
                var focusToolbar_1 = function (retries) {
                    var _a;
                    if (retries === void 0) { retries = 0; }
                    if (retries > 30)
                        return;
                    var toolbar = document.querySelector('.bottomPanelTool');
                    if (toolbar) {
                        var first = (_a = toolbar.querySelector('button:not([disabled]):not([tabindex="-1"])')) !== null && _a !== void 0 ? _a : toolbar.querySelector('button:not([disabled])');
                        if (first) {
                            first.focus();
                            return;
                        }
                    }
                    requestAnimationFrame(function () { return focusToolbar_1(retries + 1); });
                };
                setTimeout(function () { return focusToolbar_1(0); }, 100);
                break;
            }
            case 'tableNavigateCells': {
                customEventHandler.dispatchEvent('openTableCellPicker', {
                    manipulativeId: contextMenu.manipulativeId,
                });
                break;
            }
            case 'tableCellEdit': {
                if (!isTableCellMenu || cellMenuTableIndex < 0)
                    break;
                // Inline teardown (skip closeMenu's 50ms focus-restore) so focus goes
                // directly to the cell editor instead of bouncing back to the table container.
                highlightDice(null);
                setContextMenu(null);
                setSubmenuOpen(false);
                setActiveIndex(-1);
                setSubmenuIndex(0);
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'tableEditCell',
                    row: cellMenuRow,
                    col: cellMenuCol,
                    manipulativeId: "tableText_".concat(cellMenuTableIndex),
                });
                break;
            }
            case 'tableCellLock': {
                if (!isTableCellMenu || !cellMenuTableItem)
                    break;
                var newLock_2 = !cellIsLocked;
                var cols_1 = cellMenuNumCols;
                // After re-render the menu item label changes (Lock ↔ Unlock) and the
                // DOM node is replaced — re-query by activeIndex to keep focus on it.
                var lockItemIndex_1 = activeIndex;
                setTimeout(function () {
                    var _a, _b;
                    var btns = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"]');
                    (_b = btns === null || btns === void 0 ? void 0 : btns[lockItemIndex_1]) === null || _b === void 0 ? void 0 : _b.focus();
                }, 0);
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    var tbl = __assign({}, nt[cellMenuTableIndex]);
                    var td = __assign({}, tbl.data);
                    var ncd = __spreadArray([], td.cellData, true);
                    var ci = cellMenuRow * cols_1 + cellMenuCol;
                    if (ncd[ci]) {
                        ncd[ci] = __assign(__assign({}, ncd[ci]), { data: __assign(__assign({}, ncd[ci].data), { lock: __assign(__assign({}, (ncd[ci].data.lock || { positioning: false })), { editing: newLock_2 }) }) });
                    }
                    td.cellData = ncd;
                    tbl.data = td;
                    nt[cellMenuTableIndex] = tbl;
                    return nt;
                });
                break;
            }
            case 'tableCellTextColor_RED':
            case 'tableCellTextColor_YELLOW':
            case 'tableCellTextColor_BLUE':
            case 'tableCellTextColor_BROWN':
            case 'tableCellTextColor_ORANGE':
            case 'tableCellTextColor_GREEN':
            case 'tableCellTextColor_PURPLE':
            case 'tableCellTextColor_BLACK':
            case 'tableCellTextColor_WHITE':
            case 'tableCellTextColor_GRAY': {
                if (!isTableCellMenu || cellMenuTableIndex < 0 || !cellMenuTableItem)
                    break;
                var colorKey = actionId.replace('tableCellTextColor_', '');
                var CELL_COLOR_MAP = {
                    RED: { hex: ColorEnum.RED, label: 'text.red' },
                    YELLOW: { hex: ColorEnum.YELLOW, label: 'text.yellow' },
                    BLUE: { hex: ColorEnum.BLUE, label: 'text.blue' },
                    BROWN: { hex: ColorEnum.BROWN, label: 'text.brown' },
                    ORANGE: { hex: ColorEnum.ORANGE, label: 'text.orange' },
                    GREEN: { hex: ColorEnum.GREEN, label: 'text.green' },
                    PURPLE: { hex: ColorEnum.PURPLE, label: 'text.purple' },
                    BLACK: { hex: ColorEnum.BLACK, label: 'text.black' },
                    WHITE: { hex: ColorEnum.WHITE, label: 'text.white' },
                    GRAY: { hex: ColorEnum.GRAY, label: 'text.gray' },
                };
                var chosen = CELL_COLOR_MAP[colorKey];
                if (!chosen)
                    break;
                var cols = cellMenuNumCols;
                var ci = cellMenuRow * cols + cellMenuCol;
                var current = cellMenuTableItem;
                if (!current.data.cellData[ci])
                    break;
                var ncd = __spreadArray([], current.data.cellData, true);
                ncd[ci] = __assign(__assign({}, ncd[ci]), { data: __assign(__assign({}, ncd[ci].data), { color: chosen.hex }) });
                var updated_6 = __assign(__assign({}, current), { data: __assign(__assign({}, current.data), { cellData: ncd }) });
                saveState({
                    type: 'table',
                    id: String(cellMenuTableIndex),
                    propertyName: 'textColorChange',
                    old: JSON.stringify(current),
                    new: JSON.stringify(updated_6),
                });
                setTable(function (prev) { var nt = __spreadArray([], prev, true); nt[cellMenuTableIndex] = updated_6; return nt; });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                announce(t('text.colorChanged', { color: t(chosen.label) }));
                break;
            }
            case 'tableLevelTextColor_RED':
            case 'tableLevelTextColor_YELLOW':
            case 'tableLevelTextColor_BLUE':
            case 'tableLevelTextColor_BROWN':
            case 'tableLevelTextColor_ORANGE':
            case 'tableLevelTextColor_GREEN':
            case 'tableLevelTextColor_PURPLE':
            case 'tableLevelTextColor_BLACK':
            case 'tableLevelTextColor_WHITE':
            case 'tableLevelTextColor_GRAY': {
                if (!isTableTextMenu || tableTextIndex < 0 || !selectedTableItem)
                    break;
                var colorKey = actionId.replace('tableLevelTextColor_', '');
                var TABLE_COLOR_MAP = {
                    RED: { hex: ColorEnum.RED, label: 'text.red' },
                    YELLOW: { hex: ColorEnum.YELLOW, label: 'text.yellow' },
                    BLUE: { hex: ColorEnum.BLUE, label: 'text.blue' },
                    BROWN: { hex: ColorEnum.BROWN, label: 'text.brown' },
                    ORANGE: { hex: ColorEnum.ORANGE, label: 'text.orange' },
                    GREEN: { hex: ColorEnum.GREEN, label: 'text.green' },
                    PURPLE: { hex: ColorEnum.PURPLE, label: 'text.purple' },
                    BLACK: { hex: ColorEnum.BLACK, label: 'text.black' },
                    WHITE: { hex: ColorEnum.WHITE, label: 'text.white' },
                    GRAY: { hex: ColorEnum.GRAY, label: 'text.gray' },
                };
                var chosen_2 = TABLE_COLOR_MAP[colorKey];
                if (!chosen_2)
                    break;
                var current = selectedTableItem;
                var updatedCellData = current.data.cellData.map(function (cell) { return (__assign(__assign({}, cell), { data: __assign(__assign({}, cell.data), { color: chosen_2.hex }) })); });
                var updated_7 = __assign(__assign({}, current), { data: __assign(__assign({}, current.data), { cellData: updatedCellData }) });
                saveState({
                    type: 'table',
                    id: String(tableTextIndex),
                    propertyName: 'textColorChange',
                    old: JSON.stringify(current),
                    new: JSON.stringify(updated_7),
                });
                setTable(function (prev) { var nt = __spreadArray([], prev, true); nt[tableTextIndex] = updated_7; return nt; });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                announce(t('text.colorChanged', { color: t(chosen_2.label) }));
                break;
            }
            case 'tableCellBgColor_RED':
            case 'tableCellBgColor_YELLOW':
            case 'tableCellBgColor_BLUE':
            case 'tableCellBgColor_BROWN':
            case 'tableCellBgColor_ORANGE':
            case 'tableCellBgColor_GREEN':
            case 'tableCellBgColor_PURPLE':
            case 'tableCellBgColor_BLACK':
            case 'tableCellBgColor_WHITE':
            case 'tableCellBgColor_GRAY':
            case 'tableCellBgColor_NONE': {
                if (!isTableCellMenu || cellMenuTableIndex < 0 || !cellMenuTableItem)
                    break;
                var colorKey = actionId.replace('tableCellBgColor_', '');
                var CELL_BG_MAP = {
                    RED: { hex: ColorEnum.RED, label: 'text.red' },
                    YELLOW: { hex: ColorEnum.YELLOW, label: 'text.yellow' },
                    BLUE: { hex: ColorEnum.BLUE, label: 'text.blue' },
                    BROWN: { hex: ColorEnum.BROWN, label: 'text.brown' },
                    ORANGE: { hex: ColorEnum.ORANGE, label: 'text.orange' },
                    GREEN: { hex: ColorEnum.GREEN, label: 'text.green' },
                    PURPLE: { hex: ColorEnum.PURPLE, label: 'text.purple' },
                    BLACK: { hex: ColorEnum.BLACK, label: 'text.black' },
                    WHITE: { hex: ColorEnum.WHITE, label: 'text.white' },
                    GRAY: { hex: ColorEnum.GRAY, label: 'text.gray' },
                    NONE: { hex: '', label: 'text.none' },
                };
                var chosen = CELL_BG_MAP[colorKey];
                if (!chosen)
                    break;
                var cols = cellMenuNumCols;
                var ci = cellMenuRow * cols + cellMenuCol;
                var current = cellMenuTableItem;
                if (!current.data.cellData[ci])
                    break;
                var ncd = __spreadArray([], current.data.cellData, true);
                ncd[ci] = __assign(__assign({}, ncd[ci]), { data: __assign(__assign({}, ncd[ci].data), { backgroundColor: chosen.hex }) });
                var updated_8 = __assign(__assign({}, current), { data: __assign(__assign({}, current.data), { cellData: ncd }) });
                saveState({
                    type: 'table',
                    id: String(cellMenuTableIndex),
                    propertyName: 'colorChange',
                    old: JSON.stringify(current),
                    new: JSON.stringify(updated_8),
                });
                setTable(function (prev) { var nt = __spreadArray([], prev, true); nt[cellMenuTableIndex] = updated_8; return nt; });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                announce(chosen.hex
                    ? t('text.backgroundColorChanged', { color: t(chosen.label) })
                    : t('text.tableCellColorCleared'));
                break;
            }
            case 'tableLevelBgColor_RED':
            case 'tableLevelBgColor_YELLOW':
            case 'tableLevelBgColor_BLUE':
            case 'tableLevelBgColor_BROWN':
            case 'tableLevelBgColor_ORANGE':
            case 'tableLevelBgColor_GREEN':
            case 'tableLevelBgColor_PURPLE':
            case 'tableLevelBgColor_BLACK':
            case 'tableLevelBgColor_WHITE':
            case 'tableLevelBgColor_GRAY':
            case 'tableLevelBgColor_NONE': {
                if (!isTableTextMenu || tableTextIndex < 0 || !selectedTableItem)
                    break;
                var colorKey = actionId.replace('tableLevelBgColor_', '');
                var TABLE_BG_MAP = {
                    RED: { hex: ColorEnum.RED, label: 'text.red' },
                    YELLOW: { hex: ColorEnum.YELLOW, label: 'text.yellow' },
                    BLUE: { hex: ColorEnum.BLUE, label: 'text.blue' },
                    BROWN: { hex: ColorEnum.BROWN, label: 'text.brown' },
                    ORANGE: { hex: ColorEnum.ORANGE, label: 'text.orange' },
                    GREEN: { hex: ColorEnum.GREEN, label: 'text.green' },
                    PURPLE: { hex: ColorEnum.PURPLE, label: 'text.purple' },
                    BLACK: { hex: ColorEnum.BLACK, label: 'text.black' },
                    WHITE: { hex: ColorEnum.WHITE, label: 'text.white' },
                    GRAY: { hex: ColorEnum.GRAY, label: 'text.gray' },
                    NONE: { hex: '', label: 'text.none' },
                };
                var chosen_3 = TABLE_BG_MAP[colorKey];
                if (!chosen_3)
                    break;
                var current = selectedTableItem;
                var updatedCellData = current.data.cellData.map(function (cell) { return (__assign(__assign({}, cell), { data: __assign(__assign({}, cell.data), { backgroundColor: chosen_3.hex }) })); });
                var updated_9 = __assign(__assign({}, current), { data: __assign(__assign({}, current.data), { cellData: updatedCellData }) });
                saveState({
                    type: 'table',
                    id: String(tableTextIndex),
                    propertyName: 'colorChange',
                    old: JSON.stringify(current),
                    new: JSON.stringify(updated_9),
                });
                setTable(function (prev) { var nt = __spreadArray([], prev, true); nt[tableTextIndex] = updated_9; return nt; });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                announce(chosen_3.hex
                    ? t('text.backgroundColorChanged', { color: t(chosen_3.label) })
                    : t('text.tableCellColorCleared'));
                break;
            }
            // Table-specific row/col/cellSelect actions dispatched from accessibility menu
            case 'tableAddRowAbove':
            case 'tableAddRowBelow':
            case 'tableDeleteRow':
            case 'tableAddColLeft':
            case 'tableAddColRight':
            case 'tableDeleteCol':
            case 'cellSelectMode':
            case 'bw2':
            case 'bw3':
            case 'bw5':
            case 'bw6': {
                if (isTableTextMenu && tableTextIndex >= 0) {
                    if (!selectedTool.includes(contextMenu.manipulativeId)) {
                        setSelectedTool([contextMenu.manipulativeId]);
                    }
                    customEventHandler.dispatchEvent('bottomPanel', { id: actionId });
                }
                else if (isTableCellMenu && cellMenuTableIndex >= 0) {
                    customEventHandler.dispatchEvent('bottomPanel', { id: actionId });
                }
                break;
            }
            case 'exit':
                closeMenu();
                break;
        }
    }, [
        selectedItem,
        contextMenu,
        closeMenu,
        positionLock,
        editingLock,
        setManipulative,
        setSelectedTool,
        checkedDiceIds,
        checkedPatternIds,
        setCheckedDiceIds,
        manipulative,
        selectedTool,
        t,
        isShadeMenu,
        shadeIndex,
        shades,
        setShades,
        isCardMenu,
        isDeckMenu,
        isAnnotationMenu,
        annotationIndex,
        annotations,
        setAnnotations,
        isTextMenu,
        textIndex,
        selectedTextItem,
        setText,
        isTableTextMenu,
        tableTextIndex,
        selectedTableItem,
        setTable,
        isTableCellMenu,
        cellMenuTableIndex,
        cellMenuRow,
        cellMenuCol,
        cellMenuTableItem,
        cellMenuNumCols,
        cellIsLocked,
        getPositionForNewItem,
    ]);
    // Inert siblings when custom content submenu is open (stamp every form)
    // JAWS forms mode Tab operates below the DOM event system — only inert can block it
    // inert removes elements from the browser's focus chain at the platform level
    useEffect(function () {
        var _a, _b;
        var isCustomContentActive = submenuOpen && ((_a = menuItems[activeIndex]) === null || _a === void 0 ? void 0 : _a.customContent);
        if (!isCustomContentActive)
            return;
        var menuEl = (_b = menuRef.current) === null || _b === void 0 ? void 0 : _b.closest('.shape-context-menu-overlay');
        var inertedElements = [];
        Array.from(document.body.children).forEach(function (child) {
            var el = child;
            if (el === menuEl ||
                el.tagName === 'SCRIPT' ||
                el.tagName === 'STYLE' ||
                el.tagName === 'LINK')
                return;
            if (!el.hasAttribute('inert')) {
                el.setAttribute('inert', '');
                el.setAttribute('aria-hidden', 'true');
                inertedElements.push(el);
            }
        });
        return function () {
            inertedElements.forEach(function (el) {
                el.removeAttribute('inert');
                el.removeAttribute('aria-hidden');
            });
        };
    }, [submenuOpen, activeIndex, menuItems]);
    // Keyboard navigation
    var handleMenuKeyDown = useCallback(function (e) {
        var _a, _b, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (menuItems.length === 0)
            return;
        // Check if custom content form is active — let it handle its own keys
        var isCustomContentActive = submenuOpen && ((_a = menuItems[activeIndex]) === null || _a === void 0 ? void 0 : _a.customContent);
        // Tab / Shift+Tab navigate menu items (like ArrowDown / ArrowUp)
        if (e.key === 'Tab' && !isCustomContentActive) {
            e.preventDefault();
            // Fall through to arrow key logic below — Tab = ArrowDown, Shift+Tab = ArrowUp
        }
        // Submenu navigation
        if (submenuOpen) {
            var currentItem = menuItems[activeIndex];
            // Custom content forms handle their own keyboard navigation
            // But still handle Escape/ArrowLeft to exit, and prevent arrow keys from leaving the menu
            if (currentItem === null || currentItem === void 0 ? void 0 : currentItem.customContent) {
                if (e.key === 'Escape' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    e.stopPropagation();
                    setSubmenuOpen(false);
                    setTimeout(function () {
                        var _a, _b;
                        var items = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"], button[role="menuitemcheckbox"], button[role="menuitemradio"]');
                        (_b = items === null || items === void 0 ? void 0 : items[activeIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                    }, 50);
                }
                else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    e.stopPropagation();
                }
                else if (e.key === 'Tab') {
                    // Trap Tab inside the custom content form
                    e.preventDefault();
                    e.stopPropagation();
                    var focusable = (_b = submenuRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('input:not([disabled]), button:not([disabled]):not([tabindex="-1"])');
                    if (focusable && focusable.length > 0) {
                        var idx = Array.from(focusable).indexOf(document.activeElement);
                        var next = e.shiftKey
                            ? (idx - 1 + focusable.length) % focusable.length
                            : (idx + 1) % focusable.length;
                        focusable[next].focus();
                    }
                }
                return;
            }
            var subItems_1 = (_d = currentItem === null || currentItem === void 0 ? void 0 : currentItem.submenu) !== null && _d !== void 0 ? _d : [];
            var totalSubItems_1 = subItems_1.length + 1; // +1 for Back button
            // Map Tab → ArrowDown, Shift+Tab → ArrowUp
            var isSubTabKey_1 = e.key === 'Tab';
            var subKey = isSubTabKey_1 ? (e.shiftKey ? 'ArrowUp' : 'ArrowDown') : e.key;
            // Tab skips disabled submenu items; arrow keys land on all
            var findNextSub_1 = function (from, dir) {
                var _a;
                var idx = from;
                for (var i = 0; i < totalSubItems_1; i++) {
                    idx = (idx + dir + totalSubItems_1) % totalSubItems_1;
                    // Back button (last index) is never disabled
                    if (idx === subItems_1.length)
                        return idx;
                    if (!isSubTabKey_1 || !((_a = subItems_1[idx]) === null || _a === void 0 ? void 0 : _a.disabled))
                        return idx;
                }
                return from;
            };
            switch (subKey) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSubmenuIndex(function (prev) { return findNextSub_1(prev, 1); });
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSubmenuIndex(function (prev) { return findNextSub_1(prev, -1); });
                    break;
                case 'ArrowLeft':
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    setSubmenuOpen(false);
                    setTimeout(function () {
                        var _a;
                        (_a = activeMenuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 50);
                    break;
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    e.stopPropagation();
                    if (submenuIndex === subItems_1.length) {
                        // Back button — return to main menu
                        setSubmenuOpen(false);
                        setSubmenuIndex(0);
                        setTimeout(function () {
                            var _a;
                            (_a = activeMenuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        }, 50);
                    }
                    else if (subItems_1[submenuIndex]) {
                        var subItem_1 = subItems_1[submenuIndex];
                        if (subItem_1.isCheckbox && subItem_1.diceId) {
                            // Toggle checkbox — stay in submenu
                            var isPatternItem = subItem_1.id.startsWith('patternCheck_');
                            var isFractionCombine = !isPatternItem && (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.id) === 'combineFractionTiles';
                            var setChecked = isPatternItem ? setCheckedPatternIds : setCheckedDiceIds;
                            setChecked(function (prev) {
                                var next = new Set(prev);
                                var willCheck = !next.has(subItem_1.diceId);
                                if (willCheck) {
                                    next.add(subItem_1.diceId);
                                    announce(t('text.diceChecked', { label: subItem_1.label }));
                                }
                                else {
                                    next.delete(subItem_1.diceId);
                                    announce(t('text.diceUnchecked', { label: subItem_1.label }));
                                }
                                return next;
                            });
                        }
                        else if (!subItem_1.disabled) {
                            executeAction(subItem_1.id);
                            // Refocus this button after state change re-render
                            setTimeout(function () {
                                var _a, _b;
                                var btns = (_a = submenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"], button[role="menuitemradio"], button[role="menuitemcheckbox"]');
                                (_b = btns === null || btns === void 0 ? void 0 : btns[submenuIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                            }, 50);
                        }
                    }
                    break;
            }
            return;
        }
        // Main menu navigation — Tab/Shift+Tab also navigate items
        var isTabKey = e.key === 'Tab';
        var mainKey = isTabKey ? (e.shiftKey ? 'ArrowUp' : 'ArrowDown') : e.key;
        // Tab skips disabled items; arrow keys land on all items
        var findNext = function (from, dir) {
            var idx = from;
            for (var i = 0; i < menuItems.length; i++) {
                idx = (idx + dir + menuItems.length) % menuItems.length;
                if (!isTabKey || !menuItems[idx].disabled)
                    return idx;
            }
            return from;
        };
        switch (mainKey) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(function (prev) {
                    if (prev < 0)
                        return isTabKey ? findNext(-1, 1) : 0;
                    return findNext(prev, 1);
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(function (prev) { return findNext(prev, -1); });
                break;
            case 'ArrowRight': {
                e.preventDefault();
                var item = menuItems[activeIndex];
                if (((item === null || item === void 0 ? void 0 : item.submenu) || (item === null || item === void 0 ? void 0 : item.customContent)) && !item.disabled) {
                    activeMenuItemRef.current =
                        (_g = (_f = (_e = menuRef.current) === null || _e === void 0 ? void 0 : _e.querySelectorAll('button[role="menuitem"], button[role="menuitemcheckbox"], button[role="menuitemradio"]')) === null || _f === void 0 ? void 0 : _f[activeIndex]) !== null && _g !== void 0 ? _g : null;
                    setSubmenuOpen(true);
                    setSubmenuIndex(0);
                }
                break;
            }
            case 'Enter':
            case ' ': {
                e.preventDefault();
                e.stopPropagation();
                console.log('[ContextMenu] Enter/Space keydown', {
                    activeIndex: activeIndex,
                    menuItemsLength: menuItems.length,
                    menuItems: menuItems.map(function (m) { return ({ id: m.id, label: m.label, disabled: m.disabled, hasSubmenu: !!(m.submenu || m.customContent) }); }),
                    submenuOpen: submenuOpen,
                    manipulativeId: contextMenu === null || contextMenu === void 0 ? void 0 : contextMenu.manipulativeId,
                    focusedEl: ((_h = document.activeElement) === null || _h === void 0 ? void 0 : _h.tagName) + (((_j = document.activeElement) === null || _j === void 0 ? void 0 : _j.className) ? '.' + document.activeElement.className : ''),
                });
                if (activeIndex < 0) {
                    console.log('[ContextMenu] activeIndex < 0 → advancing to item 0');
                    setActiveIndex(0);
                    break;
                }
                var item = menuItems[activeIndex];
                console.log('[ContextMenu] item at activeIndex:', item);
                if (((item === null || item === void 0 ? void 0 : item.submenu) || (item === null || item === void 0 ? void 0 : item.customContent)) && !item.disabled) {
                    console.log('[ContextMenu] opening submenu for:', item.id);
                    activeMenuItemRef.current =
                        (_m = (_l = (_k = menuRef.current) === null || _k === void 0 ? void 0 : _k.querySelectorAll('button[role="menuitem"], button[role="menuitemcheckbox"], button[role="menuitemradio"]')) === null || _l === void 0 ? void 0 : _l[activeIndex]) !== null && _m !== void 0 ? _m : null;
                    setSubmenuOpen(true);
                    setSubmenuIndex(0);
                }
                else if (item && !item.disabled) {
                    console.log('[ContextMenu] executeAction:', item.id);
                    executeAction(item.id);
                }
                else {
                    console.log('[ContextMenu] Enter blocked — item undefined or disabled', { item: item });
                }
                break;
            }
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                closeMenu();
                if (isCanvasPanMenu) {
                    setTimeout(function () {
                        var _a;
                        (_a = document.querySelector('.panningBtn')) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 60);
                }
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(menuItems.length - 1);
                break;
        }
    }, [
        menuItems,
        activeIndex,
        submenuOpen,
        submenuIndex,
        executeAction,
        closeMenu,
        checkedDiceIds,
        t,
    ]);
    // Focus active main menu item (activeIndex -1 = header focused, 0+ = menu items)
    useEffect(function () {
        var _a, _b;
        if (!contextMenu || submenuOpen)
            return;
        if (activeIndex < 0) {
            // Header focused — don't move focus away from it
            return;
        }
        var items = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"]');
        (_b = items === null || items === void 0 ? void 0 : items[activeIndex]) === null || _b === void 0 ? void 0 : _b.focus();
    }, [activeIndex, contextMenu, submenuOpen]);
    // Initialize stamp every inputs when its custom content opens
    useEffect(function () {
        var _a, _b, _d, _e;
        if (!submenuOpen)
            return;
        var item = menuItems[activeIndex];
        if ((item === null || item === void 0 ? void 0 : item.customContent) !== 'stampEvery' || !selectedItem)
            return;
        var ncData = (_a = selectedItem.data) !== null && _a !== void 0 ? _a : {};
        var firstCell = (_d = (_b = ncData.cells) === null || _b === void 0 ? void 0 : _b[0]) === null || _d === void 0 ? void 0 : _d[0];
        if (!firstCell)
            return;
        var nt = (_e = ncData.numberType) !== null && _e !== void 0 ? _e : 0;
        if (nt === 0) {
            setStampEveryInput({
                interval: 1,
                integer: firstCell.numerator,
                numerator: 0,
                denominator: 1,
            });
        }
        else if (nt === 1) {
            setStampEveryInput({
                interval: 1,
                integer: 0,
                numerator: firstCell.numerator,
                denominator: firstCell.denominator,
            });
        }
        else if (nt === 2) {
            var intPart = Math.floor(firstCell.numerator / firstCell.denominator);
            var remNum = firstCell.numerator % firstCell.denominator;
            setStampEveryInput({
                interval: 1,
                integer: intPart,
                numerator: remNum,
                denominator: firstCell.denominator,
            });
        }
        else if (nt === 3) {
            var decVal = firstCell.denominator !== 0
                ? firstCell.numerator / firstCell.denominator
                : firstCell.numerator;
            setStampEveryInput({
                interval: 1,
                integer: decVal,
                numerator: firstCell.numerator,
                denominator: firstCell.denominator,
            });
        }
    }, [submenuOpen, activeIndex]);
    // Focus active submenu item
    var prevSubmenuOpenRef = useRef(false);
    useEffect(function () {
        if (!submenuOpen) {
            prevSubmenuOpenRef.current = false;
            return;
        }
        var isInitialOpen = !prevSubmenuOpenRef.current;
        prevSubmenuOpenRef.current = true;
        // On initial open, delay focus so VoiceOver on iPad has time to process
        // the DOM change before we move its cursor to the first item.
        // For subsequent index changes (arrow navigation), focus immediately.
        var delay = isInitialOpen ? 100 : 0;
        var timerId = setTimeout(function () {
            var _a, _b, _d;
            var currentItem = menuItems[activeIndex];
            if (currentItem === null || currentItem === void 0 ? void 0 : currentItem.customContent) {
                // For custom content (e.g., Stamp Every form), focus the first input
                var firstField = (_a = submenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input, select, textarea, button');
                firstField === null || firstField === void 0 ? void 0 : firstField.focus();
                return;
            }
            var items = (_b = submenuRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('button[role="menuitem"], button[role="menuitemcheckbox"], button[role="menuitemradio"]');
            (_d = items === null || items === void 0 ? void 0 : items[submenuIndex]) === null || _d === void 0 ? void 0 : _d.focus();
        }, delay);
        return function () { return clearTimeout(timerId); };
    }, [submenuIndex, submenuOpen, checkedDiceIds]);
    if (!contextMenu || menuItems.length === 0)
        return null;
    // Position menu adjacent to the manipulative (never overlapping it)
    var menuWidth = 230;
    // Use actual visible item count: submenu items when open, main items otherwise
    var activeMenuItem = submenuOpen ? menuItems[activeIndex] : null;
    var activeSubmenuForHeight = (_39 = activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.submenu) !== null && _39 !== void 0 ? _39 : [];
    var isCustomContent = submenuOpen && !!(activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.customContent);
    var visibleItemCount = submenuOpen
        ? isCustomContent
            ? 6
            : activeSubmenuForHeight.length + 1
        : menuItems.length;
    var menuHeight = visibleItemCount * 40 + 50;
    var gap = 8; // space between manipulative and menu
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var x = contextMenu.x;
    var y = contextMenu.y;
    // The static-outline cohort's AABB-sized focus wrapper (PatternStaticOutlineCohort)
    // shares its data-manipulative-id with the outline's first shape, so a plain
    // querySelector can match the wrapper instead of the shape that was actually
    // right-clicked. Prefer the real shape element; fall back to the wrapper (e.g.
    // for the cohort's own keyboard-triggered menu, where the AABB is the intended anchor).
    var manipEl = (_40 = document.querySelector("[data-manipulative-id=\"".concat(contextMenu.manipulativeId, "\"]:not(.staticOutlineCohort)"))) !== null && _40 !== void 0 ? _40 : document.querySelector("[data-manipulative-id=\"".concat(contextMenu.manipulativeId, "\"]"));
    if (manipEl) {
        var mRect = manipEl.getBoundingClientRect();
        // Analog-clock-specific: the digital-display / run-jump panel renders
        // under `.clockFunctionality` and is positioned absolutely OUTSIDE the
        // wrapper's bounding rect (translated to the left). When that panel is
        // visible, the menu's auto-flip "left" branch otherwise lands on top of
        // it. Extend the effective left edge for analog clocks only so the menu
        // shifts further left of the side panel. Other manipulatives keep the
        // original wrapper-rect-only behavior.
        var effectiveLeft = mRect.left;
        var effectiveRight = mRect.right;
        if (contextMenu.manipulativeId.startsWith('analog_')) {
            var sidePanel = manipEl.querySelector('.clockFunctionality');
            if (sidePanel && sidePanel.offsetParent !== null) {
                var sRect = sidePanel.getBoundingClientRect();
                effectiveLeft = Math.min(effectiveLeft, sRect.left);
                effectiveRight = Math.max(effectiveRight, sRect.right);
            }
        }
        // Prefer placing menu to the right of the manipulative
        if (effectiveRight + gap + menuWidth + 10 <= vw) {
            x = effectiveRight + gap;
        }
        else if (effectiveLeft - gap - menuWidth - 10 >= 0) {
            // Fall back to left side
            x = effectiveLeft - gap - menuWidth;
        }
        else {
            // Neither side fits — clamp to viewport edge
            x = Math.min(contextMenu.x, vw - menuWidth - 10);
        }
        // Align top of menu with top of manipulative, clamped to viewport.
        // Beads are a special case — anchor the menu BELOW the bead (top +
        // height) instead so the menu doesn't visually cover the bead the
        // user just acted on.
        var isBeadAnchor = contextMenu.manipulativeId.includes('_bead_');
        var anchorTop = isBeadAnchor ? mRect.bottom : mRect.top;
        y = Math.max(10, Math.min(anchorTop, vh - menuHeight - 10));
    }
    else {
        // Fallback: no manipulative element found, just clamp
        x = Math.min(contextMenu.x, vw - menuWidth - 10);
        y = Math.min(contextMenu.y, vh - menuHeight - 10);
    }
    // Active submenu data (when drilled into a submenu)
    var activeSubmenuParent = submenuOpen ? menuItems[activeIndex] : null;
    var activeSubItems = (_41 = activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.submenu) !== null && _41 !== void 0 ? _41 : [];
    return createPortal(_jsx("div", { className: "shape-context-menu-overlay", onMouseDown: function () { return closeMenu(); }, onTouchStart: function () { return closeMenu(); }, children: _jsxs("div", { ref: menuRef, className: "shape-context-menu", 
            // On iPad/touch devices use role="dialog" so iOS VoiceOver automatically
            // traps navigation inside the menu and moves its cursor to the first
            // focusable element. With role="menu" VoiceOver does not auto-trap focus
            // and the cursor returns to the tile after the menu opens.
            // On desktop keep role="menu" for standard ARIA keyboard semantics.
            role: IS_TOUCH_DEVICE ? 'dialog' : 'menu', "aria-modal": "true", "aria-label": submenuOpen ? String((_42 = activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.label) !== null && _42 !== void 0 ? _42 : '') : getContextMenuHeading(), style: { top: y, left: x }, onKeyDown: handleMenuKeyDown, onMouseDown: function (e) { return e.stopPropagation(); }, onTouchStart: function (e) { return e.stopPropagation(); }, children: [submenuOpen && activeSubmenuParent ? (
                /* ── Submenu view (in-place, replaces main menu) ── */
                _jsxs("div", { ref: submenuRef, role: (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.customContent) ? 'dialog' : undefined, "aria-modal": (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.customContent) ? 'true' : undefined, "aria-label": (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.customContent) ? String((_43 = activeSubmenuParent.label) !== null && _43 !== void 0 ? _43 : '') : undefined, children: [_jsx("div", { className: "shape-context-menu__header", role: "presentation", tabIndex: -1, children: activeSubmenuParent.label }), activeSubmenuParent.customContent === 'stampEvery' ? (
                        /* ── Stamp Every inline form ── */
                        _jsxs("div", { className: "shape-context-menu__stamp-every", onKeyDown: function (e) {
                                var formEl = e.currentTarget;
                                var focusable = formEl.querySelectorAll('input, button');
                                var first = focusable[0];
                                var last = focusable[focusable.length - 1];
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSubmenuOpen(false);
                                    setTimeout(function () {
                                        var _a, _b;
                                        var items = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"]');
                                        (_b = items === null || items === void 0 ? void 0 : items[activeIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                                    }, 50);
                                }
                                else if (e.key === 'Tab') {
                                    // Focus trap: cycle within form
                                    if (e.shiftKey && document.activeElement === first) {
                                        e.preventDefault();
                                        last === null || last === void 0 ? void 0 : last.focus();
                                    }
                                    else if (!e.shiftKey && document.activeElement === last) {
                                        e.preventDefault();
                                        first === null || first === void 0 ? void 0 : first.focus();
                                    }
                                }
                                else if (e.key === 'Enter' &&
                                    !(document.activeElement instanceof HTMLButtonElement)) {
                                    // Enter on inputs triggers the OK button
                                    e.preventDefault();
                                    var okBtn = formEl.querySelector('.shape-context-menu__stamp-ok');
                                    okBtn === null || okBtn === void 0 ? void 0 : okBtn.click();
                                }
                            }, children: [_jsxs("div", { className: "shape-context-menu__stamp-row", children: [_jsx("label", { htmlFor: "stampInterval_input", className: "shape-context-menu__stamp-label", children: t('text.stampEveryLabel') }), _jsx(InputBox, { id: "stampInterval", type: "number", isNumeric: true, min: 1, max: 20, step: 1, value: stampEveryInput.interval.toString(), defaultCSS: "shape-context-menu__stamp-input", onInput: function (_id, _val, e) {
                                                return setStampEveryInput(function (prev) { return (__assign(__assign({}, prev), { interval: Number(e.target.value) })); });
                                            }, ariaLabel: t('text.stampEveryIntervalAria'), inputControlledFromOutside: true, invalidKeys: ['e', 'E', '+', '-', '.'] })] }), _jsxs("div", { className: "shape-context-menu__stamp-row", children: [(function () {
                                            var _a, _b;
                                            var ncData = (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _a !== void 0 ? _a : {};
                                            var nt = (_b = ncData.numberType) !== null && _b !== void 0 ? _b : 0;
                                            var labelFor = nt === 0 || nt === 3
                                                ? 'stampInteger_input'
                                                : nt === 2
                                                    ? 'stampMixedInt_input'
                                                    : 'stampNumerator_input';
                                            return (_jsx("label", { htmlFor: labelFor, className: "shape-context-menu__stamp-label", children: t('text.startingAt') }));
                                        })(), (function () {
                                            var _a, _b;
                                            var ncData = (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _a !== void 0 ? _a : {};
                                            var nt = (_b = ncData.numberType) !== null && _b !== void 0 ? _b : 0;
                                            if (nt === 0 || nt === 3) {
                                                return (_jsx(InputBox, { id: "stampInteger", type: nt === 3 ? 'text' : 'number', defaultCSS: "shape-context-menu__stamp-input", value: stampEveryInput.integer.toString(), onInput: function (_id, _val, e) {
                                                        return setStampEveryInput(function (prev) { return (__assign(__assign({}, prev), { integer: Number(e.target.value) })); });
                                                    }, ariaLabel: t('text.startingAtAria'), inputControlledFromOutside: true }));
                                            }
                                            return (_jsxs("span", { className: "shape-context-menu__stamp-fraction", children: [nt === 2 && (_jsx(InputBox, { id: "stampMixedInt", type: "number", defaultCSS: "shape-context-menu__stamp-input shape-context-menu__stamp-input--small", min: 0, value: stampEveryInput.integer.toString(), onInput: function (_id, _val, e) {
                                                            return setStampEveryInput(function (prev) { return (__assign(__assign({}, prev), { integer: Number(e.target.value) })); });
                                                        }, ariaLabel: t('text.integerPart'), inputControlledFromOutside: true })), _jsxs("span", { className: "shape-context-menu__stamp-frac-stack", children: [_jsx(InputBox, { id: "stampNumerator", type: "number", defaultCSS: "shape-context-menu__stamp-input shape-context-menu__stamp-input--small", min: nt === 1 ? 1 : 0, value: stampEveryInput.numerator.toString(), onInput: function (_id, _val, e) {
                                                                    return setStampEveryInput(function (prev) { return (__assign(__assign({}, prev), { numerator: Number(e.target.value) })); });
                                                                }, ariaLabel: t('text.numeratorLabel'), inputControlledFromOutside: true }), _jsx("div", { className: "shape-context-menu__stamp-frac-line" }), _jsx(InputBox, { id: "stampDenominator", type: "number", defaultCSS: "shape-context-menu__stamp-input shape-context-menu__stamp-input--small", min: 1, value: stampEveryInput.denominator.toString(), onInput: function (_id, _val, e) {
                                                                    return setStampEveryInput(function (prev) { return (__assign(__assign({}, prev), { denominator: Number(e.target.value) })); });
                                                                }, ariaLabel: t('text.denominatorLabel'), inputControlledFromOutside: true })] })] }));
                                        })()] }), (function () {
                                    var _a, _b;
                                    var ncData = (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.data) !== null && _a !== void 0 ? _a : {};
                                    var nt = (_b = ncData.numberType) !== null && _b !== void 0 ? _b : 0;
                                    var interval = stampEveryInput.interval, integer = stampEveryInput.integer, numerator = stampEveryInput.numerator, denominator = stampEveryInput.denominator;
                                    var isIntervalValid = interval >= 1;
                                    // Check if the entered start value exists in any cell (same logic as StampEveryPopup)
                                    var isValueValid = false;
                                    if (ncData.cells) {
                                        for (var _d = 0, _e = ncData.cells; _d < _e.length; _d++) {
                                            var row = _e[_d];
                                            for (var _f = 0, _g = row; _f < _g.length; _f++) {
                                                var cell = _g[_f];
                                                if (nt === 0) {
                                                    if (cell.numerator === integer && cell.denominator === 1)
                                                        isValueValid = true;
                                                }
                                                else if (nt === 1) {
                                                    if (cell.numerator === numerator && cell.denominator === denominator)
                                                        isValueValid = true;
                                                }
                                                else if (nt === 2) {
                                                    var improperNum = integer * denominator + numerator;
                                                    if (cell.numerator === improperNum && cell.denominator === denominator)
                                                        isValueValid = true;
                                                }
                                                else if (nt === 3) {
                                                    var cellVal = cell.denominator !== 0
                                                        ? cell.numerator / cell.denominator
                                                        : cell.numerator;
                                                    if (cellVal === integer)
                                                        isValueValid = true;
                                                }
                                                if (isValueValid)
                                                    break;
                                            }
                                            if (isValueValid)
                                                break;
                                        }
                                    }
                                    var isOkDisabled = !isIntervalValid || !isValueValid;
                                    return (_jsx("button", { className: "shape-context-menu__item shape-context-menu__stamp-ok".concat(isOkDisabled ? ' shape-context-menu__item--disabled' : ''), type: "button", tabIndex: isOkDisabled ? -1 : undefined, onClick: function () {
                                            if (!isOkDisabled)
                                                executeAction('ncStampEveryOk');
                                        }, onKeyDown: function (e) {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                if (!isOkDisabled)
                                                    executeAction('ncStampEveryOk');
                                            }
                                        }, "aria-disabled": isOkDisabled || undefined, children: t('text.ok') }));
                                })(), _jsx("button", { className: "shape-context-menu__item shape-context-menu__item--back", type: "button", onClick: function () {
                                        setSubmenuOpen(false);
                                        setSubmenuIndex(0);
                                        setTimeout(function () {
                                            var _a;
                                            (_a = activeMenuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                        }, 50);
                                    }, onKeyDown: function (e) {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setSubmenuOpen(false);
                                            setSubmenuIndex(0);
                                            setTimeout(function () {
                                                var _a, _b;
                                                var items = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"]');
                                                (_b = items === null || items === void 0 ? void 0 : items[activeIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                                            }, 50);
                                        }
                                    }, children: t('text.back') })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.id) === 'combineFractionTiles'
                                        ? 'shape-context-menu__scroll-items' : undefined, children: activeSubItems
                                        .filter(function (si) { return (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.id) !== 'combineFractionTiles' || !si.isAction; })
                                        .map(function (subItem, _i) {
                                        var _a, _b;
                                        var subIndex = activeSubItems.indexOf(subItem);
                                        if (subItem.isCheckbox) {
                                            var isChecked = subItem.id.startsWith('patternCheck_')
                                                ? checkedPatternIds.has((_a = subItem.diceId) !== null && _a !== void 0 ? _a : '')
                                                : checkedDiceIds.has((_b = subItem.diceId) !== null && _b !== void 0 ? _b : '');
                                            return (_jsxs("button", { className: "shape-context-menu__item shape-context-menu__item--checkbox".concat(subIndex === submenuIndex ? ' shape-context-menu__item--focused' : ''), role: "menuitemcheckbox", "aria-checked": isChecked, type: "button", tabIndex: subIndex === submenuIndex ? 0 : -1, "aria-setsize": activeSubItems.length + 1, "aria-posinset": subIndex + 1, onClick: function () {
                                                    if (subItem.diceId) {
                                                        var isPatternItem = subItem.id.startsWith('patternCheck_');
                                                        var setChecked = isPatternItem
                                                            ? setCheckedPatternIds
                                                            : setCheckedDiceIds;
                                                        setChecked(function (prev) {
                                                            var next = new Set(prev);
                                                            if (next.has(subItem.diceId))
                                                                next.delete(subItem.diceId);
                                                            else
                                                                next.add(subItem.diceId);
                                                            return next;
                                                        });
                                                    }
                                                }, onFocus: function () {
                                                    var _a;
                                                    if (submenuIndex !== subIndex)
                                                        setSubmenuIndex(subIndex);
                                                    highlightDice((_a = subItem.diceId) !== null && _a !== void 0 ? _a : null);
                                                }, onMouseEnter: function () {
                                                    var _a;
                                                    setSubmenuIndex(subIndex);
                                                    highlightDice((_a = subItem.diceId) !== null && _a !== void 0 ? _a : null);
                                                }, onMouseLeave: function () { return highlightDice(null); }, children: [_jsx("span", { className: "shape-context-menu__checkbox", "aria-hidden": "true", children: isChecked ? '\u2611' : '\u2610' }), _jsx("span", { className: "shape-context-menu__item-label", children: subItem.label })] }, subItem.id));
                                        }
                                        if (subItem.isAction) {
                                            var isPatternAction = subItem.id === 'mergeSelectedPatterns' || subItem.id === 'addPatternToGroup';
                                            var isDisabled_1 = isPatternAction
                                                ? checkedPatternIds.size === 0
                                                : checkedDiceIds.size === 0;
                                            return (_jsx("button", { className: "shape-context-menu__item shape-context-menu__item--action".concat(subIndex === submenuIndex ? ' shape-context-menu__item--focused' : ''), role: "menuitem", type: "button", tabIndex: isDisabled_1 ? -1 : subIndex === submenuIndex ? 0 : -1, "aria-setsize": activeSubItems.length + 1, "aria-posinset": subIndex + 1, "aria-disabled": isDisabled_1 || undefined, onClick: function () {
                                                    if (!isDisabled_1)
                                                        executeAction(subItem.id);
                                                }, onFocus: function () {
                                                    if (submenuIndex !== subIndex)
                                                        setSubmenuIndex(subIndex);
                                                    highlightDice(null);
                                                }, onMouseEnter: function () { return setSubmenuIndex(subIndex); }, children: _jsx("span", { className: "shape-context-menu__item-label", children: subItem.label }) }, subItem.id));
                                        }
                                        return (_jsxs(Fragment, { children: [subItem.divider && (_jsx("div", { className: "shape-context-menu__divider", role: "separator", "aria-hidden": "true" })), _jsxs("button", { className: "shape-context-menu__item".concat(subIndex === submenuIndex ? ' shape-context-menu__item--focused' : '').concat(subItem.selected ? ' shape-context-menu__item--selected' : ''), role: subItem.selected !== undefined ? 'menuitemradio' : 'menuitem', "aria-checked": subItem.selected !== undefined ? subItem.selected : undefined, type: "button", tabIndex: subItem.disabled ? -1 : subIndex === submenuIndex ? 0 : -1, "aria-setsize": activeSubItems.length + 1, "aria-posinset": subIndex + 1, "aria-disabled": subItem.disabled || undefined, onClick: function () {
                                                        if (!subItem.disabled) {
                                                            executeAction(subItem.id);
                                                            // Refocus this button after state change re-render
                                                            setTimeout(function () {
                                                                var _a, _b;
                                                                var btns = (_a = submenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button[role="menuitem"], button[role="menuitemradio"], button[role="menuitemcheckbox"]');
                                                                (_b = btns === null || btns === void 0 ? void 0 : btns[subIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                                                            }, 50);
                                                        }
                                                    }, onFocus: function () {
                                                        if (submenuIndex !== subIndex)
                                                            setSubmenuIndex(subIndex);
                                                    }, onMouseEnter: function () { return setSubmenuIndex(subIndex); }, children: [subItem.selected !== undefined && (_jsx("span", { className: "shape-context-menu__checkbox", "aria-hidden": "true", children: subItem.selected ? '✓' : '\u00A0' })), _jsx("span", { className: "shape-context-menu__item-label", children: subItem.label })] })] }, subItem.id));
                                    }) }), (activeSubmenuParent === null || activeSubmenuParent === void 0 ? void 0 : activeSubmenuParent.id) === 'combineFractionTiles' &&
                                    activeSubItems.filter(function (si) { return si.isAction; }).map(function (subItem) {
                                        var subIndex = activeSubItems.indexOf(subItem);
                                        var isDisabled = checkedDiceIds.size === 0 || !!subItem.disabled;
                                        return (_jsx("button", { className: "shape-context-menu__item shape-context-menu__item--action".concat(subIndex === submenuIndex ? ' shape-context-menu__item--focused' : ''), role: "menuitem", type: "button", tabIndex: isDisabled ? -1 : subIndex === submenuIndex ? 0 : -1, "aria-setsize": activeSubItems.length + 1, "aria-posinset": subIndex + 1, "aria-disabled": isDisabled || undefined, "aria-label": subItem.ariaLabel, onClick: function () { if (!isDisabled)
                                                executeAction(subItem.id); }, onFocus: function () { if (submenuIndex !== subIndex)
                                                setSubmenuIndex(subIndex); }, onMouseEnter: function () { return setSubmenuIndex(subIndex); }, children: _jsx("span", { className: "shape-context-menu__item-label", children: subItem.label }) }, subItem.id));
                                    }), _jsx("button", { className: "shape-context-menu__item shape-context-menu__item--back".concat(submenuIndex === activeSubItems.length
                                        ? ' shape-context-menu__item--focused'
                                        : ''), role: "menuitem", type: "button", tabIndex: submenuIndex === activeSubItems.length ? 0 : -1, "aria-setsize": activeSubItems.length + 1, "aria-posinset": activeSubItems.length + 1, onClick: function () {
                                        setSubmenuOpen(false);
                                        setSubmenuIndex(0);
                                        setTimeout(function () {
                                            var _a;
                                            (_a = activeMenuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                        }, 50);
                                    }, onFocus: function () {
                                        if (submenuIndex !== activeSubItems.length)
                                            setSubmenuIndex(activeSubItems.length);
                                    }, onMouseEnter: function () { return setSubmenuIndex(activeSubItems.length); }, children: _jsx("span", { className: "shape-context-menu__item-label", children: t('text.back') }) })] }))] })) : (
                /* ── Main menu view ── */
                _jsxs(_Fragment, { children: [_jsx("div", { className: "shape-context-menu__header", role: "presentation", tabIndex: -1, children: getContextMenuHeading() }), menuItems.map(function (item, index) { return (_jsxs("button", { className: "shape-context-menu__item".concat(index === activeIndex ? ' shape-context-menu__item--focused' : '').concat(item.isBack ? ' shape-context-menu__item--back' : ''), role: "menuitem", type: "button", tabIndex: index === activeIndex ? 0 : -1, "aria-setsize": menuItems.length, "aria-posinset": index + 1, "aria-disabled": item.disabled || undefined, "aria-haspopup": item.submenu || item.customContent ? 'menu' : undefined, onClick: function (e) {
                                if (item.disabled)
                                    return;
                                if (item.submenu || item.customContent) {
                                    setActiveIndex(index);
                                    activeMenuItemRef.current = e.currentTarget;
                                    setSubmenuOpen(true);
                                    setSubmenuIndex(0);
                                }
                                else {
                                    executeAction(item.id);
                                }
                            }, onFocus: function () {
                                if (activeIndex !== index) {
                                    setActiveIndex(index);
                                }
                            }, onMouseEnter: function () {
                                setActiveIndex(index);
                            }, children: [_jsx("span", { className: "shape-context-menu__item-label", children: item.label }), (item.submenu || item.customContent) && (_jsx("span", { className: "shape-context-menu__item-arrow", "aria-hidden": "true", children: "\u25B6" }))] }, item.id)); })] })), _jsx("div", { "aria-live": "assertive", "aria-atomic": "true", style: {
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                    }, children: amAnnouncement })] }) }), document.body);
};
export default ContextMenu;
